import axios from 'axios';
import { getCollectionsRegion } from "@/lib/regionFmt";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Create axios instance.
// NB: never set a default Content-Type here — axios ≥1.13 converts FormData
// bodies to JSON when application/json is forced, which broke every
// multipart endpoint (notes, screenshot uploads → 422). Axios picks the
// right header per request: JSON for plain objects, multipart for FormData.
const apiClient = axios.create({
  baseURL: API,
});

// Token getter function - will be set by AuthContext
let getAccessToken = null;

export const setTokenGetter = (getter) => {
  getAccessToken = getter;
};

// Fetch a fresh Clerk session token, with one short retry — `getToken()`
// hits the network every time its internal ~60s cache has expired, and a
// single dropped/slow request on a heavy, long-running page (contract
// wizard, pre-funding call) must not be fatal. Without this, one glitch
// sends the request out with NO Authorization header → backend 401 →
// the whole app force-logs-out, even though the Clerk session is fine.
// `forceFresh` asks Clerk to skip its cache (used after a 401 — retrying
// with the same cached token that just failed would just fail again).
const _getTokenWithRetry = async (forceFresh = false) => {
  if (!getAccessToken) return null;
  const opts = forceFresh ? { skipCache: true } : undefined;
  try {
    const token = await getAccessToken(opts);
    if (token) return token;
  } catch (error) {
    console.warn('Clerk token fetch failed, retrying once:', error?.message || error);
  }
  await new Promise((r) => setTimeout(r, 400));
  try {
    return await getAccessToken(opts);
  } catch (error) {
    console.error('Clerk token fetch failed on retry:', error?.message || error);
    return null;
  }
};

// Add auth token to requests
apiClient.interceptors.request.use(async (config) => {
  // Identify the acting user for the automatic audit trail (backend
  // middleware reads X-Portal-User on every write request).
  try {
    const u = JSON.parse(localStorage.getItem("sf_user") || "null");
    if (u?.name || u?.email) config.headers["X-Portal-User"] = u.name || u.email;
  } catch { /* header simply omitted */ }

  // Try to get a Clerk session token first
  const token = await _getTokenWithRetry();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
    return config;
  }

  // Fallback to localStorage token (for backwards compatibility during migration)
  const localToken = localStorage.getItem('sf_token');
  if (localToken) {
    config.headers.Authorization = `Bearer ${localToken}`;
  }
  return config;
});

// ── Applications Inbox region (UK | AUS) ────────────────────────────────────
// Every /applications/inbox/* call carries the active region — the backend
// routes AUS to the AU mailbox / Monday board / Airtable. Default UK keeps
// the original behaviour for all existing callers.
let _appInboxRegion = "UK";
export const setAppInboxRegion = (r) => { _appInboxRegion = r === "AUS" ? "AUS" : "UK"; };
apiClient.interceptors.request.use((config) => {
  const u = config.url || "";
  if (u.startsWith("/applications/inbox") || u.startsWith("/applications/mirror") || u.startsWith("/applications/cody")) {
    config.params = { region: _appInboxRegion, ...(config.params || {}) };
  }
  // "My mailbox" is bound to the signed-in Clerk identity server-side; dev-bypass
  // sessions (preview only) may act as a real mailbox via localStorage sf_dev_actor.
  if (u.startsWith("/inbox/mine")) {
    const actor = localStorage.getItem("sf_dev_actor");
    if (actor) config.headers = { ...(config.headers || {}), "X-Dev-Actor": actor };
  }
  // Collections desk (Loan Arrears Ageing / Customer File / payment plans / legal docs / Collection Report / Collections Analysis)
  if (u.startsWith("/collections/ageing") || u.startsWith("/legal/") || u.startsWith("/collections/report") || u.startsWith("/collections/analysis")) {
    // Do NOT override an explicit region already present in URL query or params
    const urlHasRegion = /[?&]region=/.test(u);
    const paramsHasRegion = config.params && Object.prototype.hasOwnProperty.call(config.params, "region");
    if (!urlHasRegion && !paramsHasRegion) {
      config.params = { region: getCollectionsRegion(), ...(config.params || {}) };
    }
  }
  return config;
});

// Response interceptor — auto-retry idempotent GETs on transient gateway
// failures (Cloudflare 502/520/522/524 during deploy rollouts, dropped
// connections) so one bad moment doesn't surface an error toast or blank
// a page. Writes (POST/PUT/PATCH/DELETE) are never retried.
const TRANSIENT_STATUSES = new Set([502, 503, 504, 520, 521, 522, 523, 524]);

apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const cfg = error.config || {};
    const status = error.response?.status;
    const isNetworkDrop = !error.response
      && error.code !== 'ECONNABORTED'   // timeouts — don't pile on load
      && error.code !== 'ERR_CANCELED';
    const isTransient = isNetworkDrop || TRANSIENT_STATUSES.has(status);
    // Idempotent writes (cfg._idempotent) also retry — protects against edge
    // drops where the server processed the request but the response was lost.
    const retryable = (cfg.method || '').toLowerCase() === 'get' || cfg._idempotent;
    if (retryable && isTransient && (cfg._retryCount || 0) < 2) {
      cfg._retryCount = (cfg._retryCount || 0) + 1;
      await new Promise((r) => setTimeout(r, 1500 * cfg._retryCount));
      return apiClient(cfg);
    }
    // 401 → only force re-login when it's genuinely OUR auth failing (token/
    // session), not an upstream service's 401 passed through — otherwise one
    // misconfigured integration bounces users to /login in an endless loop.
    const detail401 = status === 401 && typeof error.response?.data?.detail === 'string' ? error.response.data.detail : '';
    const isAuthFailure = status === 401 && (!detail401 || /token|authent|session|signing key|issuer|expired|not authenticated/i.test(detail401));
    if (isAuthFailure) {
      // A 401 here usually means THIS ONE request went out with no/stale
      // Authorization header (a transient Clerk getToken() hiccup), not
      // that the session actually ended — Clerk sessions refresh silently
      // for days. Fetch a FRESH (non-cached) token and retry the original
      // request once, silently, before concluding the session is really
      // dead — no error is logged unless that retry also fails.
      if (!cfg._authRetried && getAccessToken) {
        cfg._authRetried = true;
        try {
          const freshToken = await _getTokenWithRetry(true);
          if (freshToken) {
            cfg.headers = { ...(cfg.headers || {}), Authorization: `Bearer ${freshToken}` };
            return apiClient(cfg);
          }
        } catch (e) {
          console.warn('Token refresh-and-retry failed:', e?.message || e);
        }
      }
      console.error('API Error:', error.response?.data || error.message);
      localStorage.removeItem('sf_token');
      sessionStorage.setItem('sf_auth_failed', '1');
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
      return Promise.reject(error);
    }
    if (!(status === 409 && error.response?.data?.detail?.code === 'not_logged')) console.error('API Error:', error.response?.data || error.message);
    return Promise.reject(error);
  }
);

// ==================== AUTHENTICATION ====================
// All authentication is handled by Clerk (see contexts/AuthContext.jsx).
//   • Clerk <SignIn /> on /login
//   • Backend /api/me for the authoritative profile
//   • Backend /api/admin/users/* for invites / role management
// If you need the current user, use `useAuth()` from contexts/AuthContext.

// ==================== USER DIRECTORY ====================

// Lightweight list of active portal users (any authenticated user).
// Returns DirectoryEntry items: { user_id, email, name, role, team,
// department, is_active }. Used by AuditTrail and ApplicationSearch
// to populate "filter by team member" dropdowns. Adds a synthesised
// `id` field aliasing user_id so React key bindings stay simple.
export const getDirectory = async () => {
  const response = await apiClient.get('/admin/users/directory');
  return (response.data || []).map(u => ({ ...u, id: u.user_id }));
};

// ==================== APPLICATIONS ====================

export const getApplications = async () => {
  const response = await apiClient.get('/applications');
  return response.data;
};

export const getReadyToFundApplications = async () => {
  const response = await apiClient.get('/applications/ready-to-fund');
  return response.data;
};

export const getApplication = async (appId) => {
  const response = await apiClient.get(`/applications/${appId}`);
  return response.data;
};

export const createApplication = async (data) => {
  const response = await apiClient.post('/applications', data);
  return response.data;
};

export const updateApplication = async (appId, data) => {
  const response = await apiClient.put(`/applications/${appId}`, data);
  return response.data;
};

// Assignment
export const assignApplication = async (appId, assignedTo) => {
  const response = await apiClient.post(`/applications/${appId}/assign?assigned_to=${encodeURIComponent(assignedTo)}`);
  return response.data;
};

export const unassignApplication = async (appId, unassignedBy) => {
  const response = await apiClient.post(`/applications/${appId}/unassign?unassigned_by=${encodeURIComponent(unassignedBy)}`);
  return response.data;
};

// Monday.com Sync
export const syncMondayDeals = async () => {
  const response = await apiClient.post('/sync/monday');
  return response.data;
};

export const syncMondayFundedDeals = async (region = "UK", mode = "auto") => {
  const response = await apiClient.post(`/sync/monday/funded?region=${region}&mode=${mode}`);
  return response.data;
};

export const getFundedSyncState = async (region = "UK") =>
  (await apiClient.get(`/sync/monday/funded/state?region=${region}`)).data;

// Sales ask (Sep 2026): correct broker comms / cost of funding on a funded deal
export const patchSalesMetrics = async (appId, body) =>
  (await apiClient.patch(`/applications/${encodeURIComponent(appId)}/sales-metrics`, body)).data;

// Debenture (MR01) reconciliation — Emma, 30 Oct 2026
export const getDebentureReconciliation = async (months = 6, region = "UK") =>
  (await apiClient.get("/collections/ageing/legal-events/debenture-filings/reconcile", { params: { months, region }, timeout: 180000 })).data;
export const diariseMissingDebentures = async (months = 6, region = "UK", by) =>
  (await apiClient.post("/collections/ageing/legal-events/debenture-filings/reconcile/diarise", null, { params: { months, region, by }, timeout: 120000 })).data;


export const getFundedApplications = async (year, month, region = "UK") => {
  const params = new URLSearchParams();
  if (year) params.append('year', year);
  if (month) params.append('month', month);
  params.append('region', region);
  const response = await apiClient.get(`/applications/funded?${params}`);
  return response.data;
};

export const getMondayItemUpdates = async (itemId, limit = 50) => {
  const response = await apiClient.get(`/monday/item/${itemId}/updates?limit=${limit}`);
  return response.data;
};

export const getMondayItemFiles = async (itemId) => {
  const response = await apiClient.get(`/monday/item/${itemId}/files`);
  return response.data;
};

export const processMondayFile = async (itemId, fileUrl, filename) => {
  // Longer timeout for bank statement processing (OCR + AI analysis can take 60+ seconds)
  const response = await apiClient.post(`/monday/item/${itemId}/download-file`, {
    file_url: fileUrl,
    filename: filename
  }, {
    timeout: 180000 // 3 minutes timeout
  });
  return response.data;
};

// ============ Applications Workflow ============
export const getWorkflowSnapshot = async (itemsPerGroup = 5000, excludeGroups = null) => {
  const params = new URLSearchParams();
  params.append('items_per_group', itemsPerGroup);
  if (excludeGroups) params.append('exclude_groups', excludeGroups);
  const response = await apiClient.get(`/workflow/snapshot?${params}`);
  return response.data;
};

export const getWorkflowGroups = async () => {
  const response = await apiClient.get('/workflow/groups');
  return response.data;
};

export const getWorkflowItems = async (groupId = null, status = null, search = null, limit = 100, skip = 0, region = "UK") => {
  let params = `?limit=${limit}&skip=${skip}&region=${region}`;
  if (groupId) params += `&group_id=${groupId}`;
  if (status) params += `&status=${encodeURIComponent(status)}`;
  if (search) params += `&search=${encodeURIComponent(search)}`;
  const response = await apiClient.get(`/workflow/items${params}`);
  return response.data;
};

export const getWorkflowItem = async (itemId) => {
  const response = await apiClient.get(`/workflow/items/${itemId}`);
  return response.data;
};

export const syncWorkflowItems = async (fromDate = '2025-04-01', region = "UK") => {
  const response = await apiClient.post(`/workflow/sync?from_date=${fromDate}&region=${region}`);
  return response.data;
};

export const moveWorkflowItem = async (itemId, targetGroupId) => {
  const response = await apiClient.post(`/workflow/items/${itemId}/move?target_group_id=${targetGroupId}`);
  return response.data;
};

export const updateWorkflowItemStatus = async (itemId, newStatus) => {
  const response = await apiClient.post(`/workflow/items/${itemId}/status?new_status=${encodeURIComponent(newStatus)}`);
  return response.data;
};

export const assignFundingSpecialist = async (itemId, specialistName) => {
  // _idempotent: safe to auto-retry — re-setting the same name is harmless,
  // and prod edge drops were losing the (successful) response.
  const response = await apiClient.post(`/workflow/items/${itemId}/funding-specialist?specialist_name=${encodeURIComponent(specialistName)}`, null, { _idempotent: true });
  return response.data;
};

export const getFundingSpecialist = async (itemId) => {
  const response = await apiClient.get(`/workflow/items/${itemId}/funding-specialist`);
  return response.data;
};

// ==================== STANDING ORDER FILE → LOANDISK ====================

export const getAppMondayFiles = async (appId) => {
  const response = await apiClient.get(`/applications/${appId}/monday-files`);
  return response.data;
};

export const uploadStandingOrderFile = async (appId, file) => {
  const fd = new FormData();
  fd.append("file", file);
  const response = await apiClient.post(`/applications/${appId}/standing-order/upload`, fd, {
    headers: { "Content-Type": "multipart/form-data" },
    timeout: 120000,
  });
  return response.data;
};

export const uploadStandingOrderFromMonday = async (appId, asset) => {
  const response = await apiClient.post(`/applications/${appId}/standing-order/from-monday`, asset, { timeout: 120000 });
  return response.data;
};

// Open a Monday file inline in a new tab (backend streams with
// Content-Disposition: inline — bypasses Monday's forced download).
// Uses a blob URL so we can attach the auth header on the fetch.
export const previewMondayFile = async (appId, assetId, filename) => {
  const response = await apiClient.get(
    `/applications/${appId}/monday-files/${assetId}/preview`,
    { responseType: "blob", timeout: 60000 }
  );
  const blob = new Blob([response.data], { type: response.headers?.["content-type"] || "application/octet-stream" });
  const url = URL.createObjectURL(blob);
  const w = window.open(url, "_blank", "noopener");
  // Fallback if popup blocker kicks in — download via anchor click
  if (!w) {
    const a = document.createElement("a");
    a.href = url;
    a.target = "_blank";
    a.rel = "noopener";
    if (filename) a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
  }
  // Revoke after 60s to give the tab time to render
  setTimeout(() => URL.revokeObjectURL(url), 60000);
};

export const postWorkflowItemUpdate = async (itemId, body) => {
  const response = await apiClient.post(`/workflow/items/${itemId}/update?body=${encodeURIComponent(body)}`);
  return response.data;
};

export const updateWorkflowItemColumn = async (itemId, columnId, value) => {
  const response = await apiClient.put(`/workflow/items/${itemId}/column?column_id=${encodeURIComponent(columnId)}&value=${encodeURIComponent(value)}`);
  return response.data;
};

export const getWorkflowStatuses = async () => {
  const response = await apiClient.get('/workflow/statuses');
  return response.data;
};

export const getWorkflowStats = async () => {
  const response = await apiClient.get('/workflow/stats');
  return response.data;
};

// Bank Details
export const submitBankDetails = async (appId, data) => {
  const response = await apiClient.post(`/applications/${appId}/bank-details`, data);
  return response.data;
};

export const getApplicationBeneficiary = async (appId) => {
  const response = await apiClient.get(`/applications/${appId}/beneficiary`);
  return response.data;
};

export const getPendingBeneficiaries = async (region = null) => {
  const response = await apiClient.get(`/beneficiaries/pending-approval${region ? `?region=${region}` : ""}`);
  return response.data;
};

export const runPayeeNameCheck = async (benId) => {
  const response = await apiClient.post(`/beneficiaries/${benId}/payee-name-check`);
  return response.data;
};

export const approveBankDetails = async (benId, approvedBy) => {
  const response = await apiClient.post(`/beneficiaries/${benId}/approve`, { approved_by: approvedBy });
  return response.data;
};

export const rejectBankDetails = async (benId, rejectedBy, reason) => {
  const response = await apiClient.post(`/beneficiaries/${benId}/reject`, {
    rejected_by: rejectedBy,
    rejection_reason: reason,
  });
  return response.data;
};

export const getVerifiedBeneficiariesByCompany = async (companyName) => {
  const response = await apiClient.get(`/beneficiaries/by-company/${encodeURIComponent(companyName)}`);
  return response.data;
};

export const linkExistingBeneficiary = async (appId, benId, linkedBy = 'system') => {
  const response = await apiClient.post(`/applications/${appId}/link-beneficiary/${benId}?linked_by=${linkedBy}`);
  return response.data;
};

export const setTrustee = async (appId, trusteeName, trusteeAbn) => {
  const response = await apiClient.post(`/applications/${appId}/trustee`, {
    trustee_name: trusteeName,
    trustee_abn: trusteeAbn || null,
  });
  return response.data;
};

export const runCoPCheck = async (appId, verifiedBy = 'system', manual = false) => {
  const response = await apiClient.post(`/applications/${appId}/cop-check?verified_by=${encodeURIComponent(verifiedBy)}${manual ? '&manual=true' : ''}`);
  return response.data;
};

export const mockAcceptCoP = async (appId, verifiedBy = 'system') => {
  const response = await apiClient.post(`/applications/${appId}/cop-check?verified_by=${encodeURIComponent(verifiedBy)}&mock_accept=true`);
  return response.data;
};

export const getCapayMode = async () => {
  const response = await apiClient.get('/capay/mode');
  return response.data;
};

export const capayGeoCheck = async (flow = "disbursement") => {
  const response = await apiClient.get('/capay/geo-check', { params: { flow } });
  return response.data;
};

// Contract & DocuSign - New Flow
export const checkMondayStatus = async (appId) => {
  const response = await apiClient.get(`/applications/${appId}/check-monday-status`);
  return response.data;
};

export const searchDocuSignEnvelope = async (appId) => {
  const response = await apiClient.get(`/applications/${appId}/search-docusign`);
  return response.data;
};

export const updateDocuSignStatus = async (appId, newStatus) => {
  const response = await apiClient.post(`/applications/${appId}/update-docusign-status?new_status=${newStatus}`);
  return response.data;
};

export const refreshDocuSignStatus = async (appId) => {
  const response = await apiClient.post(`/applications/${appId}/refresh-docusign-status`);
  return response.data;
};

export const linkDocuSignEnvelope = async (appId, envelopeId) => {
  const response = await apiClient.post(`/applications/${appId}/link-docusign-envelope?envelope_id=${envelopeId}`);
  return response.data;
};

export const getDocuSignSigningDetails = async (appId) => {
  const response = await apiClient.get(`/applications/${appId}/docusign-signing-details`);
  return response.data;
};

// Signed agreement card (Credit Approved Deals): the completed envelope's documents + the combined signed PDF
export const getSignedAgreement = async (appId) => (await apiClient.get(`/applications/${appId}/signed-agreement`, { timeout: 60000 })).data;
export const fetchSignedAgreementPdf = async (appId) =>
  (await apiClient.get(`/applications/${appId}/signed-agreement.pdf`, { responseType: 'blob', timeout: 120000 })).data;

export const overrideDocuSignRisk = async (appId, reason) => {
  const response = await apiClient.post(`/applications/${appId}/override-docusign-risk?reason=${encodeURIComponent(reason)}`);
  return response.data;
};

// Legacy Contract Generation (kept for compatibility)
export const generateContract = async (appId, generatedBy = 'system') => {
  const response = await apiClient.post(`/applications/${appId}/generate-contract?generated_by=${generatedBy}`);
  return response.data;
};

// DocuSign (legacy mocked endpoints)
export const sendToDocuSign = async (appId, sentBy = 'system') => {
  const response = await apiClient.post(`/applications/${appId}/send-docusign?sent_by=${sentBy}`);
  return response.data;
};

export const markDocuSignSigned = async (appId) => {
  const response = await apiClient.post(`/applications/${appId}/docusign-signed`);
  return response.data;
};

// Standing Order
export const refreshMondayApplication = async (appId) => {
  const response = await apiClient.post(`/monday/refresh-application/${appId}`);
  return response.data;
};

export const getStandingOrderEmailPreview = async (appId, startDate = null) => {
  const response = await apiClient.get(`/applications/${appId}/standing-order-email-preview${startDate ? `?start_date=${startDate}` : ""}`);
  return response.data;
};

export const initiateStandingOrder = async (appId, initiatedBy = 'system', payload = {}) => {
  const response = await apiClient.post(
    `/applications/${appId}/initiate-standing-order?initiated_by=${encodeURIComponent(initiatedBy)}`,
    { to_email: payload.to_email || "", cc: payload.cc || [], bcc: payload.bcc || [],
      start_date: payload.start_date || "", skip_email: !!payload.skip_email });
  return response.data;
};

// Test Payment — fire-and-poll.
// The POST returns 202 {job_id} immediately (all safeguards pre-checked
// synchronously). The heavy GoCardless work runs server-side as a
// FastAPI BackgroundTask; we poll the status endpoint every 2 s for up
// to 120 s (bank-side calls occasionally take ~15-25s in LIVE mode).
// On completion the resolved object shape matches the pre-refactor
// synchronous response so callers don't need to change (payment_id,
// dm_sent, warning, gc_status, etc.), with an added `error` field on
// failure that the toast surfaces verbatim.
export const sendTestPayment = async (appId, sentBy = 'system', onProgress, resend = false) => {
  const start = await apiClient.post(`/applications/${appId}/send-test-payment?sent_by=${sentBy}&resend=${resend}`);
  const jobId = start.data?.job_id;
  if (!jobId) return start.data; // dev/preview fallback if endpoint returned inline
  const deadline = Date.now() + 120_000;
  let lastStatus = 'pending';
  while (Date.now() < deadline) {
    // eslint-disable-next-line no-await-in-loop
    await new Promise(r => setTimeout(r, 2000));
    // eslint-disable-next-line no-await-in-loop
    const poll = await apiClient.get(`/applications/${appId}/send-test-payment/status`, { params: { job_id: jobId } });
    const s = poll.data?.status;
    if (s && s !== lastStatus) { lastStatus = s; onProgress?.(s); }
    if (s === 'succeeded') return poll.data;
    if (s === 'failed') {
      const err = new Error(poll.data?.error || 'Nominal payment failed');
      err.response = { data: { detail: poll.data?.error } };
      throw err;
    }
  }
  const timeout = new Error('Nominal payment is taking longer than usual — check the GoCardless dashboard before retrying.');
  timeout.response = { data: { detail: timeout.message } };
  throw timeout;
};

export const confirmTestPayment = async (appId, confirmedAmount, confirmedBy) => {
  const response = await apiClient.post(`/applications/${appId}/confirm-test-payment`, {
    confirmed_amount: confirmedAmount,
    confirmed_by: confirmedBy,
  });
  return response.data;
};

// Selective workflow element reset (redo paperwork after e.g. re-issued DocuSign)
export const resetWorkflowElements = async (appId, elements, resetBy) => {
  const response = await apiClient.post(`/applications/${appId}/reset-elements`, {
    elements,
    reset_by: resetBy,
  });
  return response.data;
};

// Disbursement
export const confirmDisbursement = async (appId, data) => {
  const response = await apiClient.post(`/applications/${appId}/confirm-disbursement`, data);
  return response.data;
};

// Download disbursement PDF
export const downloadDisbursementPdf = async (appId) => {
  const response = await apiClient.get(`/applications/${appId}/disbursement-pdf`, {
    responseType: 'blob'
  });
  return response.data;
};

// Companies House Verification
export const verifyCompany = async (appId) => {
  const response = await apiClient.post(`/applications/${appId}/verify-company`);
  return response.data;
};

export const acceptCompanyRename = async (appId, acceptedBy, force = false) => {
  const response = await apiClient.post(
    `/applications/${appId}/accept-company-rename?accepted_by=${encodeURIComponent(acceptedBy)}${force ? "&force=true" : ""}`);
  return response.data;
};

export const requestNameChange = async (appId, requestedBy) => {
  const response = await apiClient.post(
    `/applications/${appId}/request-name-change?requested_by=${encodeURIComponent(requestedBy)}`);
  return response.data;
};

export const pullContractBankDetails = async (appId, requestedBy) => {
  const response = await apiClient.post(
    `/contracts/pull-bank-details/${appId}?requested_by=${encodeURIComponent(requestedBy)}`);
  return response.data;
};

export const searchCompany = async (appId, query = null) => {
  const params = query ? `?q=${encodeURIComponent(query)}` : '';
  const response = await apiClient.get(`/applications/${appId}/company-search${params}`);
  return response.data;
};

export const selectCompany = async (appId, companyNumber) => {
  const response = await apiClient.post(`/applications/${appId}/select-company?company_number=${companyNumber}`);
  return response.data;
};

export const getCompanyOfficers = async (appId) => {
  const response = await apiClient.get(`/applications/${appId}/company-officers`);
  return response.data;
};

// LoanDisk
export const searchLoanDiskLoan = async (appId, searchTerm = null) => {
  const params = searchTerm ? `?search_term=${encodeURIComponent(searchTerm)}` : '';
  const response = await apiClient.post(`/applications/${appId}/loandisk/search${params}`);
  return response.data;
};

export const linkLoanDiskLoan = async (appId, loanId, linkedBy = 'system') => {
  const response = await apiClient.post(`/applications/${appId}/loandisk/link-loan/${loanId}?linked_by=${linkedBy}`);
  return response.data;
};

export const markAsNewLoan = async (appId, markedBy = 'system') => {
  const response = await apiClient.post(`/applications/${appId}/loandisk/mark-new?marked_by=${markedBy}`);
  return response.data;
};

export const validateLoanDiskLoan = async (appId, loanData, validatedBy = 'system') => {
  const response = await apiClient.post(`/applications/${appId}/loandisk/validate`, {
    loan_data: loanData,
    validated_by: validatedBy,
  });
  return response.data;
};

export const confirmLoanDiskValidation = async (appId, loanData, validatedBy = 'system') => {
  const response = await apiClient.post(`/applications/${appId}/loandisk/confirm-validation`, {
    loan_data: loanData,
    validated_by: validatedBy,
  });
  return response.data;
};

export const uploadDocusignToLoanDisk = async (appId) => {
  const response = await apiClient.post(`/applications/${appId}/upload-docusign-to-loandisk`);
  return response.data;
};

// LoanDisk Requests
export const createLoanDiskRequest = async (data) => {
  const response = await apiClient.post('/loandisk-requests', data);
  return response.data;
};

export const getLoanDiskRequests = async (status = null, forRole = null) => {
  const params = new URLSearchParams();
  if (status) params.append('status', status);
  if (forRole) params.append('for_role', forRole);
  const response = await apiClient.get(`/loandisk-requests?${params.toString()}`);
  return response.data;
};

export const markLoanDiskAdded = async (requestId, addedBy) => {
  const response = await apiClient.post(`/loandisk-requests/${requestId}/mark-added?added_by=${addedBy}`);
  return response.data;
};

export const verifyLoanDiskRequest = async (requestId, verifiedBy) => {
  const response = await apiClient.post(`/loandisk-requests/${requestId}/verify?verified_by=${verifiedBy}`);
  return response.data;
};

// Approvals (combined)
export const getAllApprovals = async (userRole, region = null) => {
  const response = await apiClient.get(`/approvals?user_role=${userRole}${region ? `&region=${region}` : ""}`);
  return response.data;
};

// Disbursement
export const calculateDisbursement = async (appId) => {
  const response = await apiClient.get(`/applications/${appId}/calculate-disbursement`);
  return response.data;
};

// Old disbursement approval functions - keeping for backwards compatibility
export const getDisbursementApprovalsOld = async (status = 'pending') => {
  const response = await apiClient.get(`/disbursement-approvals?status=${status}`);
  return response.data;
};

export const approveDisbursementOld = async (approvalId, approvedBy, overrideAmount = null) => {
  let url = `/disbursement-approvals/${approvalId}/approve?approved_by=${approvedBy}`;
  if (overrideAmount !== null) {
    url += `&override_amount=${overrideAmount}`;
  }
  const response = await apiClient.post(url);
  return response.data;
};

export const rejectDisbursementApprovalOld = async (approvalId, rejectedBy, reason) => {
  const response = await apiClient.post(
    `/disbursement-approvals/${approvalId}/reject?rejected_by=${rejectedBy}&rejection_reason=${encodeURIComponent(reason)}`
  );
  return response.data;
};

// Net in Hand (Top Up)
export const searchLoanDiskByBorrower = async (borrowerName, region = "UK") => {
  const response = await apiClient.get(`/loandisk/search-by-borrower?borrower_name=${encodeURIComponent(borrowerName)}&region=${region}`);
  return response.data;
};

export const getLoanDiskLoanBySF = async (sfNumber, region = "UK") => {
  const response = await apiClient.get(`/loandisk/get-loan/${sfNumber}?region=${region}`);
  return response.data;
};

export const confirmNetInHand = async (appId, existingSF, existingBalance, existingBorrower, confirmedBy = 'system', includeAirtable = true, existingLoanId = null) => {
  const params = new URLSearchParams({
    existing_sf_number: existingSF,
    existing_outstanding_balance: existingBalance,
    existing_borrower_name: existingBorrower,
    confirmed_by: confirmedBy,
    include_airtable: includeAirtable
  });
  if (existingLoanId) params.append('existing_loan_id', existingLoanId);
  const response = await apiClient.post(`/applications/${appId}/confirm-net-in-hand?${params.toString()}`);
  return response.data;
};

// Today's payments not yet in LoanDisk — UK reads the Daily Collections feed, AUS reads Airtable (response.source says which)
export const getTodaysAirtablePayments = async (appId, existingSF = null, existingCompany = null, existingLoanId = null) => {
  const params = new URLSearchParams();
  if (existingSF) params.append('existing_sf', existingSF);
  if (existingCompany) params.append('existing_company', existingCompany);
  if (existingLoanId) params.append('existing_loan_id', existingLoanId);
  const queryString = params.toString() ? `?${params.toString()}` : '';
  const response = await apiClient.get(`/applications/${appId}/todays-airtable-payments${queryString}`);
  return response.data;
};

export const requestNetInHandApproval = async (appId, data) => {
  const params = new URLSearchParams();
  if (data.existingSF) params.append('existing_sf_number', data.existingSF);
  params.append('existing_loan_found', data.existingLoanFound || false);
  params.append('existing_outstanding_balance', data.existingBalance || 0);
  if (data.existingBorrower) params.append('existing_borrower_name', data.existingBorrower);
  params.append('name_mismatch', data.nameMismatch || false);
  params.append('calculated_net_in_hand', data.calculatedNetInHand || 0);
  params.append('approval_reason', data.approvalReason);
  params.append('requested_by', data.requestedBy || 'system');
  
  const response = await apiClient.post(`/applications/${appId}/request-net-in-hand-approval?${params.toString()}`);
  return response.data;
};

export const getNetInHandApprovals = async (status = 'pending') => {
  const response = await apiClient.get(`/net-in-hand-approvals?status=${status}`);
  return response.data;
};

export const approveNetInHand = async (approvalId, approvedBy, approvedNetInHand) => {
  const response = await apiClient.post(
    `/net-in-hand-approvals/${approvalId}/approve?approved_by=${approvedBy}&approved_net_in_hand=${approvedNetInHand}`
  );
  return response.data;
};

export const rejectNetInHandApproval = async (approvalId, rejectedBy, reason) => {
  const response = await apiClient.post(
    `/net-in-hand-approvals/${approvalId}/reject?rejected_by=${rejectedBy}&rejection_reason=${encodeURIComponent(reason)}`
  );
  return response.data;
};

// Audit Logs
export const getAuditLogs = async (filters = {}) => {
  const params = new URLSearchParams();
  if (filters.action) params.append('action', filters.action);
  if (filters.application_id) params.append('application_id', filters.application_id);
  if (filters.performed_by) params.append('performed_by', filters.performed_by);
  if (filters.module) params.append('module', filters.module);
  if (filters.q) params.append('q', filters.q);
  if (filters.limit) params.append('limit', filters.limit);
  
  const response = await apiClient.get(`/audit-logs?${params.toString()}`);
  return response.data;
};

// Stats
export const getSalesAgentStats = async () => {
  const response = await apiClient.get('/stats/sales-agents');
  return response.data;
};

export const getOverviewStats = async () => {
  const response = await apiClient.get('/stats/overview');
  return response.data;
};

export const getSalesAnalysis = async () => {
  const response = await apiClient.get('/sales/analysis');
  return response.data;
};

// GPT-5.5 takes longer than the proxy timeout, so generation runs server-side
// in the background and we poll until it completes (or fails).
export const getAISalesInsights = async ({ onProgress } = {}) => {
  await apiClient.post('/sales/ai-insights/generate');
  const started = Date.now();
  while (Date.now() - started < 300000) {
    await new Promise(r => setTimeout(r, 3000));
    const { data } = await apiClient.get('/sales/ai-insights');
    if (data.status === 'completed') return data;
    if (data.status === 'failed') throw new Error(data.error || 'AI insights generation failed');
    onProgress?.(Math.round((Date.now() - started) / 1000));
  }
  throw new Error('AI insights timed out — please try again');
};

export const getLatestAISalesInsights = async () => (await apiClient.get('/sales/ai-insights')).data;

export const analyzeSalesPlan = async (scenarioData) => {
  const { data: job } = await apiClient.post('/sales/plan-analysis', scenarioData);
  const started = Date.now();
  while (Date.now() - started < 300000) {
    await new Promise(r => setTimeout(r, 3000));
    const { data } = await apiClient.get(`/sales/plan-analysis/${job.job_id}`);
    if (data.status === 'completed') return data;
    if (data.status === 'failed') throw new Error(data.error || 'Plan analysis failed');
  }
  throw new Error('Plan analysis timed out — please try again');
};

export const getSalesTargets = async () => {
  const response = await apiClient.get('/sales/targets');
  return response.data;
};

export const updateSalesTargets = async (targets, topupPct = null) => {
  const payload = { targets };
  if (topupPct) {
    payload.topup_pct = topupPct;
  }
  const response = await apiClient.post('/sales/targets', payload);
  return response.data;
};

export const triggerSalesSync = async () => {
  const response = await apiClient.post('/sales/sync');
  return response.data;
};

export const getSalesSyncStatus = async () => {
  const response = await apiClient.get('/sales/sync/status');
  return response.data;
};

export const getCostOfSales = async (fyStartYear = null) => {
  const params = fyStartYear ? { fy_start_year: fyStartYear } : {};
  const response = await apiClient.get('/sales/cost-of-sales', { params });
  return response.data;
};

export const updateSyncSchedule = async (settings) => {
  const response = await apiClient.put('/sales/sync/schedule', settings);
  return response.data;
};

// Seed Data
export const seedDatabase = async () => {
  const response = await apiClient.post('/seed');
  return response.data;
};

// ==================== CONSOLIDATION / REFINANCE ====================

export const toggleConsolidation = async (appId, isConsolidation, toggledBy = 'user') => {
  const response = await apiClient.post(
    `/applications/${appId}/toggle-consolidation?is_consolidation=${isConsolidation}&toggled_by=${toggledBy}`
  );
  return response.data;
};

export const addConsolidationLender = async (appId, lenderData, addedBy = 'user') => {
  const response = await apiClient.post(
    `/applications/${appId}/consolidation-lenders?added_by=${addedBy}`,
    lenderData
  );
  return response.data;
};

export const updateConsolidationLender = async (appId, lenderId, lenderData, updatedBy = 'user') => {
  const response = await apiClient.put(
    `/applications/${appId}/consolidation-lenders/${lenderId}?updated_by=${updatedBy}`,
    lenderData
  );
  return response.data;
};

export const removeConsolidationLender = async (appId, lenderId, removedBy = 'user') => {
  const response = await apiClient.delete(
    `/applications/${appId}/consolidation-lenders/${lenderId}?removed_by=${removedBy}`
  );
  return response.data;
};

export const verifyLenderCoP = async (appId, lenderId, verifiedBy = 'user') => {
  const response = await apiClient.post(
    `/applications/${appId}/consolidation-lenders/${lenderId}/verify-cop?verified_by=${verifiedBy}`
  );
  return response.data;
};

export const requestConsolidationApproval = async (appId, requestedBy = 'user') => {
  const response = await apiClient.post(
    `/applications/${appId}/request-consolidation-approval?requested_by=${requestedBy}`
  );
  return response.data;
};

export const resetConsolidationForReentry = async (appId, resetBy = 'user') => {
  const response = await apiClient.post(
    `/applications/${appId}/reset-consolidation?reset_by=${resetBy}`
  );
  return response.data;
};

export const clearBankRejection = async (appId) => {
  const response = await apiClient.post(`/applications/${appId}/clear-bank-rejection`);
  return response.data;
};

export const resetBankDetails = async (appId) => {
  const response = await apiClient.post(`/applications/${appId}/reset-bank-details`);
  return response.data;
};

export const getConsolidationApprovals = async (status = 'pending') => {
  const response = await apiClient.get(`/consolidation-approvals?status=${status}`);
  return response.data;
};

export const approveConsolidation = async (approvalId, approvedBy = 'credit_manager') => {
  const response = await apiClient.post(
    `/consolidation-approvals/${approvalId}/approve?approved_by=${approvedBy}`
  );
  return response.data;
};

export const rejectConsolidation = async (approvalId, rejectedBy, reason) => {
  const response = await apiClient.post(
    `/consolidation-approvals/${approvalId}/reject?rejected_by=${rejectedBy}&rejection_reason=${encodeURIComponent(reason)}`
  );
  return response.data;
};

// ==================== PRE-FUNDING CALL ====================

export const getPrefundingQuestions = async (dealType = null, hasConsolidation = false, allQuestions = false, appId = null) => {
  let url = '/prefunding-questions';
  const params = [];
  if (dealType) params.push(`deal_type=${dealType}`);
  if (hasConsolidation) params.push(`has_consolidation=true`);
  if (allQuestions) params.push(`all_questions=true`);
  if (appId) params.push(`app_id=${encodeURIComponent(appId)}`);
  if (params.length > 0) url += '?' + params.join('&');
  
  const response = await apiClient.get(url);
  return response.data;
};

export const createPrefundingQuestion = async (question, createdBy = 'admin') => {
  const response = await apiClient.post(`/prefunding-questions?created_by=${encodeURIComponent(createdBy)}`, question);
  return response.data;
};

export const updatePrefundingQuestion = async (questionId, question, updatedBy = 'admin') => {
  const response = await apiClient.put(`/prefunding-questions/${questionId}?updated_by=${encodeURIComponent(updatedBy)}`, question);
  return response.data;
};

export const deletePrefundingQuestion = async (questionId, deletedBy = 'admin') => {
  const response = await apiClient.delete(`/prefunding-questions/${questionId}?deleted_by=${encodeURIComponent(deletedBy)}`);
  return response.data;
};

export const reorderPrefundingQuestions = async (questionOrders, updatedBy = 'admin') => {
  const response = await apiClient.put(`/prefunding-questions/reorder?updated_by=${encodeURIComponent(updatedBy)}`, questionOrders);
  return response.data;
};

export const completePrefundingCall = async (appId, callData, completedBy) => {
  const response = await apiClient.post(`/applications/${appId}/prefunding-call?completed_by=${encodeURIComponent(completedBy)}`, callData);
  return response.data;
};

export const getPrefundingCallStatus = async (appId) => {
  const response = await apiClient.get(`/applications/${appId}/prefunding-call`);
  return response.data;
};

export const resetPrefundingCall = async (appId, resetBy = 'admin') => {
  const response = await apiClient.post(`/applications/${appId}/prefunding-call/reset?reset_by=${encodeURIComponent(resetBy)}`);
  return response.data;
};

export const savePrefundingDraft = async (appId, draft, savedBy = 'user') => {
  const response = await apiClient.post(`/applications/${appId}/prefunding-call/draft?saved_by=${encodeURIComponent(savedBy)}`, draft);
  return response.data;
};

export const uploadPrefundingRecording = async (appId, file, uploadedBy = 'user') => {
  const formData = new FormData();
  formData.append('file', file);
  const response = await apiClient.post(
    `/applications/${appId}/prefunding-call/upload-recording?uploaded_by=${encodeURIComponent(uploadedBy)}`,
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } }
  );
  return response.data;
};

// ==================== FORMAL DISBURSEMENT PROCESS ====================

export const getDisbursementChecklist = async (appId) => {
  const response = await apiClient.get(`/applications/${appId}/disbursement-checklist`);
  return response.data;
};

export const overrideDirectorMatch = async (appId, reason) => {
  const response = await apiClient.post(`/applications/${appId}/override-director-match`, { reason });
  return response.data;
};

export const requestDisbursementApproval = async (appId, requestedBy, requestedByName) => {
  const response = await apiClient.post(
    `/applications/${appId}/request-disbursement-approval?requested_by=${encodeURIComponent(requestedBy)}&requested_by_name=${encodeURIComponent(requestedByName)}`
  );
  return response.data;
};

export const getDisbursementApprovals = async (status = 'pending') => {
  const response = await apiClient.get(`/disbursement-approvals?status=${status}`);
  return response.data;
};

export const approveDisbursement = async (approvalId, approvedBy, approvedByName) => {
  const response = await apiClient.post(
    `/disbursement-approvals/${approvalId}/approve?approved_by=${encodeURIComponent(approvedBy)}&approved_by_name=${encodeURIComponent(approvedByName)}`
  );
  return response.data;
};

export const rejectDisbursementApproval = async (approvalId, rejectedBy, reason) => {
  const response = await apiClient.post(
    `/disbursement-approvals/${approvalId}/reject?rejected_by=${encodeURIComponent(rejectedBy)}&reason=${encodeURIComponent(reason)}`
  );
  return response.data;
};

export const verifyPasswordForDisbursement = async (appId, email, password) => {
  const response = await apiClient.post(
    `/applications/${appId}/verify-password-for-disbursement?email=${encodeURIComponent(email)}&password=${encodeURIComponent(password)}`
  );
  return response.data;
};

export const executeDisbursement = async (appId, confirmedBy, confirmedAmount, payLenders = false) => {
  const response = await apiClient.post(
    `/applications/${appId}/execute-disbursement?confirmed_by=${encodeURIComponent(confirmedBy)}&confirmed_amount=${confirmedAmount}${payLenders ? "&pay_lenders=true" : ""}`
  );
  return response.data;
};

// Poll target after execute-disbursement returns `{status:"queued"}`. The
// backend hits GoCardless in a BackgroundTask so the HTTP response returns
// in <1s (well under Cloudflare's edge timeout). Frontend polls this every
// ~2s and shows the final result when disbursement_confirmed=true or
// gc_send_status='failed'.
export const getDisbursementStatus = async (appId) => {
  const response = await apiClient.get(`/applications/${appId}/disbursement-status`);
  return response.data;
};

// Stage 7 — post-funding payment tracking (live GC status for nominal + disbursement)
export const getPaymentTracking = async (appId) => {
  const response = await apiClient.get(`/applications/${appId}/payment-tracking`);
  return response.data;
};

// ─── Guarantor Link Tasks (Jul 24 2026, Paul's fail-safe) ─────────────
// LoanDisk's /loan API only attaches ONE guarantor; when a deal has multiple
// directors we still create every PG as a LoanDisk borrower and open a task
// here so ops (Juffrey / Collections) manually attach the extras in the
// LoanDisk UI and tick them off from Approvals.
export const listGuarantorLinkTasks = async (status = "pending") => {
  const response = await apiClient.get(`/loandisk/guarantor-link-tasks?status=${status}`);
  return response.data;
};

export const confirmGuarantorLinkTask = async (taskId, notes = "") => {
  const response = await apiClient.post(
    `/loandisk/guarantor-link-tasks/${taskId}/confirm`, { notes });
  return response.data;
};

export const dismissGuarantorLinkTask = async (taskId, notes = "") => {
  const response = await apiClient.post(
    `/loandisk/guarantor-link-tasks/${taskId}/dismiss`, { notes });
  return response.data;
};

// Manual recovery for a disbursement that got stuck on Cloudflare 520/524
// AFTER GC recorded the outbound. Operator supplies the OB… id from the GC
// dashboard and this endpoint marks the deal disbursed against it.
export const recoverDisbursement = async (appId, confirmedBy, gcPaymentId, gcStatus = "confirmed") => {
  const qs = new URLSearchParams({
    confirmed_by: confirmedBy,
    gc_payment_id: gcPaymentId,
    gc_status: gcStatus,
  }).toString();
  const response = await apiClient.post(`/applications/${appId}/recover-disbursement?${qs}`);
  return response.data;
};

export const updateUserDisbursementLimit = async (userId, limit) => {
  const response = await apiClient.put(`/users/${userId}/disbursement-limit?limit=${limit}`);
  return response.data;
};

// ==================== PORTAL SETTINGS ====================

export const getPortalSettings = async () => {
  const response = await apiClient.get('/settings');
  return response.data;
};

export const updateSetting = async (settingKey, value, updatedBy) => {
  const response = await apiClient.patch(
    `/settings/${settingKey}?value=${encodeURIComponent(value)}&updated_by=${encodeURIComponent(updatedBy)}`
  );
  return response.data;
};

// ==================== COLLECTIONS DATA ADMIN ====================

export const checkCollections2025Data = async () => {
  const response = await apiClient.get('/collections/admin/check-2025-data');
  return response.data;
};

export const exportCollections2025Data = async () => {
  const response = await apiClient.get('/collections/admin/export-2025-data');
  return response.data;
};

export const importCollections2025Data = async (data) => {
  const response = await apiClient.post('/collections/admin/import-2025-data', data);
  return response.data;
};

// ==================== API CREDENTIALS ====================

export const getApiCredentials = async () => {
  const response = await apiClient.get('/api-credentials');
  return response.data;
};

export const updateApiCredentials = async (service, credentials) => {
  const response = await apiClient.put(`/api-credentials/${service}`, credentials);
  return response.data;
};

export const testApiCredentials = async (service) => {
  const response = await apiClient.post(`/api-credentials/${service}/test`);
  return response.data;
};

// GoCardless Outbound Payments — one-off admin actions (ES512 signing setup)
export const gcOutboundGenerateKeypair = async () => {
  const response = await apiClient.post('/api-credentials/gocardless_outbound_payments/generate-keypair');
  return response.data;
};

export const gcOutboundShowPublicKey = async () => {
  const response = await apiClient.post('/api-credentials/gocardless_outbound_payments/show-public-key');
  return response.data;
};

export const gcOutboundVerifyLive = async () => {
  const response = await apiClient.post('/api-credentials/gocardless_outbound_payments/verify-live');
  return response.data;
};

// ==================== LEGAL TRACKER ====================

export const getLegalCases = async (status = null, search = null) => {
  const params = new URLSearchParams();
  if (status) params.append('status', status);
  if (search) params.append('search', search);
  const response = await apiClient.get(`/legal/cases?${params.toString()}`);
  return response.data;
};

// V2 Legal Case Management (MongoDB-backed with enhancements)
export const getLegalCasesV2 = async (filters = {}) => {
  const params = new URLSearchParams();
  if (filters.actionStatus) params.append('action_status', filters.actionStatus);
  if (filters.legalStage) params.append('legal_stage', filters.legalStage);
  if (filters.caseHandlerId) params.append('case_handler_id', filters.caseHandlerId);
  if (filters.jurisdiction) params.append('jurisdiction', filters.jurisdiction);
  if (filters.search) params.append('search', filters.search);
  if (filters.upcomingDays) params.append('upcoming_days', filters.upcomingDays);
  const response = await apiClient.get(`/legal/cases/v2?${params.toString()}`);
  return response.data;
};

export const getLegalCaseHandlers = async () => {
  const response = await apiClient.get('/legal/case-handlers');
  return response.data;
};

export const assignCaseHandler = async (caseId, handlerId, handlerName) => {
  const response = await apiClient.post(`/legal/cases/${caseId}/assign-handler`, {
    case_handler_id: handlerId,
    case_handler_name: handlerName
  });
  return response.data;
};

export const updateCaseStage = async (caseId, stage, notes = null, actionDate = null) => {
  const response = await apiClient.post(`/legal/cases/${caseId}/update-stage`, {
    legal_stage: stage,
    notes,
    action_date: actionDate
  });
  return response.data;
};

export const updateCaseActionStatus = async (caseId, actionStatus, notes = null) => {
  const response = await apiClient.post(`/legal/cases/${caseId}/update-action-status`, {
    action_status: actionStatus,
    notes
  });
  return response.data;
};

export const addCaseNote = async (caseId, notes) => {
  const response = await apiClient.post(`/legal/cases/${caseId}/add-note`, { notes });
  return response.data;
};

export const addCaseTimelineEntry = async (caseId, actionType, notes = null, date = null, metadata = null) => {
  const response = await apiClient.post(`/legal/cases/${caseId}/add-timeline-entry`, {
    action_type: actionType,
    notes,
    date,
    metadata
  });
  return response.data;
};

export const getCaseTimeline = async (caseId) => {
  const response = await apiClient.get(`/legal/cases/${caseId}/timeline`);
  return response.data;
};

export const getUpcomingActions = async (days = 7) => {
  const response = await apiClient.get(`/legal/upcoming-actions?days=${days}`);
  return response.data;
};

// Postworks Letter Sending
export const getPostworksStatus = async () => {
  const response = await apiClient.get('/legal/postworks/config');
  return response.data;
};

export const sendLetterViaPostworks = async (caseId, letterType, recipientName, recipientAddress, deliveryType = 'standard', notes = null) => {
  const response = await apiClient.post('/legal/postworks/send-letter', {
    case_id: caseId,
    letter_type: letterType,
    recipient_name: recipientName,
    recipient_address: recipientAddress,
    delivery_type: deliveryType,
    notes
  });
  return response.data;
};

// Email Preparation
export const prepareEmail = async (caseId, templateType, recipientEmail, body, recipientName = null, subject = null, cc = null) => {
  const response = await apiClient.post('/legal/email/prepare', {
    case_id: caseId,
    template_type: templateType,
    recipient_email: recipientEmail,
    recipient_name: recipientName,
    subject,
    body,
    cc
  });
  return response.data;
};

export const getEmailTemplates = async () => {
  const response = await apiClient.get('/legal/email/templates');
  return response.data;
};

export const getLegalCaseDetail = async (caseId) => {
  const response = await apiClient.get(`/legal/cases/${caseId}`);
  return response.data;
};

export const getLegalSummary = async () => {
  const response = await apiClient.get('/legal/summary');
  return response.data;
};

export const getLegalCaseLoanDiskData = async (caseId) => {
  const response = await apiClient.get(`/legal/cases/${caseId}/loandisk`);
  return response.data;
};

export const searchLoanDiskForLegal = async (searchTerm) => {
  const response = await apiClient.get(`/legal/loandisk-search?search_term=${encodeURIComponent(searchTerm)}`);
  return response.data;
};

// Xero API functions
export const getXeroStatus = async () => {
  const response = await apiClient.get('/xero/status');
  return response.data;
};

export const getXeroTruePrincipal = async (sfNumber) => {
  const response = await apiClient.get(`/xero/true-principal/${encodeURIComponent(sfNumber)}`);
  return response.data;
};

export const searchXeroInvoice = async (reference) => {
  const response = await apiClient.get(`/xero/invoices/by-reference/${encodeURIComponent(reference)}`);
  return response.data;
};

// Legal Case - Companies House
export const getLegalCaseCompanyInfo = async (caseId) => {
  const response = await apiClient.get(`/legal/cases/${caseId}/company-info`);
  return response.data;
};

// Legal Case - Creditsafe Credit Report
export const getLegalCaseCreditsafeReport = async (caseId) => {
  const response = await apiClient.get(`/legal/cases/${caseId}/creditsafe-report`);
  return response.data;
};

// Legal Case - Nearest Court (for serving documents)
export const getLegalCaseNearestCourt = async (caseId, addressType = 'guarantor') => {
  const response = await apiClient.get(`/legal/cases/${caseId}/nearest-court?address_type=${addressType}`);
  return response.data;
};

// Court Finder - Search by postcode
export const searchCourtsByPostcode = async (postcode, areaOfLaw = 'Money claims', limit = 5) => {
  const params = new URLSearchParams({ postcode, area_of_law: areaOfLaw, limit });
  const response = await apiClient.get(`/legal/court-finder?${params.toString()}`);
  return response.data;
};
// Emma #2 — free-text court name search (GOV.UK)
export const searchCourtsByName = async (q, limit = 10) =>
  (await apiClient.get('/legal/court-finder', { params: { q, limit } })).data;

// Emma #3 — tick & zip download of case files. Built in the background (Ed AU, 23 Oct 2026 — inline build hit the proxy timeout):
// start → poll → download. onProgress({done,total}) is optional.
export const startCaseFilesZip = async (caseId, keys) =>
  (await apiClient.post(`/legal/docs/${encodeURIComponent(caseId)}/files/zip`, { keys })).data;
export const getCaseFilesZipStatus = async (caseId, jobId) =>
  (await apiClient.get(`/legal/docs/${encodeURIComponent(caseId)}/files/zip/${encodeURIComponent(jobId)}`)).data;
export const fetchCaseFilesZipBlob = async (caseId, jobId) =>
  (await apiClient.get(`/legal/docs/${encodeURIComponent(caseId)}/files/zip/${encodeURIComponent(jobId)}/download`,
    { responseType: 'blob', timeout: 300000 })).data;
export const downloadCaseFilesZip = async (caseId, keys, onProgress) => {
  const job = await startCaseFilesZip(caseId, keys);
  const started = Date.now();
  let status = job;
  while (Date.now() - started < 15 * 60 * 1000) {
    await new Promise(r => setTimeout(r, 1500));
    status = await getCaseFilesZipStatus(caseId, job.job_id);
    if (onProgress) onProgress({ done: status.done || 0, total: status.total || keys.length });
    if (status.state === 'ready') break;
    if (status.state === 'failed') throw new Error(status.error || 'Zip build failed');
  }
  if (status.state !== 'ready') throw new Error('Zip build timed out — try fewer files');
  const blob = await fetchCaseFilesZipBlob(caseId, job.job_id);
  return { blob, fname: status.fname || job.fname, files: status.files || 0, errors: status.errors || [] };
};

// Legal Performance Analysis
export const getLegalPerformanceAnalysis = async (params = {}) => {
  const response = await apiClient.get('/legal/performance-analysis', { params });
  return response.data;
};

export const getLegalPerformanceAnalysisEnriched = async (includeLoandisk = true, includeCompaniesHouse = true, limit = 8) => {
  const params = new URLSearchParams();
  params.append('include_loandisk', includeLoandisk);
  params.append('include_companies_house', includeCompaniesHouse);
  params.append('limit', limit);
  // Longer timeout for enriched endpoint as it fetches from external APIs
  const response = await apiClient.get(`/legal/performance-analysis/enriched?${params.toString()}`, {
    timeout: 120000 // 2 minutes timeout
  });
  return response.data;
};

// ==================== COLLECTIONS ====================

// Get due loans for a specific date
export const getDueLoans = async (date = null, page = 0, count = 100) => {
  const params = new URLSearchParams({ page, count });
  if (date) params.append('date', date);
  const response = await apiClient.get(`/collections/due-loans?${params.toString()}`);
  return response.data;
};

// Get uncleared bank transactions
export const getUnclearedTransactions = async (date = null) => {
  const params = date ? new URLSearchParams({ date }) : new URLSearchParams();
  const response = await apiClient.get(`/collections/uncleared-transactions?${params.toString()}`);
  return response.data;
};

// Match payments to loans
export const matchPaymentsToLoans = async (date = null) => {
  const params = date ? new URLSearchParams({ date }) : new URLSearchParams();
  const response = await apiClient.get(`/collections/match-payments?${params.toString()}`);
  return response.data;
};

// Get collections overview metrics
export const getCollectionsOverview = async () => {
  const response = await apiClient.get('/collections/overview');
  return response.data;
};

// Mark transaction as irrelevant
export const markTransactionIrrelevant = async (transactionId, reason = "Internal transfer") => {
  const response = await apiClient.post('/collections/mark-irrelevant', {
    transaction_id: transactionId,
    reason
  });
  return response.data;
};

// Unmark transaction as irrelevant
export const unmarkTransactionIrrelevant = async (transactionId) => {
  const response = await apiClient.delete(`/collections/mark-irrelevant/${transactionId}`);
  return response.data;
};

// Manual match a loan with a transaction (extra fields teach the alias learner)
export const manualMatchPayment = async (loanId, transactionId, date, extras = {}) => {
  const response = await apiClient.post('/collections/manual-match', {
    loan_id: loanId,
    transaction_id: transactionId,
    date: date,
    loan_sf: extras.loanSf || null,
    borrower_name: extras.borrowerName || null,
    transaction_description: extras.transactionDescription || null
  });
  return response.data;
};

// Get collections report summary (Daily, WTD, MTD)
export const getCollectionsReportSummary = async (date) => {
  const params = date ? `?date=${date}` : '';
  const response = await apiClient.get(`/collections/report-summary${params}`);
  return response.data;
};

// Update collections report for a date
export const updateCollectionsReport = async (date, loanDue, cashCollected) => {
  const response = await apiClient.post('/collections/report-update', {
    date,
    loan_due: loanDue,
    cash_collected: cashCollected
  });
  return response.data;
};

// Get LoanDisk stats for daily report submission
export const getLoanDiskStats = async (date) => {
  const params = date ? `?date=${date}` : '';
  const response = await apiClient.get(`/collections/loandisk-stats${params}`);
  return response.data;
};

// Submit daily report to Airtable
export const submitDailyReport = async (data) => {
  const response = await apiClient.post('/collections/submit-daily-report', data);
  return response.data;
};

// Prefill data for the Post Collections Report dialog (arrears autofill)
export const getReportPrefill = async (date) => {
  const response = await apiClient.get(`/collections/report-prefill?date=${date}`);
  return response.data;
};

// Live arrears stats from today's arrears list ("Use today's arrears data")
export const getArrearsLiveStats = async () => {
  const response = await apiClient.get('/collections/arrears-live-stats');
  return response.data;
};

// ── Collection Report tab (Daily Collections → Collection Report) — Dean, 28 Oct 2026 ──
export const getCollectionReportMonth = async (year, month) =>
  (await apiClient.get(`/collections/report/month?year=${year}&month=${month}`)).data;
export const getCollectionReportSummary = async (year) =>
  (await apiClient.get(`/collections/report/summary?year=${year}`)).data;
export const getCollectionReportLive = async () => (await apiClient.get('/collections/report/live')).data;
export const lockCollectionReportDay = async (payload = {}) => (await apiClient.post('/collections/report/lock', payload)).data;
export const getCollectionReportLockStatus = async () => (await apiClient.get('/collections/report/lock-status')).data;
export const editCollectionReportRow = async (date, values, note, actor) =>
  (await apiClient.put(`/collections/report/row/${date}`, { values, note, actor })).data;
export const getCollectionReportCalendar = async () => (await apiClient.get('/collections/report/calendar')).data;
export const setCollectionReportClosures = async (dates) => (await apiClient.put('/collections/report/closures', { dates })).data;
export const importCollectionReportXlsx = async (file, { dryRun = false } = {}) => {
  const fd = new FormData();
  fd.append('file', file);
  return (await apiClient.post(`/collections/report/import/xlsx${dryRun ? '?dry_run=true' : ''}`, fd, { timeout: 120000 })).data;
};
export const downloadCollectionReportXlsx = async (year) =>
  (await apiClient.get(`/collections/report/export.xlsx?year=${year}`, { responseType: 'blob', timeout: 120000 })).data;
export const rebuildCollectionReportHistory = async (from, to) =>
  (await apiClient.post('/collections/report/rebuild-history', null, { params: { from, ...(to ? { to } : {}) }, timeout: 300000 })).data;
export const getCollectionReportDifferences = async (year) => (await apiClient.get(`/collections/report/differences?year=${year}`)).data;
export const downloadCollectionReportDifferences = async (year) =>
  (await apiClient.get(`/collections/report/differences.csv?year=${year}`, { responseType: 'blob' })).data;
export const getCollectionReportModel = async () => (await apiClient.get('/collections/report/model')).data;
export const setCollectionReportModel = async (rate, avgWorkingDays) =>
  (await apiClient.put('/collections/report/model', { rate, avg_working_days: avgWorkingDays })).data;

// ─── Payments Upload Wizard (Dean, Jul 24 2026) ────────────────────────
// Yesterday's cleared Airtable txns → auto-matched to LoanDisk due loans,
// with per-row Match / Unmatch / Dismiss and one-click Post All to LoanDisk.
export const loadPaymentsWizard = async (date = null) => {
  const params = date ? `?date=${date}` : '';
  const response = await apiClient.get(`/collections/payments-wizard${params}`);
  return response.data;
};

export const matchWizardTransaction = async (txnId, payload) => {
  const response = await apiClient.post(
    `/collections/payments-wizard/${txnId}/match`, payload);
  return response.data;
};

export const unmatchWizardTransaction = async (txnId) => {
  const response = await apiClient.post(
    `/collections/payments-wizard/${txnId}/unmatch`);
  return response.data;
};

export const dismissWizardTransaction = async (txnId, payload) => {
  const response = await apiClient.post(
    `/collections/payments-wizard/${txnId}/dismiss`, payload);
  return response.data;
};

export const bulkDismissWizardTransactions = async (txnIds, dismissedBy) => {
  const response = await apiClient.post(
    `/collections/payments-wizard/bulk-dismiss`,
    { txn_ids: txnIds, dismissed_by: dismissedBy });
  return response.data;
};

export const dedupeWizardDuplicates = async (date) => {
  const response = await apiClient.post(
    `/collections/payments-wizard/dedupe`, { date });
  return response.data;
};

export const wizardBorrowerSearch = async (q) => {
  const response = await apiClient.get(
    `/collections/payments-wizard/borrower-search`, { params: { q } });
  return response.data;
};

export const postAllPaymentsWizard = async (payload = {}) => {
  const response = await apiClient.post(
    `/collections/payments-wizard/post-all`, payload);
  return response.data;
};

// Poll target for Post All progress. Returns processed/total counts + arrays
// of posted/failed/skipped. Frontend calls every ~1.5s until status is
// `done` or `failed`.
export const getPostAllStatus = async (runId) => {
  const response = await apiClient.get(
    `/collections/payments-wizard/post-all-status/${runId}`);
  return response.data;
};

// Override for a duplicate-flagged row — posts even though LoanDisk
// already has a same-day/same-amount entry on the loan.
export const postAnywayWizardTransaction = async (txnId, payload = {}) => {
  const response = await apiClient.post(
    `/collections/payments-wizard/${txnId}/post-anyway`, payload);
  return response.data;
};

// Team utilisation report (admin only)
export const getTeamUtilisation = async (date) => {
  const params = date ? `?date=${date}` : '';
  const response = await apiClient.get(`/collections/team-utilisation${params}`);
  return response.data;
};

// Report a bug (multipart — supports screenshots / screen recordings)
export const reportBug = async (payload, files = []) => {
  const fd = new FormData();
  Object.entries(payload).forEach(([k, v]) => { if (v != null) fd.append(k, v); });
  files.forEach((f) => fd.append('files', f));
  const response = await apiClient.post('/bug-reports', fd, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 300000,
  });
  return response.data;
};

// Chunked upload for large bug attachments (proxy-safe)
export const uploadBugChunk = async (fd) => {
  const response = await apiClient.post('/bug-reports/upload-chunk', fd, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 120000,
  });
  return response.data;
};

// Match an Airtable transaction to a Promise to Pay
export const matchP2PPayment = async (payload) => {
  const response = await apiClient.post('/collections/p2p-match', payload);
  return response.data;
};

export const unmatchP2PPayment = async (transactionId, date) => {
  const response = await apiClient.delete(`/collections/p2p-match/${transactionId}?date=${date}`);
  return response.data;
};

// Analysis endpoints
export const getDailyAnalysis = async (year, month) => {
  const params = new URLSearchParams();
  if (year) params.append('year', year);
  if (month) params.append('month', month);
  const response = await apiClient.get(`/collections/analysis/daily?${params.toString()}`);
  return response.data;
};

export const getWeeklyAnalysis = async (year, month) => {
  const params = new URLSearchParams();
  if (year) params.append('year', year);
  if (month) params.append('month', month);
  const response = await apiClient.get(`/collections/analysis/weekly?${params.toString()}`);
  return response.data;
};

export const getMonthlyAnalysis = async (year) => {
  const params = year ? `?year=${year}` : '';
  const response = await apiClient.get(`/collections/analysis/monthly${params}`);
  return response.data;
};

export const getCollectionTrends = async (year) => {
  const params = year ? `?year=${year}` : '';
  const response = await apiClient.get(`/collections/analysis/trends${params}`);
  return response.data;
};

export const getFundingVsArrears = async (year) => {
  const response = await apiClient.get("/collections/analysis/funding-vs-arrears", { params: { year } });
  return response.data;
};

export const getAverageDailyCollections = async (year) => {
  const params = year ? `?year=${year}` : '';
  const response = await apiClient.get(`/collections/analysis/average-daily${params}`);
  return response.data;
};

export const comparePeriods = async (p1Start, p1End, p2Start, p2End) => {
  const params = new URLSearchParams({
    period1_start: p1Start,
    period1_end: p1End
  });
  if (p2Start) params.append('period2_start', p2Start);
  if (p2End) params.append('period2_end', p2End);
  const response = await apiClient.get(`/collections/analysis/compare?${params.toString()}`);
  return response.data;
};

// Detailed metrics (arrears, loan book)
export const getDetailedMetrics = async (year, month) => {
  const params = new URLSearchParams();
  if (year) params.append('year', year);
  if (month) params.append('month', month);
  const response = await apiClient.get(`/collections/analysis/detailed?${params.toString()}`);
  return response.data;
};

export const getArrearsTrends = async (year) => {
  const params = year ? `?year=${year}` : '';
  const response = await apiClient.get(`/collections/analysis/arrears-trends${params}`);
  return response.data;
};

export const getCollectionHeatmap = async (year) => {
  const params = year ? `?year=${year}` : '';
  const response = await apiClient.get(`/collections/analysis/heatmap${params}`);
  return response.data;
};

// ==================== SALES SCENARIOS ====================

export const getSavedScenarios = async () => {
  const response = await apiClient.get('/sales/scenarios');
  return response.data;
};

export const saveScenario = async (scenario) => {
  const response = await apiClient.post('/sales/scenarios', scenario);
  return response.data;
};

export const loadScenario = async (id) => {
  const response = await apiClient.get(`/sales/scenarios/${id}`);
  return response.data;
};

export const deleteScenario = async (id) => {
  const response = await apiClient.delete(`/sales/scenarios/${id}`);
  return response.data;
};

// ==================== UNDERWRITING ====================

export const runUnderwritingFiltering = async (data) => {
  const response = await apiClient.post('/underwriting/filtering', data);
  return response.data;
};

export const runFullUnderwritingAssessment = async (data) => {
  const response = await apiClient.post('/underwriting/full-assessment', data);
  return response.data;
};

export const checkDigitalFootprint = async (companyName) => {
  const response = await apiClient.post(`/underwriting/digital-footprint?company_name=${encodeURIComponent(companyName)}`);
  return response.data;
};

export const checkDirectorNetwork = async (directorName) => {
  const response = await apiClient.post(`/underwriting/director-network?director_name=${encodeURIComponent(directorName)}`);
  return response.data;
};

export const processBankStatementOCR = async (file, applicationId) => {
  const formData = new FormData();
  formData.append('file', file);
  const params = applicationId ? `?application_id=${applicationId}` : '';
  // Longer timeout for bank statement processing (OCR + AI analysis can take 60+ seconds)
  const response = await apiClient.post(`/underwriting/bank-statement-ocr${params}`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    timeout: 180000 // 3 minutes timeout
  });
  return response.data;
};

export const analyzeAffordability = async (loanAmount, monthlyPayment, applicationId) => {
  const response = await apiClient.post(
    `/underwriting/analyze-affordability?loan_amount=${loanAmount}&monthly_payment=${monthlyPayment}&application_id=${applicationId}`
  );
  return response.data;
};

export const getUnderwritingStatus = async (applicationId) => {
  const response = await apiClient.get(`/underwriting/${applicationId}`);
  return response.data;
};

// ==================== ELIGIBILITY & UNDERWRITING FUNNEL ====================

export const getEligibilityRules = async () => {
  const response = await apiClient.get('/eligibility-rules');
  return response.data;
};

export const updateEligibilityRules = async (rules) => {
  const response = await apiClient.put('/eligibility-rules', rules);
  return response.data;
};

export const runEligibilityCheck = async (data) => {
  const response = await apiClient.post('/underwriting/eligibility-check', data);
  return response.data;
};

export const getCreditsafeReport = async (companyName, companyNumber, applicationId) => {
  const response = await apiClient.post('/underwriting/creditsafe-report', {
    company_name: companyName,
    company_number: companyNumber,
    application_id: applicationId
  });
  return response.data;
};

export const analyzeBankStatementData = async (applicationId, bankData) => {
  const response = await apiClient.post('/underwriting/bank-statement-analysis', {
    application_id: applicationId,
    bank_data: bankData
  });
  return response.data;
};

export const runOnfidoCheck = async (applicantName, applicationId) => {
  const response = await apiClient.post('/underwriting/onfido-check', {
    applicant_name: applicantName,
    application_id: applicationId
  });
  return response.data;
};

export const runLandRegistrySearch = async (address, postcode, applicationId) => {
  const response = await apiClient.post('/underwriting/land-registry-search', {
    address,
    postcode,
    application_id: applicationId
  });
  return response.data;
};

export const searchAusBusinesses = async (query) =>
  (await apiClient.get("/aus/business/search", { params: { q: query } })).data;

export const searchCompaniesHouse = async (query, limit = 10) => {
  const response = await apiClient.get(`/companies-house/search?q=${encodeURIComponent(query)}&limit=${limit}`);
  return response.data;
};

export const getCompaniesHouseProfile = async (companyNumber) => {
  const response = await apiClient.get(`/companies-house/company/${companyNumber}`);
  return response.data;
};

export const getCompaniesHouseOfficers = async (companyNumber) => {
  const response = await apiClient.get(`/companies-house/company/${companyNumber}/officers`);
  return response.data;
};

// ==================== CREDIT POLICY INTELLIGENCE ====================

export const getCreditPolicyLegalCases = async () => {
  const response = await apiClient.get('/legal/credit-policy/legal-cases');
  return response.data;
};

export const getCreditPolicyLoanBook = async () => {
  const response = await apiClient.get('/legal/credit-policy/loan-book');
  return response.data;
};

export const getCreditPolicyIndustryComparison = async () => {
  const response = await apiClient.get('/legal/credit-policy/industry-comparison');
  return response.data;
};

export const getCreditPolicySummary = async () => {
  const response = await apiClient.get('/legal/credit-policy/summary');
  return response.data;
};

export const enrichSicCodes = async (target, updateAirtable = true, limit = 50) => {
  const response = await apiClient.post(
    `/legal/credit-policy/enrich-sic-codes?target=${target}&update_airtable=${updateAirtable}&limit=${limit}`
  );
  return response.data;
};

export const getCreditPolicyHomeownerAnalysis = async () => {
  const response = await apiClient.get('/legal/credit-policy/homeowner-analysis');
  return response.data;
};

export const fuzzySearchCompany = async (companyName, limit = 10) => {
  const response = await apiClient.get(`/legal/credit-policy/fuzzy-search?company_name=${encodeURIComponent(companyName)}&limit=${limit}`);
  return response.data;
};

export const matchCompanyToCase = async (caseId, companyNumber, table = 'legal') => {
  const response = await apiClient.post(`/legal/credit-policy/match-company?case_id=${caseId}&company_number=${companyNumber}&table=${table}`);
  return response.data;
};

export const autoMatchCompanies = async (target = 'legal', minScore = 0.95, maxCases = 50) => {
  const response = await apiClient.post(`/legal/credit-policy/auto-match?target=${target}&min_score=${minScore}&max_cases=${maxCases}`);
  return response.data;
};

// ============ Bad Debt Tracker ============
export const getBadDebtSummary = async () => {
  const response = await apiClient.get('/legal/bad-debt/summary');
  return response.data;
};

export const getBadDebtCases = async ({ category, status, limit = 500 } = {}) => {
  const params = new URLSearchParams();
  if (category) params.append('category', category);
  if (status) params.append('status', status);
  params.append('limit', limit);
  const response = await apiClient.get(`/legal/bad-debt/cases?${params}`);
  return response.data;
};

export const createBadDebtCase = async (payload) => {
  const response = await apiClient.post('/legal/bad-debt/cases', payload);
  return response.data;
};

export const updateBadDebtCase = async (caseId, payload) => {
  const response = await apiClient.patch(`/legal/bad-debt/cases/${caseId}`, payload);
  return response.data;
};

export const deleteBadDebtCase = async (caseId) => {
  const response = await apiClient.delete(`/legal/bad-debt/cases/${caseId}`);
  return response.data;
};

export const logBadDebtPayment = async (caseId, payment) => {
  const response = await apiClient.post(`/legal/bad-debt/cases/${caseId}/payment`, payment);
  return response.data;
};

export const upsertBadDebtProvision = async (payload) => {
  const response = await apiClient.put('/legal/bad-debt/provisions', payload);
  return response.data;
};

export const getBadDebtProvisions = async () => {
  const response = await apiClient.get('/legal/bad-debt/provisions');
  return response.data;
};

export const getBadDebtPrincipalPreview = async (sfaRef) => {
  const response = await apiClient.get(`/legal/bad-debt/principal-preview/${encodeURIComponent(sfaRef)}`);
  return response.data;
};

export const importBadDebtXlsx = async (reset = false) => {
  const response = await apiClient.post(`/legal/bad-debt/import-xlsx?reset=${reset}`);
  return response.data;
};

// ─── Loan Ageing (collections workflow) ───────────────────────────────────
export const getAgeingHandlers = async (group) =>
  (await apiClient.get('/collections/ageing/handlers', { params: group ? { group } : {} })).data;
// Dropdown helper (Dean, 8 Oct 2026): alphabetical, case-insensitive, and the current value always present
export const sortNames = (names, keep) => {
  const out = [...new Set([...(names || []), ...(keep ? [keep] : [])].filter(Boolean))];
  return out.sort((a, b) => a.localeCompare(b, "en", { sensitivity: "base" }));
};
export const getAgeingStages   = async () => (await apiClient.get('/collections/ageing/stages')).data;
export const getAgeingReconcile   = async () => (await apiClient.get('/collections/ageing/reconcile')).data;
export const applyAgeingReconcile = async (actor) => (await apiClient.post(`/collections/ageing/reconcile/apply?actor=${encodeURIComponent(actor || "portal")}`)).data;
export const listAgeingCases   = async (params = {}) => {
  const qs = new URLSearchParams(Object.entries(params).filter(([, v]) => v != null && v !== "")).toString();
  return (await apiClient.get(`/collections/ageing/cases${qs ? "?" + qs : ""}`)).data;
};
export const getAgeingCase     = async (caseId) => (await apiClient.get(`/collections/ageing/cases/${encodeURIComponent(caseId)}`)).data;
export const updateAgeingCase  = async (caseId, fields) => {
  const qs = new URLSearchParams(Object.entries(fields).filter(([, v]) => v != null)).toString();
  return (await apiClient.patch(`/collections/ageing/cases/${encodeURIComponent(caseId)}?${qs}`)).data;
};

// LoanDisk status sync — closed-loop ecosystem (portal ↔ LD)
export const previewLdStatusChange = async (caseId, stage) =>
  (await apiClient.get(`/collections/ageing/cases/${encodeURIComponent(caseId)}/ld-status-preview?stage=${encodeURIComponent(stage)}`)).data;

export const retryLdStatusPush = async (caseId, actor = "Ops") =>
  (await apiClient.post(`/collections/ageing/cases/${encodeURIComponent(caseId)}/ld-status/retry?actor=${encodeURIComponent(actor)}`)).data;

export const bulkSetStage = async (caseIds, stage, { actor = "Ops", pushToLoanDisk = true } = {}) =>
  (await apiClient.post(`/collections/ageing/cases/bulk-stage`, {
    case_ids: caseIds, stage, actor, push_to_loandisk: pushToLoanDisk,
  })).data;

export const refreshAirtableNotes = async (caseId, sfNumber = null) => {
  const qs = sfNumber ? `?sf_number=${encodeURIComponent(sfNumber)}` : "";
  return (await apiClient.post(`/legal/cases/${encodeURIComponent(caseId)}/refresh-airtable-notes${qs}`)).data;
};

export const getLegalNotesBundle = async (caseId, sfNumber = null) => {
  const qs = sfNumber ? `?sf_number=${encodeURIComponent(sfNumber)}` : "";
  return (await apiClient.get(`/legal/cases/${encodeURIComponent(caseId)}/notes-bundle${qs}`)).data;
};

export const createLegalNote = async (caseId, payload) =>
  (await apiClient.post(`/legal/cases/${encodeURIComponent(caseId)}/notes`, payload)).data;

export const updateLegalNote = async (caseId, noteId, payload) =>
  (await apiClient.patch(`/legal/cases/${encodeURIComponent(caseId)}/notes/${encodeURIComponent(noteId)}`, payload)).data;

export const deleteLegalNote = async (caseId, noteId) =>
  (await apiClient.delete(`/legal/cases/${encodeURIComponent(caseId)}/notes/${encodeURIComponent(noteId)}`)).data;

// Bulk Airtable sync — one-shot migration that pulls every record from
// the legal tracker and applies it via the shared per-record helper.
// POST kicks off a background task and returns a job_id; subsequent
// GETs poll progress every ~2s until status='complete' or 'failed'.
export const startBulkAirtableSync = async () =>
  (await apiClient.post(`/legal/cases/bulk-sync-airtable`)).data;

export const getBulkAirtableSyncStatus = async (jobId) =>
  (await apiClient.get(`/legal/cases/bulk-sync-airtable/${encodeURIComponent(jobId)}`)).data;

export const getLatestBulkAirtableSync = async () =>
  (await apiClient.get(`/legal/cases/bulk-sync-airtable/latest`)).data;
export const getLegalAutoRefreshLatest = async () =>
  (await apiClient.get(`/legal/cases/auto-refresh/latest`)).data;

// Capital at Risk — portal-only management flag (not pushed to LoanDisk)
export const setCapitalAtRisk = async (caseId, { enabled, reason = null, actor = "Ops" }) =>
  (await apiClient.post(
    `/collections/ageing/cases/${encodeURIComponent(caseId)}/capital-at-risk`,
    { enabled, reason, actor },
  )).data;
// Legal flags — legal hold (Restructured bridge), handling marker
// (out_of_jurisdiction / external) and manual jurisdiction override
export const setLegalCaseFlags = async (caseId, payload) =>
  (await apiClient.post(
    `/collections/ageing/cases/${encodeURIComponent(caseId)}/legal-flags`,
    payload,
  )).data;
// Close a legal case: write_off (final) | closed_review (+review_at) | fully_paid | resolved_collections (+sub_reason, check_in_at) | reopen
export const closeAgeingLegalCase = async (caseId, payload) =>
  (await apiClient.post(
    `/collections/ageing/cases/${encodeURIComponent(caseId)}/close`,
    payload,
  )).data;
// Pre-close check: open actions to cancel, court items that need withdrawing, route + hold state
export const getLegalClosePreview = async (caseId) =>
  (await apiClient.get(`/collections/ageing/cases/${encodeURIComponent(caseId)}/close-preview`)).data;
export const addAgeingNote     = async (caseId, { text, note_type = "note", author = "Ops", audience = null }) => {
  const formData = new FormData();
  formData.append("text", text);
  formData.append("note_type", note_type);
  formData.append("author", author);
  if (audience) formData.append("audience", audience);
  return (await apiClient.post(`/collections/ageing/cases/${encodeURIComponent(caseId)}/notes`, formData)).data;
};
export const deleteAgeingNote  = async (caseId, noteId) => (await apiClient.delete(`/collections/ageing/cases/${encodeURIComponent(caseId)}/notes/${noteId}`)).data;

// ── Timeline soft-hide ───────────────────────────────────────────────
// Used by the timeline delete button for every event that isn't a
// portal-owned Note or P2P (external Aircall SMS / calls, M365 emails,
// LoanDisk documents, CH filings, etc.). Backend soft-hides via a
// `hidden_event_ids` set on the case so nothing is permanently lost.
export const hideAgeingTimelineEvent = async (caseId, eventId, { reason = null, actor = "Ops" } = {}) =>
  (await apiClient.post(
    `/collections/ageing/cases/${encodeURIComponent(caseId)}/timeline/hide`,
    { event_id: eventId, reason, actor },
  )).data;
export const unhideAgeingTimelineEvent = async (caseId, eventId) =>
  (await apiClient.delete(
    `/collections/ageing/cases/${encodeURIComponent(caseId)}/timeline/hide/${encodeURIComponent(eventId)}`,
  )).data;

// ── Aircall AI: on-demand summary + transcript for a specific call ─
// Backfills historic calls that pre-dated the webhook subscription.
// Returns `{ok, call_id, summary, topics, action_items, sentiment,
// transcript, note_pushed}`. Requires the Aircall account to have AI
// Assist enabled.
export const fetchAircallCallAiSummary = async (caseId, callId, { includeTranscript = false } = {}) =>
  (await apiClient.post(
    `/collections/ageing/cases/${encodeURIComponent(caseId)}/aircall-calls/${encodeURIComponent(callId)}/fetch-summary`,
    null,
    { params: { include_transcript: includeTranscript } },
  )).data;


// ── Promise to Pay (P2P) ─────────────────────────────────────────────────
export const createAgeingP2P = async (caseId, { promised_at, promised_amount, method = "bank_transfer", notes = "", created_by = "Ops" }) =>
  (await apiClient.post(`/collections/ageing/cases/${encodeURIComponent(caseId)}/p2ps`,
    { promised_at, promised_amount, method, notes, created_by })).data;
export const updateAgeingP2P = async (caseId, p2pId, patch) =>
  (await apiClient.patch(`/collections/ageing/cases/${encodeURIComponent(caseId)}/p2ps/${p2pId}`, patch)).data;
export const deleteAgeingP2P = async (caseId, p2pId) =>
  (await apiClient.delete(`/collections/ageing/cases/${encodeURIComponent(caseId)}/p2ps/${p2pId}`)).data;
export const rescheduleAgeingP2P = async (caseId, p2pId, { promised_at, promised_amount, method, notes, actor = "Ops" }) =>
  (await apiClient.post(
    `/collections/ageing/cases/${encodeURIComponent(caseId)}/p2ps/${p2pId}/reschedule`,
    { promised_at, promised_amount, method, notes, actor },
  )).data;
export const getAgeingContactability = async (caseId) =>
  (await apiClient.get(`/collections/ageing/cases/${encodeURIComponent(caseId)}/contactability`)).data;
export const bulkAgeingContactability = async ({ limit = 200 } = {}) =>
  (await apiClient.get(`/collections/ageing/contactability/bulk`, { params: { limit } })).data;
export const listAgeingP2Ps = async ({ start, end, status, handler, search } = {}) =>
  (await apiClient.get('/collections/ageing/p2ps', { params: { start, end, status, handler, search } })).data;
export const uploadArrearsReport = async (file) => {
  const fd = new FormData();
  fd.append("file", file);
  return (await apiClient.post('/collections/ageing/upload-arrears', fd, { headers: { 'Content-Type': 'multipart/form-data' } })).data;
};
export const getArrearsSyncSettings = async () =>
  (await apiClient.get('/collections/ageing/arrears/sync-settings')).data;
export const saveArrearsSyncSettings = async (settings) =>
  (await apiClient.put('/collections/ageing/arrears/sync-settings', settings)).data;
export const syncArrearsFromLoanDisk = async ({ dry_run = false, min_days, max_days, use_override = false } = {}) =>
  (await apiClient.post('/collections/ageing/sync-arrears', { min_days, max_days, use_override }, { params: { dry_run } })).data;
export const getArrearsSyncJob = async (jobId) =>
  (await apiClient.get(`/collections/ageing/sync-arrears/jobs/${encodeURIComponent(jobId)}`)).data;
// Status pills / AI triage on the arrears list (Dean, 30 Oct 2026)
export const getTriageCatalog = async () => (await apiClient.get('/collections/ageing/triage/catalog')).data;
export const getTriageSettings = async () => (await apiClient.get('/collections/ageing/triage/settings')).data;
export const saveTriageSettings = async (body) => (await apiClient.put('/collections/ageing/triage/settings', body)).data;
export const runArrearsTriage = async ({ case_ids, force_ai = false } = {}) =>
  (await apiClient.post('/collections/ageing/triage/run', { case_ids, force_ai })).data;
export const getArrearsTriageJob = async (jobId) =>
  (await apiClient.get(`/collections/ageing/triage/jobs/${encodeURIComponent(jobId)}`)).data;
export const getAgeingLoanDiskTimeline = async (caseId) =>
  (await apiClient.get(`/collections/ageing/cases/${encodeURIComponent(caseId)}/loandisk-timeline`)).data;
export const getAgeingLoanParties = async (caseId) =>
  (await apiClient.get(`/collections/ageing/cases/${encodeURIComponent(caseId)}/loan-parties`)).data;
export const attachAgeingLoanParties = async (caseId, borrowerIds) =>
  (await apiClient.post(`/collections/ageing/cases/${encodeURIComponent(caseId)}/loan-parties/attach`, { borrower_ids: borrowerIds })).data;
export const searchAgeingLoanParties = async (caseId, q) =>
  (await apiClient.get(`/collections/ageing/cases/${encodeURIComponent(caseId)}/loan-parties/search`, { params: { q } })).data;
export const bulkAssignAgeingCases = async ({ case_ids, handler, actor = "Ops" }) =>
  (await apiClient.post('/collections/ageing/cases/bulk-assign', { case_ids, handler, actor })).data;
export const getAgeingLoanDiskSnapshot = async (caseId, { repayments_limit = 15 } = {}) =>
  (await apiClient.get(`/collections/ageing/cases/${encodeURIComponent(caseId)}/loandisk-snapshot`, { params: { repayments_limit } })).data;
export const getAgeingLoanSchedule = async (caseId, { kind = "adjusted", count = 500 } = {}) =>
  (await apiClient.get(`/collections/ageing/cases/${encodeURIComponent(caseId)}/loan-schedule`, { params: { kind, count } })).data;
export const startBreathingSpace = async (caseId, payload) =>
  (await apiClient.post(`/collections/ageing/cases/${encodeURIComponent(caseId)}/breathing-space`, payload)).data;

export const endBreathingSpace = async (caseId, payload) =>
  (await apiClient.post(`/collections/ageing/cases/${encodeURIComponent(caseId)}/breathing-space/end`, payload)).data;

// Emma #14 — manual account status tags
export const getAccountStatuses = async () =>
  (await apiClient.get("/collections/ageing/account-statuses")).data;
export const saveAccountStatuses = async (statuses, updatedBy = "Ops") =>
  (await apiClient.post("/collections/ageing/account-statuses", { statuses, updated_by: updatedBy })).data;
export const setCaseManualStatuses = async (caseId, statuses, actor = "Ops") =>
  (await apiClient.post(`/collections/ageing/cases/${encodeURIComponent(caseId)}/manual-statuses`, { statuses, actor })).data;

export const getAgeingTimeline = async (caseId, { types, limit = 500, include_repayments = true } = {}) =>
  (await apiClient.get(`/collections/ageing/cases/${encodeURIComponent(caseId)}/timeline`,
    { params: { types: Array.isArray(types) ? types.join(",") : types, limit, include_repayments } })).data;
export const aircallListUsers = async () =>
  (await apiClient.get(`/collections/ageing/aircall/users`)).data;
export const aircallClickToDial = async (caseId, { to_number, user_id, from_number_id, actor }) =>
  (await apiClient.post(`/collections/ageing/cases/${encodeURIComponent(caseId)}/aircall-calls/click-to-dial`,
    { to_number, user_id, from_number_id, actor })).data;
export const getAgeingAircallCalls = async (caseId, { limit = 25, phone_filter = "all", contact_id, mode = "merge" } = {}) =>
  (await apiClient.get(`/collections/ageing/cases/${encodeURIComponent(caseId)}/aircall-calls`,
    { params: { limit, phone_filter, contact_id, mode } })).data;
export const getAgeingMondayCard = async (caseId) =>
  (await apiClient.get(`/collections/ageing/cases/${encodeURIComponent(caseId)}/monday-card`)).data;
export const getAgeingCaseFiles = async (caseId) =>
  (await apiClient.get(`/collections/ageing/cases/${encodeURIComponent(caseId)}/files`)).data;
export const getAgeingUnderwritingNotes = async (caseId) =>
  (await apiClient.get(`/collections/ageing/cases/${encodeURIComponent(caseId)}/underwriting-notes`)).data;
export const syncAgeingAircallCalls = async (caseId, { limit = 100 } = {}) =>
  (await apiClient.post(`/collections/ageing/cases/${encodeURIComponent(caseId)}/aircall-calls/sync`, null, { params: { limit } })).data;
export const getAgeingAircallSms = async (caseId, { limit = 50 } = {}) =>
  (await apiClient.get(`/collections/ageing/cases/${encodeURIComponent(caseId)}/aircall-sms`, { params: { limit } })).data;
export const sendAgeingAircallSms = async (caseId, { body, phone_id, to, actor = "Ops" }) =>
  (await apiClient.post(`/collections/ageing/cases/${encodeURIComponent(caseId)}/aircall-sms/send`, { body, phone_id, to, actor })).data;
export const getAircallUKSmsLines = async () =>
  (await apiClient.get('/collections/ageing/aircall/sms-lines')).data;

// ── Daily Collections Report: intraday bank feed (native Make replacement) + pre-chase wizard (Oct 2026) ──
export const getIntradayFeedStatus = async () => (await apiClient.get('/collections/intraday-feed/status')).data;
export const refreshIntradayFeed = async () => (await apiClient.post('/collections/intraday-feed/refresh')).data;
export const updateIntradayFeedSettings = async (patch) => (await apiClient.put('/collections/intraday-feed/settings', patch)).data;
export const getPrechaseTemplates = async () => (await apiClient.get('/collections/prechase/templates')).data;
export const savePrechaseTemplates = async (patch) => (await apiClient.put('/collections/prechase/templates', patch)).data;
export const getPrechaseStatus = async (date) => (await apiClient.get(`/collections/prechase/status?date=${encodeURIComponent(date)}`)).data;
export const previewPrechase = async (payload) => (await apiClient.post('/collections/prechase/preview', payload)).data;
export const sendPrechase = async (payload) => (await apiClient.post('/collections/prechase/send', payload)).data;
export const getPrechaseFacts = async (payload) => (await apiClient.post('/collections/prechase/facts', payload)).data;
export const getAgeingM365Emails = async (caseId, { limit = 25, mailbox = "accounts" } = {}) =>
  (await apiClient.get(`/collections/ageing/cases/${encodeURIComponent(caseId)}/m365-emails`, { params: { limit, mailbox } })).data;
// Emma, 30 Oct 2026: sends with attachments can outlive the proxy's 60 s (Graph draft + attach + send, each able to
// queue behind Microsoft throttling) and surfaced as a bare Cloudflare error. The server now queues the send and we
// poll its status — callers still just await this and get {ok, attached, …} or a thrown error with response.data.detail.
export const sendAgeingM365Email = async (caseId, payload) => {
  const base = `/collections/ageing/cases/${encodeURIComponent(caseId)}/m365-emails`;
  const start = (await apiClient.post(`${base}/send`, { ...payload, background: true })).data;
  if (!start?.job_id) return start;
  const fail = (detail) => { const err = new Error(detail); err.response = { data: { detail } }; throw err; };
  const deadline = Date.now() + 5 * 60 * 1000;
  let misses = 0;
  while (Date.now() < deadline) {
    await new Promise((r) => setTimeout(r, 2000));
    let job;
    try {
      job = (await apiClient.get(`${base}/send-status/${start.job_id}`)).data;
      misses = 0;
    } catch (e) {
      if (++misses >= 5) fail("Lost contact while sending — check the case timeline / Sent Items before re-sending.");
      continue;
    }
    if (job.status === "sent") return { ok: true, ...(job.result || {}) };
    if (job.status === "failed") fail(job.error || "Send failed");
  }
  fail("Still sending after 5 minutes — check the case timeline / Sent Items before re-sending.");
};
export const syncAgeingM365History = async (caseId, { limit = 200, mailbox = "accounts" } = {}) =>
  (await apiClient.post(`/collections/ageing/cases/${encodeURIComponent(caseId)}/m365-emails/sync-history`, null, { params: { limit, mailbox } })).data;
export const refreshAgeingLoanHistory = async (caseId) =>
  (await apiClient.post(`/collections/ageing/cases/${encodeURIComponent(caseId)}/loan-history/refresh`)).data;

// ─── Chaser SMS templates — CRUD + per-case rendering ──────────────────────
export const listChaserSmsTemplates = async ({ onlyActive = false } = {}) =>
  (await apiClient.get("/collections/ageing/sms-templates", { params: { only_active: onlyActive } })).data;
export const createChaserSmsTemplate = async (payload) =>
  (await apiClient.post("/collections/ageing/sms-templates", payload)).data;
export const updateChaserSmsTemplate = async (templateId, payload) =>
  (await apiClient.put(`/collections/ageing/sms-templates/${encodeURIComponent(templateId)}`, payload)).data;
export const deleteChaserSmsTemplate = async (templateId) =>
  (await apiClient.delete(`/collections/ageing/sms-templates/${encodeURIComponent(templateId)}`)).data;
export const resetChaserSmsTemplates = async () =>
  (await apiClient.post("/collections/ageing/sms-templates/reset")).data;
export const updateChaserSmsGroupLabels = async (labels) =>
  (await apiClient.put("/collections/ageing/sms-templates/group-labels", { labels })).data;
export const renderChaserSmsForCase = async (caseId, templateId) =>
  (await apiClient.get(`/collections/ageing/cases/${encodeURIComponent(caseId)}/render-sms-template/${encodeURIComponent(templateId)}`)).data;
export const previewChaserSmsForCase = async (caseId, body) =>
  (await apiClient.post(`/collections/ageing/cases/${encodeURIComponent(caseId)}/preview-sms`, { body })).data;
export const logDockSmsIntent = async (caseId, payload) =>
  (await apiClient.post(`/collections/ageing/cases/${encodeURIComponent(caseId)}/aircall-dock-sms/note`, payload)).data;
export const pingCaseViewing = async (caseId, actor = "Ops") =>
  (await apiClient.post(`/collections/ageing/viewing/case/${encodeURIComponent(caseId)}`, null, { params: { actor } })).data;

// ─── Chaser EMAIL templates — same shape as SMS, adds subject line ─────
export const listChaserEmailTemplates = async ({ onlyActive = false } = {}) =>
  (await apiClient.get("/collections/ageing/email-templates", { params: { only_active: onlyActive } })).data;
export const createChaserEmailTemplate = async (payload) =>
  (await apiClient.post("/collections/ageing/email-templates", payload)).data;
export const updateChaserEmailTemplate = async (templateId, payload) =>
  (await apiClient.put(`/collections/ageing/email-templates/${encodeURIComponent(templateId)}`, payload)).data;
export const deleteChaserEmailTemplate = async (templateId) =>
  (await apiClient.delete(`/collections/ageing/email-templates/${encodeURIComponent(templateId)}`)).data;
export const resetChaserEmailTemplates = async () =>
  (await apiClient.post("/collections/ageing/email-templates/reset")).data;
export const updateChaserEmailGroupLabels = async (labels) =>
  (await apiClient.put("/collections/ageing/email-templates/group-labels", { labels })).data;
export const renderChaserEmailForCase = async (caseId, templateId) =>
  (await apiClient.get(`/collections/ageing/cases/${encodeURIComponent(caseId)}/render-email-template/${encodeURIComponent(templateId)}`)).data;
export const previewChaserEmailForCase = async (caseId, { subject = "", body = "", extraTokens = null }) =>
  (await apiClient.post(`/collections/ageing/cases/${encodeURIComponent(caseId)}/preview-email`, { subject, body, extra_tokens: extraTokens })).data;
export const getCaseContactEmails = async (caseId) =>
  (await apiClient.get(`/collections/ageing/cases/${encodeURIComponent(caseId)}/contact-emails`)).data;
// ── Notice / document-issue templates (PDN / DN / Redemption / Settlement) ──
export const listNoticeTemplates = async ({ onlyActive = false } = {}) =>
  (await apiClient.get("/collections/ageing/notice-templates", { params: { only_active: onlyActive } })).data;
export const createNoticeTemplate = async (payload) =>
  (await apiClient.post("/collections/ageing/notice-templates", payload)).data;
export const updateNoticeTemplate = async (templateId, payload) =>
  (await apiClient.put(`/collections/ageing/notice-templates/${encodeURIComponent(templateId)}`, payload)).data;
export const deleteNoticeTemplate = async (templateId) =>
  (await apiClient.delete(`/collections/ageing/notice-templates/${encodeURIComponent(templateId)}`)).data;
export const getCaseNoticeContext = async (caseId) =>
  (await apiClient.get(`/collections/ageing/cases/${encodeURIComponent(caseId)}/notice-context`)).data;
// ── Payment plans (Customer File quick action) ──────────────────────────
export const getPaymentPlanMeta = async (caseId) =>
  (await apiClient.get(`/collections/ageing/cases/${encodeURIComponent(caseId)}/payment-plans/meta`)).data;
export const previewPaymentPlan = async (caseId, payload) =>
  (await apiClient.post(`/collections/ageing/cases/${encodeURIComponent(caseId)}/payment-plans/preview`, payload)).data;
export const createPaymentPlan = async (caseId, payload) =>
  (await apiClient.post(`/collections/ageing/cases/${encodeURIComponent(caseId)}/payment-plans`, payload)).data;
export const listPaymentPlans = async (caseId) =>
  (await apiClient.get(`/collections/ageing/cases/${encodeURIComponent(caseId)}/payment-plans`)).data;
export const sendPaymentPlanEmail = async (caseId, planId, payload) =>
  (await apiClient.post(`/collections/ageing/cases/${encodeURIComponent(caseId)}/payment-plans/${encodeURIComponent(planId)}/send-email`, payload)).data;
export const approvePaymentPlan = async (caseId, planId, reason) =>
  (await apiClient.post(`/collections/ageing/cases/${encodeURIComponent(caseId)}/payment-plans/${encodeURIComponent(planId)}/approve`, { reason })).data;
export const rejectPaymentPlan = async (caseId, planId, reason) =>
  (await apiClient.post(`/collections/ageing/cases/${encodeURIComponent(caseId)}/payment-plans/${encodeURIComponent(planId)}/reject`, { reason })).data;
export const exportPaymentPlanPdf = async (caseId, payload) =>
  (await apiClient.post(`/collections/ageing/cases/${encodeURIComponent(caseId)}/payment-plans/pdf`, payload, { responseType: "blob" })).data;
export const getAgeingM365EmailFull = async (caseId, messageId, mailbox = "accounts") =>
  (await apiClient.get(`/collections/ageing/cases/${encodeURIComponent(caseId)}/m365-emails/${encodeURIComponent(messageId)}`, { params: { mailbox } })).data;
export const replyAgeingM365Email = async (caseId, messageId, { body, reply_all = false, actor = "Ops", mailbox = "accounts" }) =>
  (await apiClient.post(`/collections/ageing/cases/${encodeURIComponent(caseId)}/m365-emails/${encodeURIComponent(messageId)}/reply`, { body, reply_all, actor, mailbox })).data;
export const m365SearchMailbox = async ({ q, folder_id, limit = 25 }) =>
  (await apiClient.get('/collections/ageing/m365/search', { params: { q, folder_id, limit } })).data;
export const m365GetFolders = async () =>
  (await apiClient.get('/collections/ageing/m365/folders')).data;
export const m365GetSignature = async () =>
  (await apiClient.get('/collections/ageing/m365/signature')).data;
export const m365UpdateSignature = async (signature_html) =>
  (await apiClient.put('/collections/ageing/m365/signature', { signature_html })).data;
export const m365Diagnose = async () =>
  (await apiClient.get('/collections/ageing/m365/diagnose')).data;

// --- Additional contacts (guarantor / accounts team / switchboard) ----------
export const getAgeingContacts = async (caseId) =>
  (await apiClient.get(`/collections/ageing/cases/${encodeURIComponent(caseId)}/contacts`)).data;
export const addAgeingAdditionalContact = async (caseId, payload) =>
  (await apiClient.post(`/collections/ageing/cases/${encodeURIComponent(caseId)}/additional-contacts`, payload)).data;
export const updateAgeingAdditionalContact = async (caseId, contactId, payload) =>
  (await apiClient.patch(`/collections/ageing/cases/${encodeURIComponent(caseId)}/additional-contacts/${encodeURIComponent(contactId)}`, payload)).data;
export const deleteAgeingAdditionalContact = async (caseId, contactId) =>
  (await apiClient.delete(`/collections/ageing/cases/${encodeURIComponent(caseId)}/additional-contacts/${encodeURIComponent(contactId)}`)).data;
export const getAgeingRoleOptions = async () =>
  (await apiClient.get('/collections/ageing/contacts/role-options')).data;
export const syncAgeingGuarantors = async (caseId) =>
  (await apiClient.post(`/collections/ageing/cases/${encodeURIComponent(caseId)}/contacts/sync-guarantors`)).data;
export const discoverAgeingEmailContacts = async (caseId, { limit = 200 } = {}) =>
  (await apiClient.get(`/collections/ageing/cases/${encodeURIComponent(caseId)}/discover-contacts`, { params: { limit } })).data;
export const getAgeingEngagement = async (caseId) =>
  (await apiClient.get(`/collections/ageing/cases/${encodeURIComponent(caseId)}/engagement`)).data;
export const discoverAgeingGuarantors = async (caseId) =>
  (await apiClient.get(`/collections/ageing/cases/${encodeURIComponent(caseId)}/discover-guarantors`)).data;
export const importAgeingGuarantor = async (caseId, loandiskBorrowerId) =>
  (await apiClient.post(`/collections/ageing/cases/${encodeURIComponent(caseId)}/import-guarantor/${encodeURIComponent(loandiskBorrowerId)}`)).data;

// --- Aircall company-wide call log mirror ----------------------------------
export const syncAircallCallsLog = async ({ days_back = 90, max_pages = 50, actor = "Ops" } = {}) =>
  (await apiClient.post('/collections/ageing/aircall/sync-calls-log', null, { params: { days_back, max_pages, actor } })).data;
export const getAircallCallsLogStatus = async () =>
  (await apiClient.get('/collections/ageing/aircall/calls-log-status')).data;
export const getAgeingRepaymentHistory = async (caseId, { limit = 200 } = {}) =>
  (await apiClient.get(`/collections/ageing/cases/${encodeURIComponent(caseId)}/repayment-history`, { params: { limit } })).data;

// --- Borrower's other loans ("Previous loans" tab on Customer File) -------
export const getAgeingBorrowerLoans = async (caseId, { count = 50 } = {}) =>
  (await apiClient.get(`/collections/ageing/cases/${encodeURIComponent(caseId)}/borrower-loans`, { params: { count } })).data;
export const followLoanChain = async (caseId) =>
  (await apiClient.post(`/collections/ageing/cases/${encodeURIComponent(caseId)}/follow-chain`)).data;
export const scanLoanFamilies   = async () => (await apiClient.post("/legal-process/families/scan")).data;
export const getLoanFamilyJob   = async (jobId) => (await apiClient.get(`/legal-process/families/job/${jobId}`)).data;
export const getLoanFamilyLatest = async () => (await apiClient.get("/legal-process/families/latest")).data;
export const applyLoanFamilies  = async (jobId) => (await apiClient.post(`/legal-process/families/apply/${jobId}`)).data;
export const getAgeingBorrowerLoanRepayments = async (caseId, loanAppId, { limit = 200 } = {}) =>
  (await apiClient.get(`/collections/ageing/cases/${encodeURIComponent(caseId)}/borrower-loans/${encodeURIComponent(loanAppId)}/repayments`, { params: { limit } })).data;
export const getAgeingBorrowerLoanSchedule = async (caseId, loanAppId, { kind = "adjusted", count = 500 } = {}) =>
  (await apiClient.get(`/collections/ageing/cases/${encodeURIComponent(caseId)}/borrower-loans/${encodeURIComponent(loanAppId)}/schedule`, { params: { kind, count } })).data;

// Aircall screen-pop: resolve which Collections case (if any) a phone number belongs to.
export const findAgeingCaseByPhone = async (phone) =>
  (await apiClient.get(`/collections/ageing/cases/by-phone/${encodeURIComponent(phone)}`)).data;

// Quick targeted sync of the last few minutes of Aircall calls — used to
// refresh the timeline immediately after the SDK fires call_ended.
export const aircallSyncRecentCalls = async (minutes = 10) =>
  (await apiClient.post(`/collections/ageing/aircall/sync-recent-calls`, null, { params: { minutes } })).data;

// Companies House per-case integration. The overview endpoint auto-
// resolves the CRN on first call by searching the customer name, then
// caches it on the case for instant subsequent loads.
export const getCaseCompaniesHouse = async (caseId) =>
  (await apiClient.get(`/collections/ageing/cases/${encodeURIComponent(caseId)}/companies-house`)).data;
export const searchCompaniesHouseForCase = async (caseId, q) =>
  (await apiClient.get(`/collections/ageing/cases/${encodeURIComponent(caseId)}/companies-house/search`, { params: { q } })).data;
export const linkCompaniesHouse = async (caseId, payload) =>
  (await apiClient.post(`/collections/ageing/cases/${encodeURIComponent(caseId)}/companies-house/link`, payload)).data;
export const unlinkCompaniesHouse = async (caseId) =>
  (await apiClient.delete(`/collections/ageing/cases/${encodeURIComponent(caseId)}/companies-house/link`)).data;

// ==================== SLACK ====================
// Quick-post bubble + "Share to Slack" buttons sprinkled across the portal.
// All endpoints are admin-gated on the backend (auth middleware) and use
// the bot token stored in api_credentials.
export const getSlackStatus     = async () => (await apiClient.get("/slack/status")).data;
export const listSlackChannels  = async (includePrivate = true) =>
  (await apiClient.get("/slack/channels", { params: { include_private: includePrivate } })).data;
export const listSlackUsers     = async () => (await apiClient.get("/slack/users")).data;

// ==================== SLACK COMMS FLOW (Portal Settings) ====================
export const getSlackCommsSettings   = async () => (await apiClient.get("/slack-comms/settings")).data;
export const saveSlackCommsSettings  = async (payload) => (await apiClient.put("/slack-comms/settings", payload)).data;
export const getPayoutBalances = async (region) => (await apiClient.get("/payout-balances", { params: region ? { region } : {} })).data;
export const getSlackWorkspaceUsers  = async () => (await apiClient.get("/slack/users")).data;
export const getPgContacts           = async (appId) => (await apiClient.get(`/applications/${appId}/pg-contacts`)).data;
export const prefundingLoanSummary   = async (appId) => (await apiClient.get(`/applications/${appId}/prefunding-loan-summary`)).data;
export const checkPrefundingIdentity = async (appId, answers) => (await apiClient.post(`/applications/${appId}/prefunding-identity-check`, { answers })).data;
export const submitAddressCorrection = async (appId, newFirstLine) => (await apiClient.post(`/applications/${appId}/prefunding-address-correction`, { new_first_line: newFirstLine })).data;
export const getPendingAddressCorrection = async (appId) => (await apiClient.get(`/applications/${appId}/prefunding-address-correction/pending`)).data;
export const approveAddressCorrection = async (correctionId) => (await apiClient.post(`/address-corrections/${correctionId}/approve`)).data;
export const rejectAddressCorrection = async (correctionId, reason) => (await apiClient.post(`/address-corrections/${correctionId}/reject`, { reason })).data;
export const previewSlackCommsEvent  = async (eventKey, region = "UK") => (await apiClient.post(`/slack-comms/preview/${eventKey}?region=${region}`)).data;
export const slackPost          = async (payload) => (await apiClient.post("/slack/post", payload)).data;
export const slackShareEvent    = async (payload) => (await apiClient.post("/slack/share-event", payload)).data;

// ==================== LEGAL CASE TRACKER V2 ====================
// Mirrors the collections case file but scopes to legal cases (`stage IN
// {bad_debt, written_off}` OR has a `legal_stage` set). Reuses the SAME
// /api/collections/ageing/* endpoints for centre-tab data — only the list
// + sync endpoints below are legal-specific.
export const listLegalStages    = async () => (await apiClient.get("/collections/ageing/legal-stages")).data;
export const listLegalCases     = async (params = {}) =>
  (await apiClient.get("/collections/ageing/legal-cases", { params })).data;
export const syncLegalCasesFromAirtable = async () =>
  (await apiClient.post("/collections/ageing/legal-cases/sync")).data;
export const syncLegalCasesFromLoanDisk = async () =>
  (await apiClient.post("/collections/ageing/legal-cases/sync-loandisk", {}, { timeout: 240000 })).data;

export const getLegalLoanDiskSyncStatus = async () =>
  (await apiClient.get("/collections/ageing/legal-cases/sync-loandisk/status")).data;

// ─── Find Legal Case (manual escalation from LoanDisk search) ─────────────
// Same LoanDisk search endpoint the Bad Debt tracker uses. Returns
// { results: [{loan_id, loan_application_id, borrower_name, loan_status_label,
//   principal_amount, balance_amount, days_past_due, released_date}, ...] }
export const searchLoanDiskForManualLegal = async (q) =>
  (await apiClient.get(`/legal/bad-debt/loandisk/search`, { params: { q } })).data;

// POST the picked row. On the first call `force=false`; if the response
// carries `requires_confirm:true`, show the warning to the user and call
// again with `force=true` to actually escalate.
export const manualAddLegalCase = async (payload) =>
  (await apiClient.post("/collections/ageing/legal-cases/manual-add", payload)).data;

// ==================== LEGAL MAIL INBOX (scanned post) ====================
export const legalMailList = async ({ limit = 50, folder = "inbox" } = {}) =>
  (await apiClient.get("/legal/mail-inbox/emails", { params: { limit, folder } })).data;
export const legalMailGetEmail = async (messageId) =>
  (await apiClient.get(`/legal/mail-inbox/emails/${messageId}`)).data;
export const legalMailAttachments = async (messageId) =>
  (await apiClient.get(`/legal/mail-inbox/emails/${messageId}/attachments`)).data;
export const legalMailAttachmentBlob = async (messageId, attId) =>
  (await apiClient.get(`/legal/mail-inbox/emails/${messageId}/attachment-content`,
    { params: { att_id: attId }, responseType: "blob" })).data;
export const legalMailClassify = async (messageId, force = false) =>
  (await apiClient.post(`/legal/mail-inbox/emails/${messageId}/classify`, null, { params: { force } })).data;
export const legalMailForward = async (messageId, payload) =>
  (await apiClient.post(`/legal/mail-inbox/emails/${messageId}/forward`, payload)).data;
export const legalMailMoveDone = async (messageId) =>
  (await apiClient.post(`/legal/mail-inbox/emails/${messageId}/move-done`)).data;
export const legalMailAssignCase = async (messageId, payload) => {
  // Filing runs server-side in the background (OneDrive uploads can outlast
  // the proxy timeout) — poll until the job finishes and return the result.
  const start = (await apiClient.post(`/legal/mail-inbox/emails/${messageId}/assign-to-case`, payload)).data;
  if (start.status !== "processing") return start;
  const deadline = Date.now() + 6 * 60 * 1000;
  while (Date.now() < deadline) {
    await new Promise((r) => setTimeout(r, 2500));
    const st = (await apiClient.get(`/legal/mail-inbox/filing-jobs/${start.job_id}`)).data;
    if (st.status === "done") return st;
    if (st.status === "error") throw new Error(st.error || "Filing failed");
    if (st.status === "none") throw new Error("Filing was interrupted — check the case files before retrying");
  }
  throw new Error("Filing timed out — check the case files before retrying");
};
export const legalMailAutopilotGet = async () => (await apiClient.get("/legal/mail-inbox/autopilot")).data;
export const legalMailAutopilotPut = async (payload) => (await apiClient.put("/legal/mail-inbox/autopilot", payload)).data;
export const legalMailAutopilotSweep = async () => (await apiClient.post("/legal/mail-inbox/autopilot/sweep", null, { timeout: 120000 })).data;
export const legalMailHeld = async () => (await apiClient.get("/legal/mail-inbox/held")).data;
export const legalMailHeldRelease = async (imid) => (await apiClient.post(`/legal/mail-inbox/held/${encodeURIComponent(imid)}/release`)).data;
export const legalMailHeldSendPrivate = async (imid) => (await apiClient.post(`/legal/mail-inbox/held/${encodeURIComponent(imid)}/send-private`)).data;
export const legalMailSearchCases = async (q) =>
  (await apiClient.get("/legal/mail-inbox/search-cases", { params: { q } })).data;

// ==================== LEGAL CALENDAR (Phase 2) ====================
// Mirrors the P2P CollectionsCalendar but events are typed legal milestones
// (DN/SD2/hearing/etc) rather than payment promises. Auto-follow-ups are
// computed server-side from the type catalogue (e.g. SD2 served + 18d).
export const listLegalEventTypes  = async () => (await apiClient.get("/collections/ageing/legal-event-types")).data;
export const listLegalEvents      = async (params = {}) =>
  (await apiClient.get("/collections/ageing/legal-events", { params })).data;
export const createLegalEvent     = async (payload) =>
  (await apiClient.post("/collections/ageing/legal-events", payload)).data;
export const updateLegalEvent     = async (eventId, patch) =>
  (await apiClient.patch(`/collections/ageing/legal-events/${encodeURIComponent(eventId)}`, patch)).data;
export const deleteLegalEvent     = async (eventId, cascadeFollowups = false, actorIsAdmin = false) =>
  (await apiClient.delete(`/collections/ageing/legal-events/${encodeURIComponent(eventId)}`, { params: { cascade_followups: cascadeFollowups, actor_is_admin: actorIsAdmin } })).data;
// Per-type row outcome (Legal plan item 5): record_outcome | passed_no_response | response_received | served |
// expired_no_payment | payment_received | done | chase_again | cancel | allocate | filed_ch | note
export const performLegalEventAction = async (eventId, action, payload = {}, actor = "Ops", actorIsAdmin = false) =>
  (await apiClient.post(`/collections/ageing/legal-events/${encodeURIComponent(eventId)}/action`, { action, payload, actor, actor_is_admin: actorIsAdmin })).data;

// Legal Diary guardrails — read-only compat view of the PUBLISHED legal process settings.
export const getLegalDiarySettings    = async () => (await apiClient.get("/collections/ageing/legal-diary-settings")).data;

// ==================== LEGAL PROCESS (A1, Oct 2026) ====================
// Versioned process settings (draft → diff → publish → rollback), task ownership, unassigned intake, caseload.
export const getLegalProcessSettings   = async (region = "UK") => (await apiClient.get("/legal-process/settings", { params: { region } })).data;
export const saveLegalProcessDraft     = async (body, region = "UK") => (await apiClient.put("/legal-process/settings/draft", { body }, { params: { region } })).data;
export const validateLegalProcessBody  = async (body, region = "UK") => (await apiClient.post("/legal-process/settings/validate", { body }, { params: { region } })).data;
export const discardLegalProcessDraft  = async (region = "UK") => (await apiClient.delete("/legal-process/settings/draft", { params: { region } })).data;
export const fillLegalTimelineActions  = async (region = "UK") => (await apiClient.post("/legal-process/settings/draft/fill-actions", null, { params: { region } })).data;
export const publishLegalProcess       = async (reason, region = "UK") => (await apiClient.post("/legal-process/settings/publish", { reason }, { params: { region } })).data;
export const rollbackLegalProcess      = async (version, reason, region = "UK") => (await apiClient.post(`/legal-process/settings/rollback/${version}`, { reason }, { params: { region } })).data;
export const getLegalProcessHistory    = async (region = "UK") => (await apiClient.get("/legal-process/settings/history", { params: { region } })).data;
export const getLegalProcessVersion    = async (version, region = "UK") => (await apiClient.get(`/legal-process/settings/version/${version}`, { params: { region } })).data;
export const previewLegalProcess       = async (payload, region = "UK") => (await apiClient.post("/legal-process/settings/preview", payload, { params: { region } })).data;
export const setLegalEventOwner        = async (eventId, owner, reason) => (await apiClient.patch(`/legal-process/events/${encodeURIComponent(eventId)}/owner`, { owner, reason })).data;
export const backfillLegalOwners       = async (dryRun = true) => (await apiClient.post("/legal-process/owners/backfill", null, { params: { dry_run: dryRun } })).data;
export const getLegalUnassigned        = async () => (await apiClient.get("/legal-process/unassigned")).data;
export const getLegalCaseload          = async () => (await apiClient.get("/legal-process/caseload")).data;
export const setLegalCaseloadTarget    = async (handler, target) => (await apiClient.put("/legal-process/caseload/target", { handler, target })).data;
export const escalateLegalUnassigned   = async () => (await apiClient.post("/legal-process/unassigned/escalate")).data;
// B2 — referral candidates (recommend only)
export const getReferralCandidates     = async () => (await apiClient.get("/legal-process/referral-candidates")).data;
export const referCandidate            = async (caseId, reason = "") => (await apiClient.post(`/legal-process/referral-candidates/${encodeURIComponent(caseId)}/refer`, { reason })).data;
export const snoozeCandidate           = async (caseId, days, reason) => (await apiClient.post(`/legal-process/referral-candidates/${encodeURIComponent(caseId)}/snooze`, { days, reason })).data;
export const getLegalProcessMe         = async () => (await apiClient.get("/legal-process/me")).data;
// Saved list views (Dean, 15 Oct 2026) — Airtable-style filters, personal / shared / pinned per page + region
export const listSavedViews            = async (page, region = "UK") => (await apiClient.get("/views", { params: { page, region } })).data;
export const createSavedView           = async (body) => (await apiClient.post("/views", body)).data;
export const updateSavedView           = async (viewId, patch) => (await apiClient.patch(`/views/${encodeURIComponent(viewId)}`, patch)).data;
export const deleteSavedView           = async (viewId) => (await apiClient.delete(`/views/${encodeURIComponent(viewId)}`)).data;
export const pinSavedView              = async (viewId, pinned = true) => (await apiClient.post(`/views/${encodeURIComponent(viewId)}/pin`, { pinned })).data;
export const duplicateSavedView        = async (viewId) => (await apiClient.post(`/views/${encodeURIComponent(viewId)}/duplicate`)).data;

// Two-axis Journey (Dean, 13 Oct 2026) — journey · needs attention · housekeeping · audit matrix
export const getCaseJourney            = async (caseId) => (await apiClient.get(`/legal-process/journey/${encodeURIComponent(caseId)}`)).data;
export const bulkHandToLegal           = async (caseIds, reason, legalHandler = null) => (await apiClient.post("/legal-process/cases/hand-to-legal/bulk", { case_ids: caseIds, reason, legal_handler: legalHandler }, { timeout: 300000 })).data;
export const getAgeingCaseload         = async () => (await apiClient.get("/collections/ageing/caseload", { timeout: 120000 })).data;
export const downloadAgeingCaseloadCsv = async () => (await apiClient.get("/collections/ageing/caseload", { params: { format: "csv" }, responseType: "blob", timeout: 120000 })).data;
// Collector scoreboard (Caseload → Activity) — Dean, 3 Nov 2026
export const getCollectorActivity      = async (period) => (await apiClient.get("/collections/ageing/caseload/activity", { params: { period }, timeout: 120000 })).data;
export const getCollectorCash          = async (period) => (await apiClient.get("/collections/ageing/caseload/activity/cash", { params: { period }, timeout: 180000 })).data;
export const getCollectorTimeline      = async (collector, period) => (await apiClient.get(`/collections/ageing/caseload/activity/${encodeURIComponent(collector)}/timeline`, { params: { period }, timeout: 120000 })).data;
export const handCaseToLegal           = async (caseId, reason = "", legalHandler = null) => (await apiClient.post(`/legal-process/cases/${encodeURIComponent(caseId)}/hand-to-legal`, { reason, legal_handler: legalHandler })).data;
export const pauseCaseLegal            = async (caseId, body) => (await apiClient.post(`/legal-process/cases/${encodeURIComponent(caseId)}/pause`, body)).data;
export const restructureDecision       = async (caseId, body) => (await apiClient.post(`/legal-process/cases/${encodeURIComponent(caseId)}/restructure-decision`, body)).data;
export const getLegalHandlerReport     = async (params = {}) => (await apiClient.get("/legal-process/handler-report", { params, timeout: 120000 })).data;
export const downloadLegalHandlerReportCsv = async (params = {}) => (await apiClient.get("/legal-process/handler-report", { params: { ...params, format: "csv" }, responseType: "blob", timeout: 120000 })).data;
export const getNeedsAttention         = async () => (await apiClient.get("/legal-process/needs-attention")).data;
export const getLegalHousekeeping      = async (params = {}) => (await apiClient.get("/legal-process/housekeeping", { params })).data;
export const runLegalHousekeeping      = async () => (await apiClient.post("/legal-process/housekeeping/run")).data;
export const getLegalAuditMatrix       = async () => (await apiClient.get("/legal-process/audit-matrix", { timeout: 120000 })).data;
export const downloadLegalAuditMatrixCsv = async () => (await apiClient.get("/legal-process/audit-matrix", { params: { format: "csv" }, responseType: "blob", timeout: 120000 })).data;
export const applyLegalAuditFix        = async (fix, caseIds) => (await apiClient.post("/legal-process/audit-matrix/fix", { fix, case_ids: caseIds }, { timeout: 180000 })).data;
export const getLegalAuditAirtable     = async () => (await apiClient.get("/legal-process/audit-matrix/airtable", { timeout: 120000 })).data;
export const repushLegalAuditAirtable  = async (caseIds) => (await apiClient.post("/legal-process/audit-matrix/airtable/repush", { case_ids: caseIds }, { timeout: 180000 })).data;
export const normaliseOwnerNames       = async (dryRun = true) => (await apiClient.post("/legal-process/owners/normalise", null, { params: { dry_run: dryRun } })).data;
export const getHandlerAliases         = async () => (await apiClient.get("/legal-process/handler-aliases")).data;
export const saveHandlerAliases        = async (aliases) => (await apiClient.put("/legal-process/handler-aliases", { aliases })).data;
// A2 engine — bankruptcy route, working days, expiry rules
export const getRouteVocab             = async () => (await apiClient.get("/legal-process/route/vocab")).data;
export const getRouteSummary           = async () => (await apiClient.get("/legal-process/route/summary")).data;
export const getCaseRoute              = async (caseId) => (await apiClient.get(`/legal-process/cases/${encodeURIComponent(caseId)}/route`)).data;
export const moveCaseRoute             = async (caseId, to, reason) => (await apiClient.post(`/legal-process/cases/${encodeURIComponent(caseId)}/route`, { to, reason })).data;
// A2 slice 2 — gates, register checks, service records
export const getCaseGates              = async (caseId) => (await apiClient.get(`/legal-process/cases/${encodeURIComponent(caseId)}/gates`)).data;
export const attestGateItem            = async (caseId, gate, itemId, checked) => (await apiClient.post(`/legal-process/cases/${encodeURIComponent(caseId)}/gates/${gate}/attest`, { item_id: itemId, checked })).data;
export const overrideGateItem          = async (caseId, gate, itemId, reason, clear = false) => (await apiClient.post(`/legal-process/cases/${encodeURIComponent(caseId)}/gates/${gate}/override`, { item_id: itemId, reason, clear })).data;
export const recordRegisterCheck       = async (caseId, payload) => (await apiClient.post(`/legal-process/cases/${encodeURIComponent(caseId)}/register-check`, payload)).data;
export const listRegisterChecks        = async (caseId) => (await apiClient.get(`/legal-process/cases/${encodeURIComponent(caseId)}/register-checks`)).data;
// B1 — Stage 4 post-demand review outcome
export const getReviewOutcomeOptions   = async (caseId) => (await apiClient.get(`/legal-process/cases/${encodeURIComponent(caseId)}/review-outcome/options`)).data;
export const recordReviewOutcome       = async (caseId, payload) => (await apiClient.post(`/legal-process/cases/${encodeURIComponent(caseId)}/review-outcome`, payload)).data;
export const addServiceRecord          = async (caseId, payload) => (await apiClient.post(`/legal-process/cases/${encodeURIComponent(caseId)}/service-records`, payload)).data;
// C1 — bankruptcy petition hub
export const getPetitionState          = async (caseId) => (await apiClient.get(`/legal-process/cases/${encodeURIComponent(caseId)}/petition`)).data;
export const recordPetitionPrepared    = async (caseId, payload) => (await apiClient.post(`/legal-process/cases/${encodeURIComponent(caseId)}/petition/prepared`, payload)).data;
export const recordPetitionFiling      = async (caseId, payload) => (await apiClient.post(`/legal-process/cases/${encodeURIComponent(caseId)}/petition/filing`, payload)).data;
export const recordPetitionCertificate = async (caseId, payload) => (await apiClient.post(`/legal-process/cases/${encodeURIComponent(caseId)}/petition/certificate`, payload)).data;
export const recordPetitionHearing     = async (caseId, payload) => (await apiClient.post(`/legal-process/cases/${encodeURIComponent(caseId)}/petition/hearing`, payload)).data;
// C2 — hearing outcomes
export const getHearingOutcomeOptions  = async (caseId) => (await apiClient.get(`/legal-process/cases/${encodeURIComponent(caseId)}/hearing-outcome`)).data;
export const recordHearingOutcome      = async (caseId, payload) => (await apiClient.post(`/legal-process/cases/${encodeURIComponent(caseId)}/hearing-outcome`, payload)).data;
// C3 — Menzies claim pack + court costs ledger
export const getMenziesState           = async (caseId) => (await apiClient.get(`/legal-process/cases/${encodeURIComponent(caseId)}/menzies`)).data;
// Witness statement wizard (Dean, 14 Oct 2026)
const wsBase = (caseId, wsId) => `/legal-process/cases/${encodeURIComponent(caseId)}/witness${wsId ? `/${encodeURIComponent(wsId)}` : ""}`;
export const getWitnessContext         = async (caseId) => (await apiClient.get(wsBase(caseId), { timeout: 120000 })).data;
export const createWitnessStatement    = async (caseId, payload) => (await apiClient.post(wsBase(caseId), payload, { timeout: 120000 })).data;
export const getWitnessStatement       = async (caseId, wsId) => (await apiClient.get(wsBase(caseId, wsId))).data;
export const updateWitnessStatement    = async (caseId, wsId, patch) => (await apiClient.put(wsBase(caseId, wsId), patch)).data;
export const deleteWitnessStatement    = async (caseId, wsId) => (await apiClient.delete(wsBase(caseId, wsId))).data;
export const cloneWitnessStatement     = async (caseId, wsId) => (await apiClient.post(`${wsBase(caseId, wsId)}/clone`)).data;
export const startWitnessAi            = async (caseId, wsId, payload) => (await apiClient.post(`${wsBase(caseId, wsId)}/ai`, payload)).data;
export const getWitnessAiRun           = async (caseId, wsId, runId) => (await apiClient.get(`${wsBase(caseId, wsId)}/ai/${encodeURIComponent(runId)}`)).data;
export const applyWitnessAiRun         = async (caseId, wsId, runId, payload) => (await apiClient.post(`${wsBase(caseId, wsId)}/ai/${encodeURIComponent(runId)}/apply`, payload)).data;
export const rewriteWitnessParagraph   = async (caseId, wsId, payload) => (await apiClient.post(`${wsBase(caseId, wsId)}/rewrite`, payload, { timeout: 180000 })).data;
export const witnessPreviewPdf         = async (caseId, wsId, dated) => (await apiClient.get(`${wsBase(caseId, wsId)}/preview.pdf`, { params: dated ? { dated } : {}, responseType: "blob", timeout: 120000 })).data;
export const witnessPreviewDocx        = async (caseId, wsId) => (await apiClient.get(`${wsBase(caseId, wsId)}/preview.docx`, { responseType: "blob", timeout: 120000 })).data;
export const witnessBundlePdf          = async (caseId, wsId) => (await apiClient.get(`${wsBase(caseId, wsId)}/bundle.pdf`, { responseType: "blob", timeout: 180000 })).data;
export const finishWitnessStatement    = async (caseId, wsId, payload) => (await apiClient.post(`${wsBase(caseId, wsId)}/finish`, payload, { timeout: 300000 })).data;
export const slackWitnessStatement     = async (caseId, wsId, payload) => (await apiClient.post(`${wsBase(caseId, wsId)}/slack`, payload)).data;

// N244 Application Notice wizard (Dean, 22 Oct 2026) — /api/legal-process/cases/{case_id}/n244/…
const n244Base = (caseId) => `/legal-process/cases/${encodeURIComponent(caseId)}/n244`;
export const getN244Context  = async (caseId) => (await apiClient.get(n244Base(caseId), { timeout: 120000 })).data;
export const n244CourtContact = async (caseId, name) => (await apiClient.get(`${n244Base(caseId)}/court-contact`, { params: { name }, timeout: 60000 })).data;
export const n244Compose     = async (caseId, payload) => (await apiClient.post(`${n244Base(caseId)}/compose`, payload)).data;
export const n244AiDraft     = async (caseId, payload) => (await apiClient.post(`${n244Base(caseId)}/ai-draft`, payload)).data;
export const n244AiRun       = async (caseId, runId) => (await apiClient.get(`${n244Base(caseId)}/ai-draft/${encodeURIComponent(runId)}`)).data;
export const n244Preview     = async (caseId, payload) => (await apiClient.post(`${n244Base(caseId)}/preview`, payload, { responseType: "blob", timeout: 120000 })).data;
export const n244Generate    = async (caseId, payload) => (await apiClient.post(`${n244Base(caseId)}/generate`, payload, { timeout: 300000 })).data;
export const n244MarkSent    = async (caseId, appId, payload) => (await apiClient.post(`${n244Base(caseId)}/${encodeURIComponent(appId)}/sent`, payload)).data;

export const compileMenziesPack        = async (caseId, payload) => (await apiClient.post(`/legal-process/cases/${encodeURIComponent(caseId)}/menzies/compile`, payload, { timeout: 180000 })).data;
export const sendMenziesPack           = async (caseId, payload) => (await apiClient.post(`/legal-process/cases/${encodeURIComponent(caseId)}/menzies/send`, payload, { timeout: 180000 })).data;
export const getLegalCosts             = async (caseId) => (await apiClient.get(`/legal-process/cases/${encodeURIComponent(caseId)}/costs`)).data;
export const addLegalCost              = async (caseId, payload) => (await apiClient.post(`/legal-process/cases/${encodeURIComponent(caseId)}/costs`, payload)).data;
export const voidLegalCost             = async (caseId, entryId, reason) => (await apiClient.post(`/legal-process/cases/${encodeURIComponent(caseId)}/costs/${encodeURIComponent(entryId)}/void`, { reason })).data;
export const postLegalCostsToLoanDisk  = async (caseId, entryIds) => (await apiClient.post(`/legal-process/cases/${encodeURIComponent(caseId)}/costs/post-to-loandisk`, { entry_ids: entryIds }, { timeout: 120000 })).data;
export const legalUploadChunk          = async (fd) => (await apiClient.post("/legal-process/upload-chunk", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 120000 })).data;
export const runLegalExpirySweep       = async () => (await apiClient.post("/legal-process/engine/expiry-sweep")).data;
// A2 slice 3 — SLA digest + customer emails from legal@
export const getSlaDigestPreview       = async () => (await apiClient.get("/legal-process/sla/digest-preview")).data;
export const runSlaNow                 = async (post = true) => (await apiClient.post("/legal-process/sla/run-now", null, { params: { post } })).data;
export const getCommsTemplates         = async () => (await apiClient.get("/legal-process/comms/templates")).data;
export const previewCustomerEmail      = async (payload) => (await apiClient.post("/legal-process/comms/preview", payload)).data;
export const testSendCustomerEmail     = async (payload) => (await apiClient.post("/legal-process/comms/test-send", payload)).data;
export const runScheduledCustomerEmails = async () => (await apiClient.post("/legal-process/comms/run-scheduled")).data;
export const getCaseComms              = async (caseId) => (await apiClient.get(`/legal-process/cases/${encodeURIComponent(caseId)}/comms`)).data;
export const getBankHolidays           = async () => (await apiClient.get("/legal-process/bank-holidays")).data;
export const refreshBankHolidays       = async () => (await apiClient.post("/legal-process/bank-holidays/refresh")).data;


// Issuing courts — logged per party (company / guarantor) via the GOV.UK
// court finder. Suggestions auto-resolve the address: Companies House
// registered office (company) or LoanDisk borrower address (guarantor).
export const getCourtSuggestions = async (caseId, party = "company", borrowerId = null) =>
  (await apiClient.get(`/collections/ageing/cases/${encodeURIComponent(caseId)}/court-suggestions`, {
    params: { party, ...(borrowerId != null ? { borrower_id: borrowerId } : {}) },
    timeout: 120000, // guarantor lookups scan the LoanDisk borrower list — can take ~1 min
  })).data;
export const setCaseIssuingCourt   = async (caseId, court, party = "company", selectedBy = "Ops") =>
  (await apiClient.post(`/collections/ageing/cases/${encodeURIComponent(caseId)}/issuing-court`, { court, party, selected_by: selectedBy })).data;
export const clearCaseIssuingCourt = async (caseId, party = "company") =>
  (await apiClient.delete(`/collections/ageing/cases/${encodeURIComponent(caseId)}/issuing-court`, { params: { party } })).data;
export const patchCaseIssuingCourtMeta = async (caseId, party, patch) =>
  (await apiClient.patch(`/collections/ageing/cases/${encodeURIComponent(caseId)}/issuing-court/meta`, { party, ...patch })).data;

// Arrears snapshot history — every upload is stored immutably; metrics
// live forever, full row detail for the most recent ~12 months.
export const listArrearsSnapshots = async (limit = 120) =>
  (await apiClient.get("/collections/ageing/arrears/snapshots", { params: { limit } })).data;
export const getArrearsSnapshotDetail = async (snapshotId) =>
  (await apiClient.get(`/collections/ageing/arrears/snapshots/${encodeURIComponent(snapshotId)}`)).data;

// Daily collections report — custom period trend analysis (weeks/months/YTD)
export const getCollectionsReportRange = async (fromDate, toDate) =>
  (await apiClient.get("/collections/report-range", { params: { from_date: fromDate, to_date: toDate }, timeout: 60000 })).data;

// Audit trail settings — which modules feed the automatic audit log
export const getAuditSettings    = async () => (await apiClient.get("/audit-settings")).data;
export const updateAuditSettings = async (enabledModules) =>
  (await apiClient.post("/audit-settings", { enabled_modules: enabledModules })).data;
// Audit trail export (Admin only) — every matching row as CSV, London time, bot actions folded in
export const getAuditPeople = async (days = 90) => (await apiClient.get("/audit-logs/people", { params: { days } })).data;
export const countAuditExport = async (params) =>
  (await apiClient.get("/audit-logs/export", { params: { ...params, count_only: true }, timeout: 120000 })).data;
export const downloadAuditExport = async (params) =>
  (await apiClient.get("/audit-logs/export", { params, responseType: "blob", timeout: 300000 })).data;
// One-time ticket → the browser downloads the CSV as a plain navigation (no XHR timeout, native progress bar)
export const requestAuditExportTicket = async (params) => (await apiClient.post("/audit-logs/export/ticket", null, { params })).data;
export const auditExportDownloadUrl = (ticket) => `${process.env.REACT_APP_BACKEND_URL}/api/audit-logs/export/download?ticket=${encodeURIComponent(ticket)}`;

// ==================== HANDLER ADMIN ====================
// Replaces the hardcoded HANDLERS list with a Mongo-backed list maintainable
// in Admin Settings. Also exposed via /handlers (existing endpoint, unchanged
// signature — just now reads from DB with hardcoded fallback).
export const listHandlersAdmin    = async () => (await apiClient.get("/collections/ageing/handlers/admin")).data;
export const createHandler        = async (payload) => (await apiClient.post("/collections/ageing/handlers", payload)).data;
export const updateHandler        = async (id, patch) => (await apiClient.patch(`/collections/ageing/handlers/${id}`, patch)).data;
export const deleteHandler        = async (id) => (await apiClient.delete(`/collections/ageing/handlers/${id}`)).data;
export const seedHandlersHardcoded = async () => (await apiClient.post("/collections/ageing/handlers/seed-from-hardcoded")).data;
export const syncHandlersLoanDisk  = async () => (await apiClient.post("/collections/ageing/handlers/sync-from-loandisk")).data;

// ==================== LEGAL CASE FILES (OneDrive metadata sync) ====================
// Files stay on OneDrive — the portal stores metadata (webUrl, name, size,
// last-modified) and renders one-click links per case. The sync is triggered
// from the Legal Case Tracker → Refresh All button (Phase 3).
export const triggerOneDriveSync       = async () => (await apiClient.post("/legal/cases/files/sync-onedrive")).data;
export const getOneDriveSyncStatus     = async () => (await apiClient.get("/legal/cases/files/sync-onedrive/status")).data;
export const diagnoseOneDrive          = async () => (await apiClient.post("/legal/cases/files/diagnose")).data;
export const getLegalCaseFiles         = async (caseId) =>
  (await apiClient.get(`/legal/cases/files/by-case/${encodeURIComponent(caseId)}`)).data;
export const getLegalCaseFilePreviewUrl = async (fileId) =>
  (await apiClient.get(`/legal/cases/files/${encodeURIComponent(fileId)}/preview-url`)).data;
export const saveEmailToFiles          = async (caseId, messageId, mailboxUpn = null) =>
  (await apiClient.post("/legal/cases/files/from-email", { case_id: caseId, message_id: messageId, mailbox: mailboxUpn })).data;
export const uploadCaseFile = async (caseId, file) => {
  const fd = new FormData();
  fd.append("file", file);
  return (await apiClient.post(`/legal/docs/${encodeURIComponent(caseId)}/files/upload`, fd,
    { headers: { "Content-Type": "multipart/form-data" } })).data;
};

// ── Department inboxes (Accounts / Legal) — one API shape per mailbox ───────
// Personal email sign-off (My Account → Email sign-off)
export const getMySignoff  = async () => (await apiClient.get("/me/signoff")).data;
export const saveMySignoff = async (patch) => (await apiClient.put("/me/signoff", patch)).data;

export const deptInboxApi = (dept, region = "UK") => {
  const base = `/inbox/${dept}`;
  const p = (extra = {}) => ({ params: { region, ...extra } });
  const m = (id) => `${base}/emails/${encodeURIComponent(id)}`;
  const isAus = region === "AUS";
  return {
    dept,
    region,
    isAus,
    currency: isAus ? "A$" : "£",
    domain: isAus ? "simplyfunded.com.au" : "simplyfunded.co.uk",
    list: async ({ limit = 200, folder = "inbox" } = {}) => (await apiClient.get(`${base}/emails`, p({ limit, folder }))).data,
    count: async () => (await apiClient.get(`${base}/count`, p())).data,
    getEmail: async (id) => (await apiClient.get(m(id), p())).data,
    attachments: async (id) => (await apiClient.get(`${m(id)}/attachments`, p())).data,
    attachmentBlob: async (id, attId) => (await apiClient.get(`${m(id)}/attachment-content`, { ...p({ att_id: attId }), responseType: "blob" })).data,
    classify: async (id, force = false) => (await apiClient.post(`${m(id)}/classify`, null, p({ force }))).data,
    rematch: async (id) => (await apiClient.post(`${m(id)}/rematch`, null, p())).data,
    reply: async (id, payload) => (await apiClient.post(`${m(id)}/reply`, payload, p())).data,
    forward: async (id, payload) => (await apiClient.post(`${m(id)}/forward`, payload, p())).data,
    moveDone: async (id, payload = null) => (await apiClient.post(`${m(id)}/move-done`, payload, p())).data,
    assign: async (id, assignee, assigneeEmail = "") => (await apiClient.post(`${m(id)}/assign`, { assignee, assignee_email: assigneeEmail }, p())).data,
    assignees: async () => (await apiClient.get(`${base}/assignees`, p())).data,
    compose: async (payload) => (await apiClient.post(`${base}/compose`, payload, p())).data,
    logToCase: async (id, payload) => (await apiClient.post(`${m(id)}/log-to-case`, payload, p())).data,
    searchCases: async (q) => (await apiClient.get(`${base}/search-cases`, p({ q }))).data,
    getSettings: async () => (await apiClient.get(`${base}/settings`, p())).data,
    setSettings: async (patch) => (await apiClient.put(`${base}/settings`, patch, p())).data,
    folders: async (force = false) => (await apiClient.get(`${base}/folders`, p({ force }))).data,
    move: async (id, folderId) => (await apiClient.post(`${m(id)}/move`, { folder_id: folderId }, p())).data,
    autopilotLog: async (limit = 100) => (await apiClient.get(`${base}/autopilot/log`, p({ limit }))).data,
    draftVsSent: async (weeks = 4) => (await apiClient.get(`${base}/autopilot/draft-vs-sent`, p({ weeks }))).data,
    prereplyAuditExport: async () => (await apiClient.get(`${base}/prereply-audit/export`, { ...p(), responseType: "blob", timeout: 120000 })).data,
    autopilotRun: async () => (await apiClient.post(`${base}/autopilot/run-now`, null, p())).data,
    uploadChunk: async (fd) => (await apiClient.post(`${base}/upload-chunk`, fd, { ...p(), headers: { "Content-Type": "multipart/form-data" }, timeout: 120000 })).data,
    listFiles: async (caseId) => (await apiClient.get(`${base}/file-attachments`, { ...p({ case_id: caseId }), timeout: 60000 })).data,
    search: async (q, top = 60) => (await apiClient.get(`${base}/search`, p({ q, top }))).data,
    recipients: async (q) => (await apiClient.get(`${base}/recipients`, p({ q }))).data,
    adoptLoan: async (id, actor = "") => (await apiClient.post(`${m(id)}/adopt-loan`, { actor }, p())).data,
    prereply: async (id) => (await apiClient.post(`${m(id)}/prereply`, null, { ...p(), timeout: 120000 })).data,
    available: async () => (await apiClient.get(`${base}/available`, p())).data,
    p2pPrefill: async (id) => (await apiClient.get(`${m(id)}/p2p-prefill`, p())).data,
    capturePromise: async (id, payload) => (await apiClient.post(`${m(id)}/p2p`, payload, p())).data,
    bulk: async (payload) => (await apiClient.post(`${base}/bulk`, payload, { ...p(), timeout: 180000 })).data,
    triageCatalog: async () => (await apiClient.get(`${base}/triage-catalog`, p())).data,
    knowledge: async () => (await apiClient.get(`${base}/knowledge`, p())).data,
    addKnowledge: async (payload) => (await apiClient.post(`${base}/knowledge`, payload, { ...p(), timeout: 120000 })).data,
    deleteKnowledge: async (id) => (await apiClient.delete(`${base}/knowledge/${id}`, p())).data,
  };
};

// ── Shared email templates — one library for every mailbox (Juffrey / Dean, 12 Oct 2026) ─────────────────────────────
export const listEmailTemplates = async (dept = "all", region = "all") =>
  (await apiClient.get("/email-templates", { params: { dept, region } })).data;
export const createEmailTemplate = async (payload) => (await apiClient.post("/email-templates", payload)).data;
export const updateEmailTemplate = async (id, payload) => (await apiClient.put(`/email-templates/${id}`, payload)).data;
export const deleteEmailTemplate = async (id) => (await apiClient.delete(`/email-templates/${id}`)).data;
export const markEmailTemplateUsed = (id) => apiClient.post(`/email-templates/${id}/used`).catch(() => {});

// Chunk a browser File into ~2 MB pieces and push them through `uploadChunk`
// (proxy-safe). Resolves to the attachment descriptor the reply endpoint expects.
export const uploadInboxAttachment = async (uploadChunk, file, onProgress) => {
  const CHUNK = 2 * 1024 * 1024;
  const uploadId = `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`;
  const total = Math.max(1, Math.ceil(file.size / CHUNK));
  for (let i = 0; i < total; i++) {
    const fd = new FormData();
    fd.append("upload_id", uploadId);
    fd.append("chunk_index", String(i));
    fd.append("total_chunks", String(total));
    fd.append("filename", file.name);
    fd.append("chunk", file.slice(i * CHUNK, Math.min(file.size, (i + 1) * CHUNK)), file.name);
    await uploadChunk(fd);
    onProgress?.(Math.round(((i + 1) / total) * 100));
  }
  return { upload_id: uploadId, name: file.name, content_type: file.type || "application/octet-stream" };
};

// ── Applications Inbox (Make.com replacement — Phase 1) ─────────────────────
export const appInboxList = async ({ limit = 50, folder = "inbox" } = {}) =>
  (await apiClient.get("/applications/inbox/emails", { params: { limit, folder } })).data;
export const appInboxGetEmail = async (messageId) =>
  (await apiClient.get(`/applications/inbox/emails/${encodeURIComponent(messageId)}`)).data;
export const appInboxAttachSO = async (messageId, payload) =>
  (await apiClient.post(`/applications/inbox/emails/${encodeURIComponent(messageId)}/attach-standing-order`, payload)).data;
export const appInboxClassify = async (messageId, force = false) =>
  (await apiClient.post(`/applications/inbox/emails/${encodeURIComponent(messageId)}/classify`, null, { params: { force } })).data;
export const appInboxExtract = async (messageId) =>
  (await apiClient.post(`/applications/inbox/emails/${encodeURIComponent(messageId)}/extract`)).data;
export const appInboxExtractStatus = async (messageId) =>
  (await apiClient.get(`/applications/inbox/emails/${encodeURIComponent(messageId)}/extract/status`)).data;
export const appInboxCreateApp = async (messageId, payload) =>
  (await apiClient.post(`/applications/inbox/emails/${encodeURIComponent(messageId)}/create-app`, payload)).data;
export const appInboxGetSettings = async () =>
  (await apiClient.get("/applications/inbox/settings")).data;
export const appInboxSetSettings = async (payload) =>
  (await apiClient.put("/applications/inbox/settings", payload)).data;
export const appInboxAckDefaults = async () => (await apiClient.get("/applications/inbox/ack-defaults")).data;
export const appInboxAckPreview = async (messageId, intent, params = {}) =>
  (await apiClient.get(`/applications/inbox/emails/${encodeURIComponent(messageId)}/ack-preview`, { params: { intent, ...params } })).data;
export const appInboxSendAck = async (messageId, payload) =>
  (await apiClient.post(`/applications/inbox/emails/${encodeURIComponent(messageId)}/send-ack`, payload)).data;
export const appInboxAckTest = async (payload) => (await apiClient.post("/applications/inbox/settings/ack-test", payload)).data;
export const appInboxHistory = async (messageId) =>
  (await apiClient.get(`/applications/inbox/emails/${encodeURIComponent(messageId)}/history`)).data;
export const appInboxPrepUpdate = async (messageId, sfSearch = null) =>
  (await apiClient.post(`/applications/inbox/emails/${encodeURIComponent(messageId)}/prep-update`, null,
    { params: sfSearch ? { sf_search: sfSearch } : {} })).data;
export const appInboxPostUpdate = async (messageId, payload) =>
  (await apiClient.post(`/applications/inbox/emails/${encodeURIComponent(messageId)}/post-update`, payload)).data;
export const appInboxQueue = async (status = "all", limit = 50) =>
  (await apiClient.get("/applications/inbox/queue", { params: { status, limit } })).data;
export const appInboxAudit = async (params = {}) =>
  (await apiClient.get("/applications/inbox/audit", { params })).data;
export const appInboxAuditCsv = async (params = {}) =>
  (await apiClient.get("/applications/inbox/audit/export.csv", { params, responseType: "blob" })).data;
export const appInboxQueueApprove = async (queueId, override = null) =>
  (await apiClient.post(`/applications/inbox/queue/${encodeURIComponent(queueId)}/approve`, override)).data;
export const appInboxQueueReject = async (queueId) =>
  (await apiClient.post(`/applications/inbox/queue/${encodeURIComponent(queueId)}/reject`)).data;
export const appInboxRunAutopilot = async () =>
  (await apiClient.post("/applications/inbox/autopilot/run")).data;
export const appInboxGetAttachments = async (messageId) =>
  (await apiClient.get(`/applications/inbox/emails/${encodeURIComponent(messageId)}/attachments`)).data;
export const appInboxAttachmentBlob = async (messageId, attId) =>
  (await apiClient.get(`/applications/inbox/emails/${encodeURIComponent(messageId)}/attachment-content`,
    { params: { att_id: attId }, responseType: "blob" })).data;
export const appInboxForward = async (messageId, payload) =>
  (await apiClient.post(`/applications/inbox/emails/${encodeURIComponent(messageId)}/forward`, payload)).data;
export const appInboxMoveDone = async (messageId) =>
  (await apiClient.post(`/applications/inbox/emails/${encodeURIComponent(messageId)}/move-done`)).data;
export const appInboxStatusOptions = async () =>
  (await apiClient.get("/applications/inbox/status-options")).data;
export const appInboxSetAppStatus = async (itemId, label) =>
  (await apiClient.post(`/applications/inbox/apps/${encodeURIComponent(itemId)}/status`, { label })).data;
export const appInboxReply = async (messageId, payload) =>
  (await apiClient.post(`/applications/inbox/emails/${encodeURIComponent(messageId)}/reply`, payload)).data;
export const appInboxAssign = async (messageId, assignee, assigneeEmail = "") =>
  (await apiClient.post(`/applications/inbox/emails/${encodeURIComponent(messageId)}/assign`, { assignee, assignee_email: assigneeEmail })).data;
export const appInboxMarkDuplicate = async (messageId, payload) =>
  (await apiClient.post(`/applications/inbox/emails/${encodeURIComponent(messageId)}/mark-duplicate`, payload)).data;
export const appInboxBrokers = async () =>
  (await apiClient.get("/applications/inbox/brokers")).data;
export const appInboxLoanDiskBorrowers = async (q) =>
  (await apiClient.get("/applications/inbox/loandisk-borrowers", { params: { q } })).data;
export const appInboxAusDiagnose = async () =>
  (await apiClient.get("/applications/inbox/aus/diagnose")).data;
// Application Hub — work-from-the-portal pieces (Dean, Oct 2026): folders · move · search · New email · bulk
export const appInboxFolders = async (force = false) =>
  (await apiClient.get("/applications/inbox/folders", { params: { force } })).data;
export const appInboxMove = async (messageId, folderId) =>
  (await apiClient.post(`/applications/inbox/emails/${encodeURIComponent(messageId)}/move`, { folder_id: folderId })).data;
export const appInboxSearch = async (q, top = 60) =>
  (await apiClient.get("/applications/inbox/search", { params: { q, top } })).data;
export const appInboxCompose = async (payload) =>
  (await apiClient.post("/applications/inbox/compose", payload, { timeout: 180000 })).data;
// The dept-inbox shaped adapter the shared pieces (RecipientInput, BulkBar, uploadInboxAttachment) expect.
export const appInboxApi = (region = "UK") => ({
  dept: "applications",
  region,
  isAus: region === "AUS",
  domain: region === "AUS" ? "simplyfunded.com.au" : "simplyfunded.co.uk",
  recipients: async (q) => (await apiClient.get("/applications/inbox/recipients", { params: { q } })).data,
  assignees: async () => (await apiClient.get("/applications/inbox/assignees")).data,
  bulk: async (payload) => (await apiClient.post("/applications/inbox/bulk", payload, { timeout: 180000 })).data,
  uploadChunk: async (fd) => (await apiClient.post("/applications/inbox/upload-chunk", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 120000 })).data,
  compose: appInboxCompose,
  attachments: appInboxGetAttachments,
});
export const appInboxListFiles = async (sf, itemId) =>
  (await apiClient.get("/applications/inbox/file-attachments", { params: { sf: sf || "", item_id: itemId || "" }, timeout: 60000 })).data;

// Complaints register
export const getComplaintsSettings = async () =>
  (await apiClient.get("/complaints/settings")).data;
export const updateComplaintsSettings = async (payload) =>
  (await apiClient.patch("/complaints/settings", payload)).data;
export const listComplaints = async (params = {}) =>
  (await apiClient.get("/complaints", { params })).data;
export const complaintsForCase = async (caseId) =>
  (await apiClient.get(`/complaints/by-case/${caseId}`)).data;
export const complaintCaseSearch = async (q) =>
  (await apiClient.get("/complaints/case-search", { params: { q } })).data;
export const createComplaint = async (payload) =>
  (await apiClient.post("/complaints", payload)).data;
export const updateComplaint = async (complaintId, payload) =>
  (await apiClient.patch(`/complaints/${complaintId}`, payload)).data;
export const deleteComplaint = async (complaintId) =>
  (await apiClient.delete(`/complaints/${complaintId}`)).data;
export const addComplaintComm = async (complaintId, payload) =>
  (await apiClient.post(`/complaints/${complaintId}/comms`, payload)).data;
export const sendComplaintAck = async (complaintId, payload) =>
  (await apiClient.post(`/complaints/${complaintId}/send-ack`, payload)).data;

// Monday Mirror autopilot
export const mirrorAutopilotSettings = async () =>
  (await apiClient.get("/applications/mirror/autopilot/settings")).data;
export const mirrorAutopilotSaveSettings = async (payload) =>
  (await apiClient.put("/applications/mirror/autopilot/settings", payload)).data;
export const mirrorAutopilotStatus = async () =>
  (await apiClient.get("/applications/mirror/autopilot/status")).data;
export const mirrorAutopilotQueue = async (status = "proposed") =>
  (await apiClient.get("/applications/mirror/autopilot/queue", { params: { status } })).data;
export const mirrorAutopilotApprove = async (queueId, actor) =>
  (await apiClient.post(`/applications/mirror/autopilot/queue/${queueId}/approve`, { actor })).data;
export const mirrorAutopilotDismiss = async (queueId, actor) =>
  (await apiClient.post(`/applications/mirror/autopilot/queue/${queueId}/dismiss`, { actor })).data;
export const mirrorAutopilotRun = async () =>
  (await apiClient.post("/applications/mirror/autopilot/run")).data;
export const mirrorAutopilotApproveCatchUp = async (actor) =>
  (await apiClient.post("/applications/mirror/autopilot/queue/catch-up/approve-all", { actor })).data;
export const mirrorNeedsReview = async () => (await apiClient.get("/applications/mirror/autopilot/needs-review")).data;
export const mirrorAutopilotFlags = async (params = {}) =>
  (await apiClient.get("/applications/mirror/autopilot/flags", { params })).data;
export const mirrorAutopilotDismissFlag = async (itemId, stage) =>
  (await apiClient.post("/applications/mirror/autopilot/flags/dismiss", { item_id: itemId, stage })).data;
// Recipient check — deals held / flagged because the contact email is an automated (no-reply) address
export const mirrorRecipientList = async () => (await apiClient.get("/applications/mirror/autopilot/recipient-check")).data;
export const mirrorRecipientApply = async (itemId, email, actor) =>
  (await apiClient.post(`/applications/mirror/autopilot/recipient-check/${itemId}/apply`, { email, actor })).data;
export const mirrorRecipientDismiss = async (itemId, actor, whitelist = false) =>
  (await apiClient.post(`/applications/mirror/autopilot/recipient-check/${itemId}/dismiss`, { actor, whitelist })).data;
export const mirrorRecipientRun = async () => (await apiClient.post("/applications/mirror/autopilot/recipient-check/run")).data;
export const mirrorRecipientForgetMapping = async (badEmail, fixedEmail, actor, restore = false) =>
  (await apiClient.post(`/applications/mirror/autopilot/recipient-check/mappings/${restore ? "restore" : "forget"}`, { bad_email: badEmail, fixed_email: fixedEmail, actor })).data;
// Portal-owned Monday trigger events (Decline Send / Request Info / Chase IR → portal handlers)
export const mirrorEventsStatus = async () =>
  (await apiClient.get("/applications/mirror/events/status")).data;
export const mirrorEventsSettings = async () =>
  (await apiClient.get("/applications/mirror/events/settings")).data;
export const mirrorEventsSaveSettings = async (payload) =>
  (await apiClient.put("/applications/mirror/events/settings", payload)).data;
export const mirrorEventsList = async (status = "", kind = "", limit = 60) =>
  (await apiClient.get("/applications/mirror/events", { params: { status, kind, limit } })).data;
export const mirrorEventsApprove = async (eventId, actor) =>
  (await apiClient.post(`/applications/mirror/events/${eventId}/approve`, { actor })).data;
export const mirrorEventsDismiss = async (eventId, actor) =>
  (await apiClient.post(`/applications/mirror/events/${eventId}/dismiss`, { actor })).data;
export const mirrorEventsPreview = async (itemId, kind) =>
  (await apiClient.post("/applications/mirror/events/preview", { item_id: itemId, kind })).data;
export const mirrorEventsPollNow = async () =>
  (await apiClient.post("/applications/mirror/events/poll")).data;
export const mirrorEventsRegisterWebhooks = async (actor) =>
  (await apiClient.post("/applications/mirror/events/webhooks/register", { actor })).data;
export const mirrorEventsRemoveWebhooks = async (actor) =>
  (await apiClient.post("/applications/mirror/events/webhooks/remove", { actor })).data;
export const mirrorEventsCompare = async (hours = 24) =>
  (await apiClient.get("/applications/mirror/events/compare", { params: { hours } })).data;
// ── Cody (applications assistant: jobs · queue · documents · log) ──
export const codyStatus = async () => (await apiClient.get("/applications/cody/status")).data;
export const codyJobs = async () => (await apiClient.get("/applications/cody/jobs")).data;
export const codySaveJob = async (key, patch) => (await apiClient.put(`/applications/cody/jobs/${key}`, patch)).data;
export const codyVersions = async (key) => (await apiClient.get(`/applications/cody/jobs/${key}/versions`)).data;
export const codyRestore = async (key, version) => (await apiClient.post(`/applications/cody/jobs/${key}/restore/${version}`)).data;
export const codyDocs = async () => (await apiClient.get("/applications/cody/docs")).data;
export const codyUploadDoc = async (file, kind, jobKeys = []) => {
  const fd = new FormData();
  fd.append("file", file); fd.append("kind", kind); fd.append("job_keys", jobKeys.join(","));
  return (await apiClient.post("/applications/cody/docs", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 300000 })).data;
};
export const codyRemoveDoc = async (docId) => (await apiClient.delete(`/applications/cody/docs/${docId}`)).data;
export const codyQueue = async () => (await apiClient.get("/applications/cody/queue")).data;
export const codyRuns = async (job = "", status = "", limit = 60) =>
  (await apiClient.get("/applications/cody/runs", { params: { job, status, limit } })).data;
export const codyRunDetail = async (runId) => (await apiClient.get(`/applications/cody/runs/${runId}`)).data;
export const codyApprove = async (runId, note, extra) => (await apiClient.post(`/applications/cody/runs/${runId}/approve`, { ...(note ? { note } : {}), ...(extra || {}) })).data;
export const codyPark = async (runId) => (await apiClient.post(`/applications/cody/runs/${runId}/park`)).data;
export const codyDismiss = async (runId) => (await apiClient.post(`/applications/cody/runs/${runId}/dismiss`)).data;
export const codyRun = async (job, item, test = false, reply = {}) =>
  (await apiClient.post("/applications/cody/run", { job, item, test, reply_text: reply.text || undefined, reply_from: reply.from || undefined, reply_subject: reply.subject || undefined }, { timeout: 180000 })).data;
export const codySaveSettings = async (patch) => (await apiClient.put("/applications/cody/settings", patch)).data;
export const codyTalk = async () => (await apiClient.get("/applications/cody/talk")).data;
export const codySaveTalk = async (patch) => (await apiClient.put("/applications/cody/talk", patch)).data;
export const codySlackUsers = async (force = false) => (await apiClient.get("/applications/cody/talk/users", { params: { force } })).data;
export const codyConversations = async (limit = 50) => (await apiClient.get("/applications/cody/talk/conversations", { params: { limit } })).data;
export const codyTalkTest = async (topic) => (await apiClient.post(`/applications/cody/talk/test/${topic}`)).data;
export const codyTopups = async () => (await apiClient.get("/applications/cody/topups")).data;
export const codyTopupsSettings = async (patch) => (await apiClient.put("/applications/cody/topups/settings", patch)).data;
export const codyTopupsScan = async () => (await apiClient.post("/applications/cody/topups/scan", {}, { timeout: 300000 })).data;
export const codyTopupsBatch = async () => (await apiClient.post("/applications/cody/topups/endorsement-batch")).data;
export const codyTopupsEndorse = async (batchId, mode, numbers = []) => (await apiClient.post(`/applications/cody/topups/batch/${batchId}/endorse`, { mode, numbers })).data;
export const codyTopupsEmail = async (batchId, mode, numbers = []) => (await apiClient.post(`/applications/cody/topups/batch/${batchId}/email`, { mode, numbers })).data;
export const codyTopupsFollowups = async () => (await apiClient.post("/applications/cody/topups/followups", {}, { timeout: 300000 })).data;
export const codyTopupState = async (topupId, state, reason = "") => (await apiClient.post(`/applications/cody/topups/${topupId}/state`, { state, reason })).data;
export const codyTopupCreateApp = async (topupId) => (await apiClient.post(`/applications/cody/topups/${topupId}/create-app`, {}, { timeout: 120000 })).data;
export const codyTopupPreview = async (topupId) => (await apiClient.get(`/applications/cody/topups/preview-email/${topupId}`)).data;
export const codyPack = async () => (await apiClient.get("/applications/cody/pack")).data;
export const codyPackInstall = async (only = null) => (await apiClient.post("/applications/cody/pack/install", { only }, { timeout: 120000 })).data;
export const codyAnswerQuery = async (runId, body) => (await apiClient.post(`/applications/cody/runs/${runId}/answer-query`, body, { timeout: 120000 })).data;
export const codyAnswerMistake = async (runId, body) => (await apiClient.post(`/applications/cody/runs/${runId}/answer-mistake`, body, { timeout: 120000 })).data;
export const appInboxSearchApps = async (q) =>
  (await apiClient.get("/applications/inbox/search-apps", { params: { q } })).data;
export const appInboxAutopilotStatus = async () =>
  (await apiClient.get("/applications/inbox/autopilot/status")).data;
export const mirrorAssignItem = async (itemId, assignee) =>
  (await apiClient.post(`/applications/mirror/items/${encodeURIComponent(itemId)}/assign`, { assignee })).data;

// ── Monday Mirror (processor workbench over the Funding Apps board) ─────────
export const mirrorClosed = async (params) =>
  (await apiClient.get("/applications/mirror/closed", { params })).data;
export const mirrorBoard = async (fresh = false) =>
  (await apiClient.get("/applications/mirror/board", { params: fresh ? { fresh: 1 } : {} })).data;
export const mirrorItem = async (itemId) =>
  (await apiClient.get(`/applications/mirror/items/${encodeURIComponent(itemId)}`)).data;
export const mirrorSetColumn = async (itemId, columnId, value, createLabel = false) =>
  (await apiClient.put(`/applications/mirror/items/${encodeURIComponent(itemId)}/column`, { column_id: columnId, value, create_label: createLabel })).data;
export const mirrorPostUpdate = async (itemId, body) =>
  (await apiClient.post(`/applications/mirror/items/${encodeURIComponent(itemId)}/updates`, { body })).data;
export const mirrorUploadFile = async (itemId, file) => {
  const fd = new FormData();
  fd.append("file", file);
  return (await apiClient.post(`/applications/mirror/items/${encodeURIComponent(itemId)}/upload-file`, fd, {
    headers: { "Content-Type": "multipart/form-data" },
    timeout: 180000,
  })).data;
};
export const mirrorInfoRequest = async (itemId, text) =>
  (await apiClient.post(`/applications/mirror/items/${encodeURIComponent(itemId)}/info-request`, { text })).data;
export const mirrorSendEmail = async (itemId, payload) =>
  (await apiClient.post(`/applications/mirror/items/${encodeURIComponent(itemId)}/send-email`, payload)).data;
export const mirrorRenameItem = async (itemId, name) =>
  (await apiClient.put(`/applications/mirror/items/${encodeURIComponent(itemId)}/name`, { name })).data;
export const mirrorItemActivity = async (itemId) =>
  (await apiClient.get(`/applications/mirror/items/${encodeURIComponent(itemId)}/activity`)).data;
export const mirrorFileViewUrl = (fileUrl, name, tok) =>
  `${API}/applications/mirror/files/view?src=${encodeURIComponent(fileUrl)}&name=${encodeURIComponent(name || "file")}&tok=${encodeURIComponent(tok || "")}`;
export const uploadSoEmailAttachment = async (appId, file) => {
  const fd = new FormData();
  fd.append("file", file);
  return (await apiClient.post(`/applications/${appId}/standing-order/email-attachments`, fd, {
    headers: { "Content-Type": "multipart/form-data" },
    timeout: 120000,
  })).data;
};
export const listSoEmailAttachments = async (appId) =>
  (await apiClient.get(`/applications/${appId}/standing-order/email-attachments`)).data;
export const deleteSoEmailAttachment = async (appId, attId) =>
  (await apiClient.delete(`/applications/${appId}/standing-order/email-attachments/${attId}`)).data;
export const mirrorChase = async (itemId, kind = "ir", reason = null) =>
  (await apiClient.post(`/applications/mirror/items/${encodeURIComponent(itemId)}/chase?kind=${kind}`, reason ? { reason } : {})).data;
export const mirrorBrokerSearch = async (q) =>
  (await apiClient.get(`/applications/mirror/brokers`, { params: { q } })).data;
export const mirrorSetBroker = async (itemId, brokerItemId, brokerName) =>
  (await apiClient.post(`/applications/mirror/items/${encodeURIComponent(itemId)}/broker`, { broker_item_id: brokerItemId, broker_name: brokerName })).data;
export const listUnmatchedFiles        = async () => (await apiClient.get("/legal/cases/files/unmatched")).data;
export const assignFileToCase          = async (fileId, caseId) =>
  (await apiClient.post(`/legal/cases/files/${encodeURIComponent(fileId)}/assign`, { case_id: caseId })).data;
export const deleteLegalCaseFile       = async (fileId) =>
  (await apiClient.delete(`/legal/cases/files/${encodeURIComponent(fileId)}`)).data;
export const renameLegalCaseFile       = async (fileId, name, actor, caseIds = []) =>
  (await apiClient.patch(`/legal/cases/files/${encodeURIComponent(fileId)}/rename`, { name, actor, case_ids: caseIds })).data;
export const getLegalCaseFilesStats    = async () => (await apiClient.get("/legal/cases/files/stats")).data;

// ==================== LEGAL DOCUMENT GENERATION (DocuPilot) ====================
export const getLegalDocPrefill = async (caseId, kind) =>
  (await apiClient.get(`/legal/docs/${encodeURIComponent(caseId)}/prefill`, { params: { kind } })).data;
export const generateLegalDocs  = async (caseId, payload) =>
  (await apiClient.post(`/legal/docs/${encodeURIComponent(caseId)}/generate`, payload)).data;
export const discoverLegalGuarantors = async (caseId) =>
  (await apiClient.post(`/legal/docs/${encodeURIComponent(caseId)}/guarantors/discover`, {}, { timeout: 150000 })).data;
export const setLegalChosenCourt = async (caseId, name, address) =>
  (await apiClient.put(`/legal/docs/${encodeURIComponent(caseId)}/court`, { name, address })).data;
export const setLegalCourtCaseNumber = async (caseId, value) =>
  (await apiClient.put(`/legal/docs/${encodeURIComponent(caseId)}/court-case-number`, { value })).data;
// Elle (9bb64edd60): free-text "Case No. & Court" per account, searchable on the tracker
export const setLegalCourtReference = async (caseId, value, actor) =>
  (await apiClient.put(`/legal/docs/${encodeURIComponent(caseId)}/court-reference`, { value, actor })).data;
export const aiFillLegalDoc = async (caseId, payload) =>
  (await apiClient.post(`/legal/docs/${encodeURIComponent(caseId)}/ai-fill`, payload, { timeout: 180000 })).data;
// Payments Rescheduling Deed → DocuSign (draft envelope + sender view), envelope status, fresh sender view
export const deedToDocuSign = async (caseId, payload) =>
  (await apiClient.post(`/legal/docs/${encodeURIComponent(caseId)}/docusign`, payload, { timeout: 180000 })).data;
export const getDeedEnvelopes = async (caseId, refresh = false) =>
  (await apiClient.get(`/legal/docs/${encodeURIComponent(caseId)}/docusign`, { params: refresh ? { refresh: true } : {}, timeout: 120000 })).data;
export const resumeDeedEnvelope = async (caseId, envelopeId) =>
  (await apiClient.post(`/legal/docs/${encodeURIComponent(caseId)}/docusign/${encodeURIComponent(envelopeId)}/resume`)).data;
export const discardDeedEnvelope = async (caseId, envelopeId, actor, reason) =>
  (await apiClient.post(`/legal/docs/${encodeURIComponent(caseId)}/docusign/${encodeURIComponent(envelopeId)}/discard`, { actor, reason })).data;
export const searchCompaniesForDocs = async (q) =>
  (await apiClient.get("/legal/docs/company-search", { params: { q } })).data;
export const getCompanyProfileForDocs = async (number) =>
  (await apiClient.get("/legal/docs/company-profile", { params: { number } })).data;
export const getLegalDocAddressBook = async (caseId) =>
  (await apiClient.get(`/legal/docs/${encodeURIComponent(caseId)}/address-book`)).data;
export const fetchPrefundingAircallRecording = async (appId, actor) =>
  (await apiClient.post(`/applications/${appId}/prefunding-call/fetch-aircall-recording`, { phone: "", actor })).data;
// Further calls on the pre-funding call (Paul SF-32669, 12 Oct 2026)
export const listPrefundingExtraRecordings = async (appId) =>
  (await apiClient.get(`/applications/${appId}/prefunding-call/extra-recordings`)).data;
export const uploadPrefundingExtraRecording = async (appId, file, note, uploadedBy) => {
  const fd = new FormData();
  fd.append("file", file);
  fd.append("note", note || "");
  return (await apiClient.post(`/applications/${appId}/prefunding-call/extra-recordings?uploaded_by=${encodeURIComponent(uploadedBy || "user")}`, fd,
    { headers: { "Content-Type": "multipart/form-data" } })).data;
};
export const listPrefundingAircallCalls = async (appId, { days = 14, phone = "" } = {}) =>
  (await apiClient.get(`/applications/${appId}/prefunding-call/aircall-calls`, { params: { days, ...(phone ? { phone } : {}) } })).data;
export const attachPrefundingExtraFromAircall = async (appId, callId, note, actor) =>
  (await apiClient.post(`/applications/${appId}/prefunding-call/extra-recordings/from-aircall`, { call_id: String(callId), note: note || "", actor })).data;
export const deletePrefundingExtraRecording = async (appId, recId, actor) =>
  (await apiClient.delete(`/applications/${appId}/prefunding-call/extra-recordings/${recId}`, { params: { actor } })).data;
export const prefundingExtraRecordingUrl = (appId, recId) =>
  `${API}/applications/${appId}/prefunding-call/extra-recordings/${recId}/content`;
export const setPortalBankAccountExclusions = async (requisitionId, excluded_account_ids) =>
  (await apiClient.patch(`/portal-bank-feed/connections/${requisitionId}/accounts`, { excluded_account_ids })).data;
export const postcodeAutocomplete = async (term) =>
  (await apiClient.get("/legal/docs/postcode-lookup", { params: { term } })).data;
export const postcodeResolve = async (id) =>
  (await apiClient.get(`/legal/docs/postcode-lookup/${encodeURIComponent(id)}`)).data;
export const postcodeAddresses = async (postcode) =>
  (await apiClient.get("/legal/docs/postcode-addresses", { params: { postcode } })).data;
export const auPostcodeSuburbs = async (postcode) =>
  (await apiClient.get("/aus/postcode-suburbs", { params: { postcode } })).data;
export const getPostworksConfig = async () => (await apiClient.get("/postworks/status")).data;
export const getPostworksItems = async (caseId) =>
  (await apiClient.get(`/postworks/cases/${encodeURIComponent(caseId)}/items`)).data;
export const sendPostworks = async (caseId, payload) =>
  (await apiClient.post(`/postworks/cases/${encodeURIComponent(caseId)}/send`, payload, { timeout: 300000 })).data;
export const deletePostworksItem = async (itemId) =>
  (await apiClient.delete(`/postworks/items/${encodeURIComponent(itemId)}`)).data;
export const refreshPostworksItem = async (itemId) =>
  (await apiClient.post(`/postworks/items/${encodeURIComponent(itemId)}/refresh`)).data;
export const getPackFiles = async (caseId) =>
  (await apiClient.get(`/legal/docs/${encodeURIComponent(caseId)}/pack-files`)).data;
export const previewPackFile = async (caseId, key) =>
  (await apiClient.get(`/legal/docs/${encodeURIComponent(caseId)}/pack-files/preview`,
    { params: { key }, responseType: "blob" })).data;
export const uploadPackFile = async (caseId, file) => {
  const fd = new FormData();
  fd.append("file", file);
  return (await apiClient.post(`/legal/docs/${encodeURIComponent(caseId)}/pack-upload`, fd,
    { headers: { "Content-Type": "multipart/form-data" } })).data;
};
export const sendServicePack = async (caseId, payload) =>
  (await apiClient.post(`/legal/docs/${encodeURIComponent(caseId)}/send-service-pack`, payload)).data;
export const listProcessServers  = async () => (await apiClient.get("/legal/docs/process-servers")).data;
export const addProcessServer    = async (payload) => (await apiClient.post("/legal/docs/process-servers", payload)).data;
export const deleteProcessServer = async (id) => (await apiClient.delete(`/legal/docs/process-servers/${id}`)).data;

export default apiClient;

// ─── Applications Daily Report ──────────────────────────────────────────────
export const getAppDailyReport = async (date) =>
  (await apiClient.get("/applications/inbox/daily-report", { params: date ? { date } : {} })).data;
export const postAppDailyReport = async (payload) =>
  (await apiClient.post("/applications/inbox/daily-report/post", payload)).data;
export const setAppDailyReportSettings = async (payload) =>
  (await apiClient.put("/applications/inbox/daily-report/settings", payload)).data;

export const mirrorBulkStatus = async (groupId, label, actor) =>
  (await apiClient.post(`/applications/mirror/groups/${groupId}/bulk-status`, { label, actor })).data;
export const mirrorBulkItemsStatus = async (itemIds, label, actor) =>
  (await apiClient.post("/applications/mirror/items/bulk-status", { item_ids: itemIds, label, actor })).data;
export const mirrorIrSweepPreview = async (overdueBusinessDays = 2) =>
  (await apiClient.get("/applications/mirror/groups/ir/sweep-preview", { params: { overdue_business_days: overdueBusinessDays } })).data;
export const mirrorIrSweep = async (itemIds, actor, overdueBusinessDays = 2) =>
  (await apiClient.post("/applications/mirror/groups/ir/sweep", { item_ids: itemIds, actor, overdue_business_days: overdueBusinessDays })).data;
export const mirrorBulkAssign = async (itemIds, assignee) =>
  (await apiClient.post("/applications/mirror/items/bulk-assign", { item_ids: itemIds, assignee })).data;
export const contractsDocInline = async (url) =>
  (await apiClient.get("/contracts/doc-inline", { params: { url }, responseType: "blob" })).data;
// Sales ask (Sep 2026): editable Word copy of a generated PDF
export const contractsDocAsWord = async (url, name) =>
  (await apiClient.get("/contracts/doc-as-word", { params: { url, name }, responseType: "blob", timeout: 120000 })).data;

// ─── Portal Bank Feed (native GoCardless Open Banking, replaces Make.com) ─
export const listPortalBankConnections = async (region = null) =>
  (await apiClient.get("/portal-bank-feed/connections", { params: region ? { region } : {} })).data;
export const reconnectPortalBank = async (institution_id, redirect_url = null) =>
  (await apiClient.post("/portal-bank-feed/connections/reconnect", { institution_id, redirect_url })).data;
export const discoverPortalBankConnections = async () =>
  (await apiClient.post("/portal-bank-feed/connections/discover", {})).data;
export const refreshPortalBankStatus = async (requisition_id) =>
  (await apiClient.post("/portal-bank-feed/connections/refresh", { requisition_id })).data;
export const deletePortalBankConnection = async (requisition_id) =>
  (await apiClient.delete(`/portal-bank-feed/connections/${requisition_id}`)).data;
export const syncPortalBankNow = async (date_from = null, date_to = null, region = null) =>
  (await apiClient.post("/portal-bank-feed/sync-now", { date_from, date_to, region })).data;
export const listPortalBankSyncRuns = async (limit = 5, region = null) =>
  (await apiClient.get("/portal-bank-feed/sync-runs", { params: { limit, ...(region ? { region } : {}) } })).data;
export const getPortalBankSettings = async () =>
  (await apiClient.get("/portal-bank-feed/settings")).data;

// ─── Loan Repayments — Undo (deletes exactly what the page posted) ──
export const previewRollback = async (date = null) =>
  (await apiClient.get("/collections/payments-wizard/rollback-preview", {
    params: date ? { date } : {},
  })).data;
export const startRollback = async (date = null, actioning_user = null) =>
  (await apiClient.post("/collections/payments-wizard/rollback", { date, actioning_user })).data;
export const getRollbackStatus = async (run_id) =>
  (await apiClient.get(`/collections/payments-wizard/rollback-status/${run_id}`)).data;

export const setPortalBankSettings = async (patch) =>
  (await apiClient.put("/portal-bank-feed/settings", patch)).data;



// ── AUS Credit Approved Deals (Phase 2) ─────────────────────────────────────
export const ausDealsList = async (force = false) =>
  (await apiClient.get("/aus/deals", { params: force ? { force: true } : {} })).data;
export const ausDealsStart = async (item_id) =>
  (await apiClient.post("/aus/deals/start", { item_id })).data;
export const ausDealGet = async (appId) =>
  (await apiClient.get(`/aus/deals/app/${appId}`)).data;
export const ausDealRefreshMonday = async (appId) =>
  (await apiClient.post(`/aus/deals/app/${appId}/refresh-monday`)).data;
export const ausDealOnboarding = async (appId) =>
  (await apiClient.get(`/aus/deals/app/${appId}/onboarding`)).data;
export const ausDealConfirmStep = async (appId, step, confirmed, note) =>
  (await apiClient.post(`/aus/deals/app/${appId}/confirm-step`, { step, confirmed, note })).data;
export const ausDealBank = async (appId, payload) =>
  (await apiClient.put(`/aus/deals/app/${appId}/bank`, payload)).data;
export const ausDealLoanPull = async (appId) =>
  (await apiClient.post(`/aus/deals/app/${appId}/loandisk-pull`)).data;
export const ausDealPrefunding = async (appId, payload) =>
  (await apiClient.post(`/aus/deals/app/${appId}/prefunding`, payload)).data;
export const ausDealNominal = async (appId, payload) =>
  (await apiClient.post(`/aus/deals/app/${appId}/nominal`, payload)).data;
export const ausDealNominalCapay = async (appId) =>
  (await apiClient.post(`/aus/deals/app/${appId}/nominal/capay`)).data;
export const ausDealDisbRequest = async (appId, payload) =>
  (await apiClient.post(`/aus/deals/app/${appId}/disbursement/request`, payload)).data;
export const ausDealDisbApprove = async (appId, password) =>
  (await apiClient.post(`/aus/deals/app/${appId}/disbursement/approve`, { password })).data;
export const ausDealDisbReject = async (appId, password, reason) =>
  (await apiClient.post(`/aus/deals/app/${appId}/disbursement/reject`, { password, reason })).data;
export const ausDealCapayExecute = async (appId) =>
  (await apiClient.post(`/aus/deals/app/${appId}/disbursement/capay-execute`)).data;
export const ausDealPaidManual = async (appId, reference) =>
  (await apiClient.post(`/aus/deals/app/${appId}/disbursement/paid-manual`, { reference })).data;
export const ausDealMarkFunded = async (appId) =>
  (await apiClient.post(`/aus/deals/app/${appId}/mark-funded`)).data;
export const ausDealChangeStatus = async (itemId, label) =>
  (await apiClient.post(`/aus/deals/item/${itemId}/status`, { label })).data;

// ── Portfolio Audit (loan-family dataset, Phase 1 LoanDisk) ─────────
export const paStatus = async () => (await apiClient.get('/portfolio-audit/status')).data;
export const paStartSync = async (region) => (await apiClient.post(`/portfolio-audit/sync/${region}`)).data;
export const paCancelSync = async (region) => (await apiClient.post(`/portfolio-audit/sync/${region}/cancel`)).data;
export const paOverview = async (region) => (await apiClient.get('/portfolio-audit/overview', { params: { region } })).data;
export const paVintages = async (region, matured_only) => (await apiClient.get('/portfolio-audit/vintages', { params: { region, matured_only } })).data;
export const paSegments = async (region, matured_only, dr_all_years = false) => (await apiClient.get('/portfolio-audit/segments', { params: { region, matured_only, dr_all_years } })).data;
export const paReportsReject = async (region, base_sf, item_id, rejected) => (await apiClient.post('/portfolio-audit/reports/reject', null, { params: { region, base_sf, item_id, rejected } })).data;
export const paLoans = async (params) => (await apiClient.get('/portfolio-audit/loans', { params })).data;
export const paLoanDetail = async (region, baseSf) => (await apiClient.get(`/portfolio-audit/loans/${encodeURIComponent(baseSf)}`, { params: { region } })).data;
export const paDataQuality = async (region) => (await apiClient.get('/portfolio-audit/data-quality', { params: { region } })).data;
export const paDefinitions = async () => (await apiClient.get('/portfolio-audit/definitions')).data;
export const paReconcile = async (region, as_of) => (await apiClient.get('/portfolio-audit/reconcile', { params: { region, as_of } })).data;
export const paAcceptanceTest = async () => (await apiClient.get('/portfolio-audit/reports/acceptance-test')).data;
export const paReportsRun = async (region, refs) => (await apiClient.post(`/portfolio-audit/reports/run/${region}`, null, { params: refs ? { refs } : {} })).data;
export const paReportsCancel = async (region) => (await apiClient.post(`/portfolio-audit/reports/run/${region}/cancel`)).data;
export const paNotesRun = async (region, refs) => (await apiClient.post(`/portfolio-audit/notes/run/${region}`, null, { params: refs ? { refs } : {} })).data;
export const paNotesCancel = async (region) => (await apiClient.post(`/portfolio-audit/notes/run/${region}/cancel`)).data;
export const paNotesAcceptance = async (version) => (await apiClient.get('/portfolio-audit/notes/acceptance-test', { params: { version } })).data;
export const paChRun = async (region, refs) => (await apiClient.post(`/portfolio-audit/ch/run/${region}`, null, { params: refs ? { refs } : {} })).data;
export const paChCancel = async (region) => (await apiClient.post(`/portfolio-audit/ch/run/${region}/cancel`)).data;
export const paChReset = async (region) => (await apiClient.post(`/portfolio-audit/ch/run/${region}/reset`)).data;
export const paChIndustryAiRun = async () => (await apiClient.post('/portfolio-audit/ch/industry-ai/run')).data;
export const paChIndustryAiStatus = async () => (await apiClient.get('/portfolio-audit/ch/industry-ai/status')).data;
export const paChAcceptance = async () => (await apiClient.get('/portfolio-audit/ch/acceptance-test')).data;
export const paReportsReview = async (region, refs) => (await apiClient.get('/portfolio-audit/reports/review', { params: { region, refs } })).data;
export const paExportLoans = async (params) =>
  (await apiClient.get('/portfolio-audit/loans-export', { params, responseType: 'blob', timeout: 120000 })).data;
export const paRefreshAll = async (region) => (await apiClient.post(`/portfolio-audit/refresh-all/${region}`)).data;
export const paSnapshotNow = async (region) => (await apiClient.post(`/portfolio-audit/snapshot/${region}`)).data;
export const paSnapshots = async (region) => (await apiClient.get('/portfolio-audit/snapshots', { params: region ? { region } : {} })).data;

// "As at" — when set, every Portfolio Analysis read is served from the frozen snapshot taken on that date.
let paAsOf = null;
export const setPaAsOf = (v) => { paAsOf = v || null; };
export const getPaAsOf = () => paAsOf;
apiClient.interceptors.request.use((config) => {
  if (paAsOf && (config.method || 'get') === 'get' && /\/portfolio-audit\/(overview|vintages|segments|loans|loans-export|loans\/)/.test(config.url || '')) {
    config.params = { ...(config.params || {}), as_of: paAsOf };
  }
  return config;
});

// ── Portfolio Analysis (whole-of-book) ──────────────────────────────
export const portfolioStatus = async () =>
  (await apiClient.get('/portfolio/status')).data;
export const startPortfolioSync = async () =>
  (await apiClient.post('/portfolio/sync/loandisk')).data;
export const startPortfolioEnrich = async (force = false) =>
  (await apiClient.post('/portfolio/enrich/companies-house', null, { params: { force } })).data;
export const cancelPortfolioEnrich = async () =>
  (await apiClient.post('/portfolio/enrich/companies-house/cancel')).data;
export const getPortfolioSummary = async () =>
  (await apiClient.get('/portfolio/summary')).data;
export const listPortfolioCompanies = async (params = {}) =>
  (await apiClient.get('/portfolio/companies', { params })).data;
export const getPortfolioCompany = async (key) =>
  (await apiClient.get(`/portfolio/companies/${encodeURIComponent(key)}`)).data;

// ─── AUS Loan Repayments (GoCardless AU payments → LoanDisk AU) ───────────
const _pwAus = "/collections/payments-wizard-aus";
export const loadPaymentsWizardAus = async (date = null, dateTo = null, inFlightAll = false, basis = "charge") =>
  (await apiClient.get(_pwAus, { params: { ...(date ? { date } : {}), ...(dateTo ? { date_to: dateTo } : {}), ...(inFlightAll ? { in_flight_all: true } : {}), basis } })).data;
export const syncPaymentsWizardAus = async (source = "all") => (await apiClient.post(`${_pwAus}/sync`, null, { params: { source } })).data;
export const syncStatusPaymentsWizardAus = async () => (await apiClient.get(`${_pwAus}/sync-status`)).data;
const _ageing = "/collections/ageing";
export const getAsicAbrProfile = async (caseId, refresh = false) => (await apiClient.get(`${_ageing}/cases/${caseId}/asic-abr`, { params: refresh ? { refresh: true } : {} })).data;
export const linkAsicAbr = async (caseId, payload) => (await apiClient.post(`${_ageing}/cases/${caseId}/asic-abr/link`, payload)).data;
export const unlinkAsicAbr = async (caseId) => (await apiClient.delete(`${_ageing}/cases/${caseId}/asic-abr/link`)).data;
export const searchAsicAbr = async (caseId, q) => (await apiClient.get(`${_ageing}/cases/${caseId}/asic-abr/search`, { params: { q } })).data;
// Portfolio Accounts — work any live loan in the Customer File
export const getPortfolioAccounts = async () => (await apiClient.get(`${_ageing}/portfolio-accounts`)).data;
export const addCaseToPortfolio = async (caseId) => (await apiClient.post(`${_ageing}/cases/${encodeURIComponent(caseId)}/portfolio`)).data;
export const removeCaseFromPortfolio = async (caseId) => (await apiClient.delete(`${_ageing}/cases/${encodeURIComponent(caseId)}/portfolio`)).data;
export const addPortfolioFromLoanDisk = async (payload) => (await apiClient.post(`${_ageing}/portfolio-accounts/from-loandisk`, payload)).data;
export const searchLoanDiskLoans = async ({ borrower_name, application_id, region }) =>
  (await apiClient.get(`/loandisk/search-loans`, { params: { borrower_name, application_id, region } })).data;
export const searchPortfolioLoans = async (q) => (await apiClient.get(`${_ageing}/portfolio-accounts/search`, { params: { q } })).data;
// CreditorWatch (AUS credit bureau) — score snapshot, monitoring, alerts
const _cw = (caseId) => `${_ageing}/cases/${encodeURIComponent(caseId)}/creditorwatch`;
export const getCaseCreditorWatch = async (caseId, auto = true) => (await apiClient.get(_cw(caseId), { params: { auto } })).data;
export const refreshCaseCreditorWatch = async (caseId) => (await apiClient.post(`${_cw(caseId)}/refresh`)).data;
export const watchCaseCreditorWatch = async (caseId) => (await apiClient.post(`${_cw(caseId)}/watch`)).data;
export const unwatchCaseCreditorWatch = async (caseId) => (await apiClient.delete(`${_cw(caseId)}/watch`)).data;
export const pullCaseCreditorWatchReport = async (caseId) => (await apiClient.post(`${_cw(caseId)}/report`)).data;

// ── GoCardless tab (AUS Customer File) ──
const _gc = (caseId) => `${_ageing}/cases/${caseId}/gocardless`;
export const getCaseGoCardless = async (caseId, mandateId) => (await apiClient.get(_gc(caseId), { params: mandateId ? { mandate_id: mandateId } : {} })).data;
export const linkCaseMandate = async (caseId, mandate_id) => (await apiClient.post(`${_gc(caseId)}/link`, { mandate_id })).data;
export const unlinkCaseMandate = async (caseId) => (await apiClient.delete(`${_gc(caseId)}/link`)).data;
export const searchCaseMandates = async (caseId, q) => (await apiClient.get(`${_gc(caseId)}/mandate-search`, { params: { q } })).data;
export const gcRetryPayment = async (caseId, paymentId, charge_date) => (await apiClient.post(`${_gc(caseId)}/payments/${paymentId}/retry`, { charge_date: charge_date || null })).data;
export const gcCancelPayment = async (caseId, paymentId) => (await apiClient.post(`${_gc(caseId)}/payments/${paymentId}/cancel`, {})).data;
export const gcChangePayment = async (caseId, paymentId, payload) => (await apiClient.post(`${_gc(caseId)}/payments/${paymentId}/change`, payload)).data;
export const gcSubscriptionAction = async (caseId, subId, action, payload = {}) => (await apiClient.post(`${_gc(caseId)}/subscriptions/${subId}/${action}`, payload)).data;
export const gcRevisePreview = async (caseId, payload) => (await apiClient.post(`${_gc(caseId)}/revise-schedule/preview`, payload)).data;
export const gcReviseSchedule = async (caseId, payload) => (await apiClient.post(`${_gc(caseId)}/revise-schedule`, payload)).data;
export const gcUndoRevision = async (caseId, revisionId) => (await apiClient.post(`${_gc(caseId)}/revisions/${revisionId}/undo`, {})).data;
export const gcCancelInstalmentSchedule = async (caseId, scheduleId) => (await apiClient.post(`${_gc(caseId)}/instalment-schedules/${scheduleId}/cancel`, {})).data;
// "Create payment schedule" wizard — additional collections on the live mandate (Dean, 9 Oct 2026)
export const gcScheduleOptions = async (caseId) => (await apiClient.get(`${_gc(caseId)}/schedule/options`)).data;
export const gcSchedulePreview = async (caseId, payload) => (await apiClient.post(`${_gc(caseId)}/schedule/preview`, payload)).data;
export const gcScheduleCreate = async (caseId, payload) => (await apiClient.post(`${_gc(caseId)}/schedule`, payload)).data;
export const gcScheduleCancel = async (caseId, scheduleId) => (await apiClient.post(`${_gc(caseId)}/schedule/${scheduleId}/cancel`, {})).data;
export const gcScheduleEmailPreview = async (caseId, scheduleId) => (await apiClient.get(`${_gc(caseId)}/schedule/${scheduleId}/email-preview`)).data;
export const gcScheduleSendEmail = async (caseId, scheduleId, payload) => (await apiClient.post(`${_gc(caseId)}/schedule/${scheduleId}/send-email`, payload)).data;
// "Create new mandate" wizard — replacement PayTo / BECS agreement from the customer file (Dean, 3 Nov 2026)
export const gcNewMandatePrefill = async (caseId) => (await apiClient.get(`${_gc(caseId)}/new-mandate/prefill`)).data;
export const gcNewMandateCreate = async (caseId, payload) => (await apiClient.post(`${_gc(caseId)}/new-mandate`, payload)).data;
export const gcNewMandateSend = async (caseId, brId, payload) => (await apiClient.post(`${_gc(caseId)}/new-mandate/${brId}/send`, payload)).data;
export const gcNewMandateCancel = async (caseId, brId) => (await apiClient.post(`${_gc(caseId)}/new-mandate/${brId}/cancel`, {})).data;
export const gcNewMandateStatus = async (caseId) => (await apiClient.get(`${_gc(caseId)}/new-mandate`)).data;
export const loadAusLateFailures = async () => (await apiClient.get(`${_pwAus}/late-failures`)).data;
export const reverseAusLateFailure = async (paymentId, payload) => (await apiClient.post(`${_pwAus}/late-failures/${paymentId}/reverse`, payload)).data;
export const ackAusLateFailure = async (paymentId, payload) => (await apiClient.post(`${_pwAus}/late-failures/${paymentId}/acknowledge`, payload)).data;
export const loadAusRepaymentPerformance = async (basis = "charge") => (await apiClient.get(`${_pwAus}/performance`, { params: { basis } })).data;
export const loadAusRepaymentSettings = async () => (await apiClient.get(`${_pwAus}/settings`)).data;
export const loadPaytoOverCap = async (refresh = false) => (await apiClient.get(`/aus/gocardless/payto-over-cap`, { params: refresh ? { refresh: true } : {}, timeout: 180000 })).data;
export const saveAusRepaymentSettings = async (payload) => (await apiClient.put(`${_pwAus}/settings`, payload)).data;
export const loadAusDueReconciliation = async (date = null, dateTo = null) =>
  (await apiClient.get(`${_pwAus}/due-reconciliation`, { params: { ...(date ? { date } : {}), ...(dateTo ? { date_to: dateTo } : {}) } })).data;
export const loadAusPayouts = async (date = null, dateTo = null) =>
  (await apiClient.get(`${_pwAus}/payouts`, { params: { ...(date ? { date } : {}), ...(dateTo ? { date_to: dateTo } : {}) } })).data;
export const tickAusPayout = async (payoutId, payload) => (await apiClient.post(`${_pwAus}/payouts/${payoutId}/tick`, payload)).data;
export const checkLoandiskAusPayments = async (payload = {}) => (await apiClient.post(`${_pwAus}/check-loandisk`, payload)).data;
export const ausWizardBorrowerSearch = async (q) =>
  (await apiClient.get(`${_pwAus}/borrower-search`, { params: { q } })).data;
export const matchAusPayment = async (paymentId, payload) =>
  (await apiClient.post(`${_pwAus}/${encodeURIComponent(paymentId)}/match`, payload)).data;
export const unmatchAusPayment = async (paymentId) =>
  (await apiClient.post(`${_pwAus}/${encodeURIComponent(paymentId)}/unmatch`)).data;
export const dismissAusPayment = async (paymentId, payload) =>
  (await apiClient.post(`${_pwAus}/${encodeURIComponent(paymentId)}/dismiss`, payload)).data;
export const undismissAusPayment = async (paymentId) =>
  (await apiClient.post(`${_pwAus}/${encodeURIComponent(paymentId)}/undismiss`)).data;
export const postAllAusPayments = async (payload = {}) => (await apiClient.post(`${_pwAus}/post-all`, payload)).data;
export const getAusPostAllStatus = async (runId) => (await apiClient.get(`${_pwAus}/post-all-status/${runId}`)).data;
export const postAnywayAusPayment = async (paymentId, payload = {}) =>
  (await apiClient.post(`${_pwAus}/${encodeURIComponent(paymentId)}/post-anyway`, payload)).data;
export const previewAusRollback = async (date = null, dateTo = null) =>
  (await apiClient.get(`${_pwAus}/rollback-preview`, { params: { ...(date ? { date } : {}), ...(dateTo ? { date_to: dateTo } : {}) } })).data;
export const startAusRollback = async (date = null, actioning_user = null, dateTo = null) =>
  (await apiClient.post(`${_pwAus}/rollback`, { date, date_to: dateTo, actioning_user })).data;
export const getAusRollbackStatus = async (runId) => (await apiClient.get(`${_pwAus}/rollback-status/${runId}`)).data;

// ---- Capay customer Virtual Accounts (AUS repayment accounts) ----
const _capayCust = "/capay/customers";
export const getCapayVaSettings = async () => (await apiClient.get(`${_capayCust}/settings`)).data;
export const updateCapayVaSettings = async (patch) => (await apiClient.put(`${_capayCust}/settings`, patch)).data;
export const listCapayAccounts = async () => (await apiClient.get(_capayCust)).data;
export const importCapayAccounts = async (requested_by) => (await apiClient.post(`${_capayCust}/import-from-capay`, { requested_by })).data;
export const previewCapayAccount = async (payload) => (await apiClient.post(`${_capayCust}/preview`, payload)).data;
export const ensureCapayAccount = async (payload) => (await apiClient.post(`${_capayCust}/ensure`, payload)).data;
export const getCapayAccountBySf = async (sf) => (await apiClient.get(`${_capayCust}/by-sf/${encodeURIComponent(sf)}`)).data;
export const getCapayAccountByCase = async (caseId) => (await apiClient.get(`${_capayCust}/by-case/${caseId}`)).data;
export const getSettlementOptions = async ({ sf_number, case_id } = {}) => (await apiClient.get(`${_capayCust}/settlement-options`, { params: { sf_number, case_id } })).data;
export const disableLegacyCapayAccounts = async (requested_by) => (await apiClient.post(`${_capayCust}/disable-legacy`, { requested_by })).data;
export const refreshCapayAccount = async (sf) => (await apiClient.post(`${_capayCust}/by-sf/${encodeURIComponent(sf)}/refresh`)).data;
export const setCapayAccountPayId = async (sf, payid_email) => (await apiClient.post(`${_capayCust}/by-sf/${encodeURIComponent(sf)}/payid`, { payid_email })).data;
export const capayAccountPdfUrl = (sf) => `${apiClient.defaults.baseURL}${_capayCust}/by-sf/${encodeURIComponent(sf)}/pdf`;
// Routes are authenticated now (live Capay) — fetch the PDF with the bearer token and open it as a blob
export const openCapayAccountPdf = async (sf) => {
  const blob = (await apiClient.get(`${_capayCust}/by-sf/${encodeURIComponent(sf)}/pdf`, { responseType: "blob", timeout: 60000 })).data;
  const url = URL.createObjectURL(blob);
  window.open(url, "_blank", "noopener");
  setTimeout(() => URL.revokeObjectURL(url), 60000);
};
export const getCapayBackfillCandidates = async (refreshLoandisk = false) => (await apiClient.get(`${_capayCust}/backfill/candidates`, { params: refreshLoandisk ? { refresh_loandisk: true } : {} })).data;
export const runCapayBackfill = async (rows, requested_by) => (await apiClient.post(`${_capayCust}/backfill/run`, { rows, requested_by })).data;
export const getCapayBackfillStatus = async () => (await apiClient.get(`${_capayCust}/backfill/status`)).data;
export const resolveCapayBackfill = async () => (await apiClient.post(`${_capayCust}/backfill/resolve`)).data;
export const getCapayAbnPrefill = async (abn) => (await apiClient.get(`${_capayCust}/abn-prefill/${encodeURIComponent(abn)}`)).data;
export const searchCapayAccounts = async (q) => (await apiClient.get(`${_capayCust}/search`, { params: { q } })).data;
export const linkCapayAccount = async (sf, target_sf, requested_by) => (await apiClient.post(`${_capayCust}/by-sf/${encodeURIComponent(sf)}/link`, { target_sf, requested_by })).data;
export const previewLegacyShells      = async (region = "UK") => (await apiClient.get(`/legal/cases/files/unmatched/shell-preview?region=${region}`)).data;
export const createLegacyShells       = async (region = "UK") => (await apiClient.post(`/legal/cases/files/unmatched/create-shells?region=${region}`)).data;
export const fileAusSignedPacks       = async (days = 365) => (await apiClient.post(`/legal/docs/aus/file-signed-packs?days=${days}`)).data;
export const closeCapayAccount        = async (sf, requested_by, reason = "") => (await apiClient.post(`${_capayCust}/by-sf/${encodeURIComponent(sf)}/close`, { requested_by, reason })).data;
export const lookupCapayCustomers     = async (q) => (await apiClient.get(`${_capayCust}/lookup`, { params: { q } })).data;
export const getPayoutPrefill      = async (caseId) => (await apiClient.get(`/payout/${encodeURIComponent(caseId)}/prefill`)).data;
export const previewPayoutLetter   = async (caseId, body) => (await apiClient.post(`/payout/${encodeURIComponent(caseId)}/preview`, body)).data;
export const approvePayoutLetter   = async (caseId, payout_id, note = "") => (await apiClient.post(`/payout/${encodeURIComponent(caseId)}/approve`, { payout_id, note })).data;
export const sendPayoutLetter      = async (caseId, body) => (await apiClient.post(`/payout/${encodeURIComponent(caseId)}/send`, body)).data;
export const getPayoutSettings     = async () => (await apiClient.get(`/payout/settings`)).data;
export const updatePayoutSettings  = async (body) => (await apiClient.put(`/payout/settings`, body)).data;

// ==================== POLICY CENTRE ====================
// Preview-only: the dev-bypass user can act as a second person (two-person approval rule).
let _policyDevActor = "";
export const setPolicyDevActor = (email) => { _policyDevActor = (email || "").trim(); };
apiClient.interceptors.request.use((config) => {
  if (_policyDevActor && (config.url || "").startsWith("/policy")) config.headers["X-Dev-Actor"] = _policyDevActor;
  return config;
});
export const getPolicySchema        = async () => (await apiClient.get('/policy/schema')).data;
export const getPolicyLive          = async () => (await apiClient.get('/policy/live')).data;
export const getPolicyVersions      = async (state) => (await apiClient.get('/policy/versions', { params: state ? { state } : {} })).data;
export const getPolicyVersion       = async (id) => (await apiClient.get(`/policy/versions/${id}`)).data;
export const getPolicyVersionDiff   = async (id, against = 'live') => (await apiClient.get(`/policy/versions/${id}/diff`, { params: { against } })).data;
export const getPolicyChangelog     = async (params = {}) => (await apiClient.get('/policy/changelog', { params })).data;
export const downloadPolicyChangelog = async (params = {}) => (await apiClient.get('/policy/changelog', { params: { ...params, format: 'csv' }, responseType: 'blob' })).data;
export const getPolicyDrafts        = async () => (await apiClient.get('/policy/drafts')).data;
export const createPolicyDraft      = async (body = {}) => (await apiClient.post('/policy/drafts', body)).data;
export const patchPolicyDraft       = async (id, changes) => (await apiClient.patch(`/policy/drafts/${id}`, { changes })).data;
export const getPolicyDraftDiff     = async (id) => (await apiClient.get(`/policy/drafts/${id}/diff`)).data;
export const runPolicyDraftTests    = async (id) => (await apiClient.post(`/policy/drafts/${id}/test`)).data;
export const getPolicyTestReport    = async (id) => (await apiClient.get(`/policy/drafts/${id}/test-report`)).data;
export const submitPolicyDraft      = async (id) => (await apiClient.post(`/policy/drafts/${id}/submit`)).data;
export const approvePolicyDraft     = async (id, comment = '') => (await apiClient.post(`/policy/drafts/${id}/approve`, { comment })).data;
export const rejectPolicyDraft      = async (id, comment) => (await apiClient.post(`/policy/drafts/${id}/reject`, { comment })).data;
export const publishPolicyDraft     = async (id, version_bump = 'minor') => (await apiClient.post(`/policy/drafts/${id}/publish`, { version_bump })).data;
export const rollbackPolicyVersion  = async (id, reason, version_bump = 'minor') => (await apiClient.post(`/policy/versions/${id}/rollback`, { reason, version_bump })).data;
export const emergencyPublishPolicy = async (body) => (await apiClient.post('/policy/emergency', body)).data;
export const getPolicyRequired      = async (version_id) => (await apiClient.get('/policy/required', { params: version_id ? { version_id } : {} })).data;
export const getPolicyDeal          = async (sf) => (await apiClient.get(`/policy/deals/${encodeURIComponent(sf)}`)).data;
export const evaluatePolicyItem     = async (body) => (await apiClient.post('/policy/evaluate-item', body, { timeout: 180000 })).data;
export const testPolicyRailwayReload = async (target = 'railway') => (await apiClient.post('/policy/railway/test-reload', null, { params: { target } })).data;
export const getPolicyRailwayStatus = async () => (await apiClient.get('/policy/railway/status')).data;
export const getPolicyImportCatalogue = async (group) => (await apiClient.get(`/policy/import/catalogue/${group}`)).data;
export const getPolicyImportAuditStatus = async (region = "UK") => (await apiClient.get(`/policy/import/audit-status`, { params: { region } })).data;
export const getPolicyImportLastRun    = async (draftId) => (await apiClient.get(`/policy/drafts/${draftId}/import/last-run`)).data;
export const importPolicyRows       = async (draftId, group, body) => (await apiClient.post(`/policy/drafts/${draftId}/import/${group}`, body)).data;
export const getPolicyLiveTables    = async () => (await apiClient.get('/policy/live/tables')).data;
export const getPolicyMondayConfig  = async () => (await apiClient.get('/policy/monday-config')).data;
export const policyRerunScan        = async (dry_run = false) => (await apiClient.post('/policy/rerun-scan', null, { params: { dry_run } })).data;
export const testPolicyPayee        = async (payee, version_id) => (await apiClient.post('/policy/lenders/test-payee', { payee, version_id: version_id || null })).data;
export const evaluatePolicy         = async (inputs, version_id) => (await apiClient.post('/policy/evaluate', { inputs, version_id: version_id || null })).data;
export const getPolicyAudit         = async (params = {}) => (await apiClient.get('/policy/audit', { params })).data;
// copilot trigger (AI Opinion webhook → queue) + weekly comparison — Dean, 7 Oct 2026
export const getCopilotStatus       = async () => (await apiClient.get('/policy/copilot/status')).data;
export const getCopilotQueue        = async (params = {}) => (await apiClient.get('/policy/copilot/queue', { params })).data;
export const getCopilotWriteLog     = async (params = {}) => (await apiClient.get('/policy/copilot/write-log', { params })).data;
export const copilotRegisterWebhooks = async () => (await apiClient.post('/policy/copilot/register-webhooks')).data;
export const copilotEnsureLabels    = async (dry_run = false) => (await apiClient.post('/policy/copilot/ensure-labels', null, { params: { dry_run } })).data;
export const copilotEnqueue         = async (itemId, reason = 'manual') => (await apiClient.post(`/policy/copilot/enqueue/${encodeURIComponent(itemId)}`, null, { params: { reason } })).data;
export const copilotSweep           = async (act = false) => (await apiClient.post('/policy/copilot/sweep', null, { params: { act } })).data;
export const getCopilotSwitch       = async () => (await apiClient.get('/policy/copilot/switch')).data;
export const setCopilotSwitch       = async (enabled, reason = '') => (await apiClient.put('/policy/copilot/switch', { enabled, reason })).data;
export const copilotReset           = async (params = {}) => (await apiClient.post('/policy/copilot/reset', null, { params, timeout: 300000 })).data;
export const copilotResetBefore     = async (params = {}) => (await apiClient.post('/policy/copilot/reset-before', null, { params, timeout: 300000 })).data;
export const getCopilotResets       = async (limit = 20) => (await apiClient.get('/policy/copilot/resets', { params: { limit } })).data;
export const getCopilotOverwriteAudit = async (params = {}) => (await apiClient.get('/policy/copilot/overwrite-audit', { params, timeout: 180000 })).data;
export const getPolicyComparison    = async (params = {}) => (await apiClient.get('/policy/comparison', { params, timeout: 180000 })).data;
export const downloadPolicyComparison = async (params = {}) => (await apiClient.get('/policy/comparison', { params: { ...params, format: 'csv' }, responseType: 'blob', timeout: 180000 })).data;

// ── LOANDISK RESTRUCTURE (create-only: new SF-xxxx/N child loan, parent never edited) ──
export const previewLoanRestructure  = async (body) => (await apiClient.post('/loandisk/restructure/preview', body)).data;
export const executeLoanRestructure  = async (body) => (await apiClient.post('/loandisk/restructure/execute', body)).data;
export const getLoanRestructureHistory = async (params = {}) => (await apiClient.get('/loandisk/restructure/history', { params })).data;
export const getLoanRestructureSettings = async () => (await apiClient.get('/loandisk/restructure/settings')).data;
export const putLoanRestructureSettings = async (body) => (await apiClient.put('/loandisk/restructure/settings', body)).data;

// ── PARTNER SUBMISSION API (admin side; public API lives at /api/partner/v1) ──
export const getPartnerApiKeys = async () => (await apiClient.get('/partner-admin/keys')).data;
export const createPartnerApiKey = async (body) => (await apiClient.post('/partner-admin/keys', body)).data;
export const patchPartnerApiKey = async (keyId, body) => (await apiClient.patch(`/partner-admin/keys/${keyId}`, body)).data;
export const rotatePartnerWebhookSecret = async (keyId) => (await apiClient.post(`/partner-admin/keys/${keyId}/rotate-webhook-secret`)).data;
export const testPartnerApiKey = async (keyId) => (await apiClient.post(`/partner-admin/keys/${keyId}/test`)).data;
export const getPartnerSubmissions = async (params = {}) => (await apiClient.get('/partner-admin/submissions', { params })).data;
export const getPartnerKeyReadiness = async (keyId) => (await apiClient.get(`/partner-admin/keys/${keyId}/readiness`)).data;
export const getPartnerKeyDeals = async (keyId, limit = 50) => (await apiClient.get(`/partner-admin/keys/${keyId}/deals`, { params: { limit } })).data;
export const openPartnerDealDocument = async (ref, docId) => {
  const res = await apiClient.get(`/partner-admin/deals/${ref}/documents/${docId}`, { responseType: 'blob' });
  const url = URL.createObjectURL(res.data);
  window.open(url, '_blank', 'noopener');
  setTimeout(() => URL.revokeObjectURL(url), 60000);
};
export const goLivePartnerApiKey = async (keyId, body = {}) => (await apiClient.post(`/partner-admin/keys/${keyId}/go-live`, body)).data;
export const reissuePartnerApiKey = async (keyId, body = {}) => (await apiClient.post(`/partner-admin/keys/${keyId}/reissue`, body)).data;
export const getPartnerAuthFailures = async (limit = 50) => (await apiClient.get('/partner-admin/auth-failures', { params: { limit } })).data;
export const getPartnerOnboardingTemplate = async () => (await apiClient.get('/partner-admin/onboarding-template')).data;
export const putPartnerOnboardingTemplate = async (body) => (await apiClient.put('/partner-admin/onboarding-template', body)).data;
export const resetPartnerOnboardingTemplate = async () => (await apiClient.delete('/partner-admin/onboarding-template')).data;
export const getPartnerApiBrokers = async (params = {}) => (await apiClient.get('/partner-admin/brokers', { params })).data;
export const sendPartnerWelcomeEmail = async (keyId, body) => (await apiClient.post(`/partner-admin/keys/${keyId}/send-welcome`, body)).data;
export const deletePartnerApiKey = async (keyId) => (await apiClient.delete(`/partner-admin/keys/${keyId}`)).data;

// ── Sumsub hub (Application Hub → Sumsub tab, Dean 9 Oct 2026) ─────────────────────────────────────────────────────
// Credit Sense (AU bank statements) — Applications Hub tab beside Sumsub.
export const creditSense = {
  list: async (params = {}) => (await apiClient.get('/creditsense/apps', { params })).data,
  detail: async (id) => (await apiClient.get(`/creditsense/apps/${encodeURIComponent(id)}`)).data,
  fileBlob: async (id, fileId, download = false) =>
    (await apiClient.get(`/creditsense/apps/${encodeURIComponent(id)}/files/${encodeURIComponent(fileId)}`, { params: { download }, responseType: 'blob', timeout: 120000 })).data,
  matches: async (id, q = '') => (await apiClient.get(`/creditsense/apps/${encodeURIComponent(id)}/matches`, { params: q ? { q } : {} })).data,
  attach: async (id, body) => (await apiClient.post(`/creditsense/apps/${encodeURIComponent(id)}/attach`, body, { timeout: 180000 })).data,
  unlink: async (id) => (await apiClient.post(`/creditsense/apps/${encodeURIComponent(id)}/unlink`)).data,
  attention: async () => (await apiClient.get('/creditsense/attention')).data,
  markSeen: async (body) => (await apiClient.post('/creditsense/attention/seen', body)).data,
  sync: async (days = 7) => (await apiClient.post('/creditsense/sync', null, { params: { days }, timeout: 120000 })).data,
  webhookInfo: async () => (await apiClient.get('/creditsense/webhook-info')).data,
  quicklinks: async () => (await apiClient.get('/creditsense/quicklinks')).data,
  createQuicklink: async (body) => (await apiClient.post('/creditsense/quicklinks', body)).data,
  quicklinkStatus: async (token) => (await apiClient.post(`/creditsense/quicklinks/${encodeURIComponent(token)}/status`)).data,
};

export const sumsubHub = {
  list: async (params = {}) => (await apiClient.get('/sumsub-hub/applicants', { params })).data,
  sync: async (region) => (await apiClient.post('/sumsub-hub/sync', null, { params: region ? { region } : {} })).data,
  detail: async (id, region) => (await apiClient.get(`/sumsub-hub/applicants/${encodeURIComponent(id)}`, { params: region ? { region } : {} })).data,
  imageBlob: async (id, imageId, { inspectionId, preview = false } = {}) =>
    (await apiClient.get(`/sumsub-hub/applicants/${encodeURIComponent(id)}/images/${encodeURIComponent(imageId)}`,
      { params: { inspection_id: inspectionId || id, preview }, responseType: 'blob' })).data,
  reportBlob: async (id, download = false) =>
    (await apiClient.get(`/sumsub-hub/applicants/${encodeURIComponent(id)}/report.pdf`, { params: { download }, responseType: 'blob', timeout: 120000 })).data,
  matches: async (id, q = '') => (await apiClient.get(`/sumsub-hub/applicants/${encodeURIComponent(id)}/matches`, { params: q ? { q } : {} })).data,
  attach: async (id, body) => (await apiClient.post(`/sumsub-hub/applicants/${encodeURIComponent(id)}/attach`, body, { timeout: 180000 })).data,
  unlink: async (id) => (await apiClient.post(`/sumsub-hub/applicants/${encodeURIComponent(id)}/unlink`)).data,
  attention: async (region, includeSeen = false) => (await apiClient.get('/sumsub-hub/attention', { params: { region, include_seen: includeSeen } })).data,
  markSeen: async (region, body) => (await apiClient.post('/sumsub-hub/attention/seen', body, { params: { region } })).data,
  webhookInfo: async () => (await apiClient.get('/sumsub-hub/webhook-info')).data,
  actions: async (applicantId) => (await apiClient.get('/sumsub-hub/actions', { params: { applicant_id: applicantId } })).data,
  act: async (id, action, body = {}) => (await apiClient.post(`/sumsub-hub/applicants/${encodeURIComponent(id)}/${action}`, body)).data,
  docKinds: async () => (await apiClient.get('/sumsub-hub/doc-kinds')).data,
  addDocs: async (id, body) => (await apiClient.post(`/sumsub-hub/applicants/${encodeURIComponent(id)}/documents`, body, { timeout: 180000 })).data,
  uploadDocs: async (id, formData) =>
    (await apiClient.post(`/sumsub-hub/applicants/${encodeURIComponent(id)}/documents/upload`, formData, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 180000 })).data,
  forDeal: async ({ sf, itemId, region }) => (await apiClient.get('/sumsub-hub/for-deal', { params: { sf: sf || '', item_id: itemId || '', region: region || 'UK' } })).data,
};

// ---- Legal file map + Timeline designer (Dean, 14 Oct 2026) ----
export const getLegalMap               = async (caseId) => (await apiClient.get(`/legal-process/cases/${encodeURIComponent(caseId)}/map`)).data;
export const recordLegalMap            = async (caseId, payload) => (await apiClient.post(`/legal-process/cases/${encodeURIComponent(caseId)}/map/record`, payload)).data;
export const undoLegalMap              = async (caseId, payload) => (await apiClient.post(`/legal-process/cases/${encodeURIComponent(caseId)}/map/undo`, payload)).data;
export const forceLegalMap             = async (caseId, payload) => (await apiClient.post(`/legal-process/cases/${encodeURIComponent(caseId)}/map/force`, payload)).data;
export const rewindLegalMap            = async (caseId, payload) => (await apiClient.post(`/legal-process/cases/${encodeURIComponent(caseId)}/map/rewind`, payload)).data;
export const addLegalMapParty          = async (caseId, payload) => (await apiClient.post(`/legal-process/cases/${encodeURIComponent(caseId)}/map/parties`, payload)).data;
export const patchLegalMapParty        = async (caseId, partyId, payload) => (await apiClient.patch(`/legal-process/cases/${encodeURIComponent(caseId)}/map/parties/${encodeURIComponent(partyId)}`, payload)).data;
export const resolveLegalMapSuggestion = async (caseId, sid, payload) => (await apiClient.post(`/legal-process/cases/${encodeURIComponent(caseId)}/map/suggestions/${encodeURIComponent(sid)}`, payload)).data;
export const startLegalMapAiFill       = async (caseId) => (await apiClient.post(`/legal-process/cases/${encodeURIComponent(caseId)}/map/ai-fill`)).data;
export const getLegalMapAiRun          = async (caseId, runId) => (await apiClient.get(`/legal-process/cases/${encodeURIComponent(caseId)}/map/ai-fill/${encodeURIComponent(runId)}`)).data;
export const getLegalMapAiLatest       = async (caseId) => (await apiClient.get(`/legal-process/cases/${encodeURIComponent(caseId)}/map/ai-fill/latest`)).data;
export const applyLegalMapAiFill       = async (caseId, runId, payload) => (await apiClient.post(`/legal-process/cases/${encodeURIComponent(caseId)}/map/ai-fill/${encodeURIComponent(runId)}/apply`, payload)).data;
export const planLegalMapAiFill        = async (caseId, runId, payload) => (await apiClient.post(`/legal-process/cases/${encodeURIComponent(caseId)}/map/ai-fill/${encodeURIComponent(runId)}/plan`, payload)).data;
export const listProcessSuggestions    = async (status = "open") => (await apiClient.get(`/legal-process/suggestions`, { params: { status } })).data;
export const decideProcessSuggestion   = async (sid, status, note = "") => (await apiClient.post(`/legal-process/suggestions/${encodeURIComponent(sid)}`, { status, note })).data;

export const getLegalTimelines         = async (region = "UK") => (await apiClient.get("/legal-process/timelines", { params: { region } })).data;
export const saveLegalTimelinesDraft   = async (timelines, region = "UK") => (await apiClient.put("/legal-process/timelines/draft", { timelines }, { params: { region } })).data;
export const validateLegalTimelines    = async (timelines, region = "UK") => (await apiClient.post("/legal-process/timelines/validate", { timelines }, { params: { region } })).data;
export const discardLegalTimelinesDraft = async (region = "UK") => (await apiClient.delete("/legal-process/timelines/draft", { params: { region } })).data;
export const wizardValidateTimelines   = async (region = "UK") => (await apiClient.post("/legal-process/timelines/publish/validate", {}, { params: { region } })).data;
export const wizardBulkMap             = async (payload = {}, region = "UK") => (await apiClient.post("/legal-process/timelines/publish/bulk-map", payload, { params: { region }, timeout: 180000 })).data;
export const wizardBulkMapCsv          = async (payload = {}, region = "UK") => (await apiClient.post("/legal-process/timelines/publish/bulk-map", payload, { params: { region, format: "csv" }, responseType: "blob", timeout: 180000 })).data;
export const wizardApplyTimelines      = async (payload, region = "UK") => (await apiClient.post("/legal-process/timelines/publish/apply", payload, { params: { region }, timeout: 180000 })).data;
export const getTimelineRuns           = async () => (await apiClient.get("/legal-process/timelines/publish/runs")).data;
export const previewTimelinesOnFile    = async (caseId, region = "UK") => (await apiClient.get(`/legal-process/timelines/preview/${encodeURIComponent(caseId)}`, { params: { region } })).data;
export const exportLegalTimelines      = async (source = "published", region = "UK") => (await apiClient.get("/legal-process/timelines/export", { params: { source, region }, responseType: "blob" })).data;
export const importLegalTimelines      = async (payload, apply = false, region = "UK") => (await apiClient.post("/legal-process/timelines/import", { payload, apply }, { params: { region } })).data;
export const sendTimelinesToProduction = async (source = "published", region = "UK") => (await apiClient.post("/legal-process/timelines/send-to-production", { source }, { params: { region }, timeout: 60000 })).data;
export const clearIncomingTimelines    = async (region = "UK") => (await apiClient.delete("/legal-process/timelines/incoming", { params: { region } })).data;

// DocuSign envelope workbench (Credit Approved Deals → DocuSign envelopes tab, Dean 30 Oct 2026)
const _env = (id) => `/contracts/envelope/${encodeURIComponent(id)}`;
export const listDocuSignEnvelopes   = async (region, days = 30, fresh = false) => (await apiClient.get("/contracts/envelopes/list", { params: { region, days, ...(fresh ? { fresh: true } : {}) }, timeout: 90000 })).data;
export const getEnvelopeTimeline     = async (id) => (await apiClient.get(`/contracts/envelopes/${encodeURIComponent(id)}/timeline`, { timeout: 60000 })).data;
export const fetchEnvelopePdf        = async (id, { certificate = false } = {}) =>
  (await apiClient.get(`${_env(id)}/documents/combined`, { params: { certificate, download: true }, responseType: "blob", timeout: 120000 })).data;
export const fetchEnvelopeCertificate = async (id) =>
  (await apiClient.get(`${_env(id)}/documents/certificate`, { params: { download: true }, responseType: "blob", timeout: 120000 })).data;
export const getEnvelopeCorrectView  = async (id) => (await apiClient.post(`${_env(id)}/correct-view`)).data;
export const getEnvelopeResumeView   = async (id) => (await apiClient.get(`${_env(id)}/resume`)).data;
export const discardDraftEnvelope    = async (id) => (await apiClient.delete(_env(id))).data;
export const getEnvelopeNotification = async (id) => (await apiClient.get(`${_env(id)}/notification`)).data;
export const putEnvelopeNotification = async (id, body) => (await apiClient.put(`${_env(id)}/notification`, body)).data;
